import type { Locale } from './index';

export interface StudioCopy {
  header: {
    planNames: Record<string, string>;
    credits: (n: number) => string;
    vectorCredits: (n: number) => string;
    aiCredits: (n: number) => string;
    unlimitedCredits: string;
    upgrade: string;
    creditTooltip: string;
    aiCreditTooltip: string;
    admin: string;
    userMenu: {
      accountPlan: string;
      remainingCredits: string;
      vectorCreditLabel: string;
      aiCreditLabel: string;
      creditNote: string;
      upgradePlan: string;
      managePlan: string;
      workspace: string;
      byokKeys: string;
      themeLight: string;
      themeDark: string;
      signOut: string;
      adminConsole: string;
      changePassword?: string;
      topupCredits?: string;
    };
    collapseQueue: string;
    expandQueue: string;
    toggleTheme: string;
  };
  leftSidebar: {
    autoOptimize: string;
    analyzing: string;
    autoNoticeEmpty: string;
    autoTitleEmpty: string;
    autoTitleActive: string;
    perImagePrefix: string;
    quickPresets: string;
    guideBtn: string;
    guideTitle: string;
    guideItems: { name: string; desc: string }[];
    presetChips: Record<string, string>;
    presetTooltips: Record<string, string>;
    outputFormats: string;
    formatBoth: string;
    formatSvg: string;
    formatEps: string;
    tuningTitle: string;
    colorLimit: string;
    truecolorLabel: string;
    colorsUnit: (n: number) => string;
    colorHints: {
      flat: string;
      vibrant: string;
      detailed: string;
      photo: string;
    };
    speckleFilter: string;
    cornerThreshold: string;
    curveSmoothness: string;
    curveTolerance: string;
    colorMerge: string;
    nativeTopology: string;
    validated: string;
    stripWhiteBg: string;
    stripWhiteDesc: string;
    whiteSensitivity: string;
    antiHalo: string;
    computeEngine?: string;
    engineAuto?: string;
    engineDevice?: string;
    engineCloud?: string;
    engineAutoDesc?: (cores: number) => string;
    engineDeviceDesc?: string;
    engineCloudDesc?: string;
  };
  canvas: {
    dropTitle: string;
    dropDesc: string;
    browseBtn: string;
    dualView: string;
    vectorView: string;
    outlineView: string;
    dualViewTitle: string;
    vectorViewTitle: string;
    outlineViewTitle: string;
    downloadSvg: string;
    downloadEps: string;
    revectorize: string;
    revectorizeTitle: string;
    convertVector: string;
    convertAll: string;
    vectorizing: string;
    computingCurves: string;
    noVectorTitle: string;
    noVectorDesc: string;
    vectorReadyTitle: string;
    vectorReadyDesc: string;
    tagRaster: string;
    tagOriginal: string;
    tagVectorSvg: string;
    tagVectorWireframe: string;
    tagOutlinePure: string;
    zoomOut: string;
    resetZoom: string;
    zoomIn: string;
    wireframeInspect: string;
    wireframeFills: string;
    checkerboardSolid: string;
    checkerboardTrans: string;
    verifiedTitle: string;
    verifiedSpecs: string;
    verifiedBadge: string;
  };
  rightSidebar: {
    queueTitle: (count: number) => string;
    queueProgress: (cur: number, tot: number, file: string) => string;
    queueConverted: (count: number) => string;
    resetQueue: string;
    convertAll: string;
    queueEmptyTitle: string;
    queueEmptyDesc: string;
    statusVectorizing: (cur: number, tot: number) => string;
    metadataTitle: string;
    generateBtn: string;
    regenerateBtn: string;
    categoryLabel: string;
    keywordsCount: (n: number) => string;
    keywordsMore: (n: number) => string;
    noMetadataDesc: string;
    copiedKeywords: string;
    csvExporterTitle: string;
    csvBtn: string;
    csvColumnsLabel: string;
    batchExporterTitle: string;
    downloadZipBtn: string;
    packagingZip: string;
    batchZipDesc: string;
  };
  pricingModal: {
    title: string;
    subtitle: string;
    rolloverNotice: string;
    monthly: string;
    yearly: string;
    savePercent: string;
    perMonth: string;
    perYear: string;
    currentPlan: string;
    popularBadge: string;
    bestValueBadge: string;
    choosePlan: (name: string, price: string) => string;
    bullets: Record<'starter' | 'pro' | 'studio', string[]>;
    activeSubscription: (plan: string, credits: number) => string;
    cancelSubscription: string;
    cancelConfirmTitle: string;
    cancelConfirmDesc: (credits: number) => string;
    cancelKeepBtn: string;
    cancelProceedBtn: string;
    cancelledSuccess: string;
    renewsOn: (date: string) => string;
    cancelsOn: (date: string) => string;
    cancellationScheduledBadge: string;
    activePeriod: (start: string, end: string) => string;
    cancelsOnNotice: (plan: string, date: string) => string;
    renewsOnNotice: (date: string, amount: string) => string;
    higherPlanAlert?: string;
    samePlanNotice?: string;
    includedBadge?: string;
    includedInPlan?: string;
    topupPrompt?: string;
    topupBtn?: string;
  };
  passwordModal?: {
    title: string;
    subtitle: string;
    currentPasswordLabel: string;
    newPasswordLabel: string;
    confirmPasswordLabel: string;
    placeholderCurrent: string;
    placeholderNew: string;
    placeholderConfirm: string;
    submitBtn: string;
    updating: string;
    successMsg: string;
    mismatchError: string;
    minLenError: string;
    sameError: string;
    cancelBtn: string;
  };
  topupModal?: {
    title: string;
    subtitle: string;
    currentBalanceLabel?: string;
    vectorTab: string;
    aiTab: string;
    vectorDesc: string;
    aiDesc: string;
    saveTag: (pct: number) => string;
    perCredit: (cost: string) => string;
    buyBtn: (name: string, price: string) => string;
    buyPack?: (price: string) => string;
    popularBadge?: string;
    bestValueBadge?: string;
    successAlert: (amount: number, type: string) => string;
    connectingCheckout: string;
    closeBtn: string;
    packs?: {
      vec_200: string;
      vec_500: string;
      vec_1500: string;
      ai_500: string;
      ai_1500: string;
      ai_4000: string;
    };
  };
  aiModal: {
    title: string;
    subtitle: string;
    selectProvider: string;
    apiKeyLabel: (prov: string) => string;
    apiKeysHint: string;
    apiKeysCount: (count: number) => string;
    getKey: string;
    primaryModel: string;
    customModelPlaceholder: string;
    fallbackModel: string;
    fallbackNone: string;
    fallbackDesc: string;
    customFallbackPlaceholder: string;
    customEndpointLabel: string;
    customEndpointDesc: string;
    testConnection: string;
    testConnectionDesc: string;
    verifying: string;
    autoMetadataTitle: string;
    autoMetadataDesc: string;
    vectriaFallbackTitle: string;
    vectriaFallbackDesc: string;
    sourceByok: string;
    sourceManaged: string;
    sourceFallback: string;
    estimatedCharge: (count: number) => string;
    byokNoCharge: string;
    insufficientAiCredits: string;
    resultSummary: (byok: number, managed: number, fallback: number, charged: number) => string;
    cancel: string;
    saveConfig: string;
  };
}

const en: StudioCopy = {
  header: {
    planNames: {
      free: 'Free Plan',
      starter: 'Starter Plan',
      pro: 'Pro Plan',
      studio: 'Studio Plan',
      agency: 'Agency Plan'
    },
    credits: (n) => `${n.toLocaleString()} Credits`,
    vectorCredits: (n) => `${n.toLocaleString()} Vector Credits`,
    aiCredits: (n) => `${n.toLocaleString()} AI Credits`,
    unlimitedCredits: 'Unlimited Credits',
    upgrade: 'Upgrade',
    creditTooltip: '1 Vector Credit = 1 Vector Conversion (SVG + EPS 10 + Preview). Click to view plans or top up.',
    aiCreditTooltip: '1 AI Credit is charged only after one asset succeeds with Vectria AI Agent. BYOK costs 0 AI Credits.',
    admin: 'Admin',
    userMenu: {
      accountPlan: 'Account Plan:',
      remainingCredits: 'Remaining Credits:',
      vectorCreditLabel: 'Vector Credits:',
      aiCreditLabel: 'AI Credits:',
      creditNote: 'Vector Credits power vector conversion. AI Credits pay only for successful Vectria AI Agent metadata.',
      upgradePlan: 'Upgrade Plan',
      managePlan: 'Manage Subscription',
      workspace: 'Workspace',
      byokKeys: 'BYOK AI Keys',
      themeLight: 'Light Theme',
      themeDark: 'Dark Theme',
      signOut: 'Sign Out',
      adminConsole: 'Owner Admin Console',
      changePassword: 'Change Password',
      topupCredits: 'Top Up Credits'
    },
    collapseQueue: 'Collapse Queue Panel',
    expandQueue: 'Expand Queue Panel',
    toggleTheme: 'Toggle Theme'
  },
  leftSidebar: {
    autoOptimize: 'Auto-Optimize Mode',
    analyzing: 'Analyzing Image…',
    autoNoticeEmpty: 'Upload an image first to enable auto-detection',
    autoTitleEmpty: 'Upload an image first to enable Auto-Optimize analysis',
    autoTitleActive: 'Analyze contours, line density, and merge similar colors into clean vector fills',
    perImagePrefix: 'Per-image:',
    quickPresets: 'Quick Presets',
    guideBtn: 'Guide',
    guideTitle: 'Preset Reference Guide',
    guideItems: [
      { name: 'Flat (6–8 Colors)', desc: 'Icon sets, minimalist logos, pictograms, and UI symbols. Eliminates contour banding.' },
      { name: 'Vibrant (12–16 Colors)', desc: 'Stickers, badges, mascot logos, and cartoon art with basic cell shading.' },
      { name: 'Detailed (24–32 Colors)', desc: 'Multi-layered illustrations and detailed game assets. Not recommended for flat icons.' },
      { name: 'Truecolor (64+ Colors)', desc: 'Landscape art, digital paintings, and photographic subjects.' },
      { name: 'Monoline (2 Colors)', desc: 'Line drawings, silhouettes, stamps, and high-contrast B&W artwork.' }
    ],
    presetChips: {
      'flat-6': 'Flat Icon',
      'vibrant-12': 'Vibrant Art',
      'detailed-32': 'Detailed 32',
      'ultra-64': 'Ultra HD 64',
      'truecolor': 'Full Truecolor',
      'monoline': 'B&W Monoline'
    },
    presetTooltips: {
      'flat-6': 'Flat Icon: 8 colors, low speckle filtering, protected corners, and no edge erosion',
      'vibrant-12': 'Vibrant Art: balanced settings for stickers, badges, mascots, and layered art',
      'detailed-32': 'Detailed 32: For Multi-layered Illustrations & Shaded Characters',
      'ultra-64': 'Ultra HD 64: Rich Color Art & Landscapes (Avoid for Flat Icons)',
      'truecolor': 'Full Truecolor: For Realistic Photos & Complex Paintings',
      'monoline': 'B&W Monoline: Pure Line Art, Silhouettes, & Monochrome Stamps'
    },
    outputFormats: 'Output Formats',
    formatBoth: 'SVG + EPS 10',
    formatSvg: 'SVG Only',
    formatEps: 'EPS 10',
    tuningTitle: 'Vector Tuning Controls',
    colorLimit: 'Color Palette Limit',
    truecolorLabel: 'Warna Penuh (256+)',
    colorsUnit: (n) => `${n} colors`,
    colorHints: {
      flat: 'Optimal for icon sets and minimalist logos',
      vibrant: 'Best for stickers, badges, and mascot illustrations',
      detailed: 'Multi-toned artwork and detailed illustrations',
      photo: 'Recommended for landscapes and photographic subjects'
    },
    speckleFilter: 'Speckle Filter (Noise)',
    cornerThreshold: 'Corner Threshold',
    curveSmoothness: 'Curve Smoothness',
    curveTolerance: 'Curve Fit Tolerance',
    colorMerge: 'Similar Color Merge',
    nativeTopology: 'Native Topology',
    validated: 'Validated',
    stripWhiteBg: 'Strip White Background',
    stripWhiteDesc: 'Clear negative spaces & halos',
    whiteSensitivity: 'White Sensitivity',
    antiHalo: 'Edge Inset (Anti-Halo)',
    computeEngine: 'Compute Engine',
    engineAuto: 'Auto',
    engineDevice: 'Device (WASM)',
    engineCloud: 'Cloud (VPS)',
    engineAutoDesc: (cores: number) => `Auto: ${cores >= 4 ? 'Device WASM' : 'Cloud Server'} (${cores} Cores)`,
    engineDeviceDesc: 'Runs locally on your device CPU/GPU via WebAssembly. Blazing fast, zero server queue.',
    engineCloudDesc: 'Runs remotely on Vectria Cloud VPS. Best for battery saving or ultra low-spec devices.'
  },
  canvas: {
    dropTitle: 'Drag & Drop AI Images Here',
    dropDesc: 'Supports PNG, JPG, JPEG, and WEBP formats from Midjourney, Recraft, or Leonardo AI.',
    browseBtn: 'Browse Files from Device',
    dualView: 'Dual View',
    vectorView: 'Vector',
    outlineView: 'Outline',
    dualViewTitle: 'Dual View: Original Pixels & Vector Result',
    vectorViewTitle: 'Full Color Vector Art',
    outlineViewTitle: 'Outline Wireframe / Bezier Inspection (Illustrator Ctrl+Y)',
    downloadSvg: 'SVG',
    downloadEps: 'EPS 10',
    revectorize: 'Re-vectorize',
    revectorizeTitle: 'Run vectorization again with the current options',
    convertVector: 'Convert Vector',
    convertAll: 'Convert All',
    vectorizing: 'Vectorizing...',
    computingCurves: 'Computing Bézier splines & sharp corners',
    noVectorTitle: 'No Vector Generated Yet',
    noVectorDesc: 'Convert this raster artwork into SVG & EPS 10 vector format',
    vectorReadyTitle: 'Vector Ready to Generate',
    vectorReadyDesc: 'Click below to convert original pixels into commercial Bézier curves',
    tagRaster: 'Raster Pixels',
    tagOriginal: 'Original Pixels',
    tagVectorSvg: 'Vector SVG (Pure Bezier)',
    tagVectorWireframe: 'Outline Wireframe (Pure Bezier)',
    tagOutlinePure: 'Vector Wireframe',
    zoomOut: 'Zoom Out (-)',
    resetZoom: 'Reset Zoom (100%)',
    zoomIn: 'Zoom In (+) — max 800%',
    wireframeInspect: 'Inspect Pure Vector Outlines (Illustrator Ctrl+Y)',
    wireframeFills: 'Switch to Full Color Fills',
    checkerboardSolid: 'Switch to Dark Solid Background',
    checkerboardTrans: 'Switch to Transparency Checkerboard',
    verifiedTitle: 'Microstock Ready',
    verifiedSpecs: '4000 × 4000 px • Vector Only',
    verifiedBadge: 'EPS 10'
  },
  rightSidebar: {
    queueTitle: (count) => `Queue (${count})`,
    queueProgress: (cur, tot, file) => `Vectorizing ${cur}/${tot}: ${file}`,
    queueConverted: (count) => `${count} converted`,
    resetQueue: 'Reset',
    convertAll: 'Convert All',
    queueEmptyTitle: 'Queue is empty.',
    queueEmptyDesc: 'Drag images onto canvas or click browse.',
    statusVectorizing: (cur, tot) => `VECTORIZING ${cur}/${tot}`,
    metadataTitle: 'AI Microstock Metadata',
    generateBtn: 'Generate',
    regenerateBtn: 'Regenerate',
    categoryLabel: 'Category:',
    keywordsCount: (n) => `${n} Keywords (Copy)`,
    keywordsMore: (n) => `+${n} more`,
    noMetadataDesc: 'No AI metadata generated yet. Click "Generate" to create commercial title, 35 SEO keywords, and category.',
    copiedKeywords: 'Copied keywords to clipboard!',
    csvExporterTitle: 'Microstock Metadata Exporter',
    csvBtn: 'CSV',
    csvColumnsLabel: 'Columns:',
    batchExporterTitle: 'Batch Vector Exporter',
    downloadZipBtn: 'Download Batch ZIP Package',
    packagingZip: 'Packaging ZIP...',
    batchZipDesc: 'Bundles all converted SVG, EPS 10 & preview files into a single downloadable ZIP.'
  },
  pricingModal: {
    title: 'Choose Your Vectria Plan',
    subtitle: 'Select the right plan for your vector production volume. 1 Credit = 1 Complete Vector Conversion (SVG + EPS 10 + Preview PNG).',
    rolloverNotice: 'No hidden fees. Unused subscription credits rollover automatically during active billing.',
    monthly: 'Monthly',
    yearly: 'Yearly',
    savePercent: 'Save up to 20%',
    perMonth: ' / month',
    perYear: ' / year',
    currentPlan: 'Current Plan',
    popularBadge: 'Popular',
    bestValueBadge: 'Best Value',
    choosePlan: (name, price) => `Choose ${name} (${price})`,
    bullets: {
      starter: [
        'Pure SVG & Zero-Bitmap EPS 10 Export',
        'High-Res PNG / JPG Companion Preview',
        'Dual View & Outline Studio Inspector',
        'Single & Multi File Processing'
      ],
      pro: [
        'All features in Starter plan',
        'Batch ZIP Exporter (up to 50 files simultaneously)',
        'AI Auto-Keywords & Adobe CSV',
        'EPS 10 Zero-Bitmap Technical Validation',
        'Priority vector processing queue'
      ],
      studio: [
        'All features in Pro plan',
        'Unlimited Batch Queue Processing',
        'Ultra High-Speed Dedicated Pipeline',
        'Multi-Seat Team License & Priority Support'
      ]
    },
    activeSubscription: (plan, credits) => `Active Plan: ${plan} • ${credits.toLocaleString()} Credits Available`,
    cancelSubscription: 'Cancel Subscription',
    cancelConfirmTitle: 'Cancel Subscription?',
    cancelConfirmDesc: (credits) => `Are you sure you want to cancel your subscription? You will keep your remaining ${credits.toLocaleString()} credits, but priority queue and batch ZIP tools will end after your current billing period.`,
    cancelKeepBtn: 'Keep Subscription',
    cancelProceedBtn: 'Yes, Cancel Subscription',
    cancelledSuccess: 'Your subscription has been cancelled. Your remaining credits remain active.',
    renewsOn: (date) => `Renews automatically on ${date}`,
    cancelsOn: (date) => `Plan cancels on ${date} • Full access remains active until then`,
    cancellationScheduledBadge: 'Cancellation Scheduled',
    activePeriod: (start, end) => `Active Period: ${start} – ${end}`,
    cancelsOnNotice: (plan, date) => `Your ${plan} plan is scheduled to cancel on ${date}. All ${plan} features and rollover credits remain fully active until then.`,
    renewsOnNotice: (date, amount) => `Renews automatically on ${date} (${amount}).`,
    higherPlanAlert: 'Your current plan is higher.',
    samePlanNotice: 'You are already subscribed to this plan. To add more credits, please use the Top-up Credits feature.',
    includedBadge: 'Included',
    includedInPlan: 'Included in your plan',
    topupPrompt: 'Need more credits before renewal?',
    topupBtn: 'Top Up Credits'
  },
  passwordModal: {
    title: 'Change Password',
    subtitle: 'Update your account password. Must be at least 8 characters long.',
    currentPasswordLabel: 'Current Password',
    newPasswordLabel: 'New Password',
    confirmPasswordLabel: 'Confirm New Password',
    placeholderCurrent: 'Enter current password',
    placeholderNew: 'Minimum 8 characters',
    placeholderConfirm: 'Repeat new password',
    submitBtn: 'Update Password',
    updating: 'Updating...',
    successMsg: 'Your password has been changed successfully.',
    mismatchError: 'New passwords do not match.',
    minLenError: 'Password must be at least 8 characters long.',
    sameError: 'New password must be different from current password.',
    cancelBtn: 'Cancel',
  },
  topupModal: {
    title: 'Top Up Credits',
    subtitle: 'Add extra Vector or AI Credits instantly without changing your subscription billing cycle.',
    currentBalanceLabel: 'Current Account Balance:',
    vectorTab: 'Vector Credits',
    aiTab: 'AI Credits',
    vectorDesc: '1 Vector Credit = 1 complete SVG + EPS 10 conversion. Never expires while account is active.',
    aiDesc: 'AI Credits pay for commercial metadata generation with Vectria AI Agent. Charged only on success.',
    saveTag: (pct) => `Save ${pct}%`,
    perCredit: (cost) => `(${cost} / credit)`,
    buyBtn: (name, price) => `Buy ${name} (${price})`,
    buyPack: (price) => `Get Pack (${price})`,
    popularBadge: 'Popular',
    bestValueBadge: 'Best Value',
    successAlert: (amount, type) => `Successfully added +${amount.toLocaleString()} ${type} Credits!`,
    connectingCheckout: 'Connecting to checkout...',
    closeBtn: 'Close',
    packs: {
      vec_200: 'Mini Booster',
      vec_500: 'Growth Booster',
      vec_1500: 'Power Booster',
      ai_500: 'AI Mini Pack',
      ai_1500: 'AI Ultra Pack',
      ai_4000: 'AI Studio Pack'
    }
  },
  aiModal: {
    title: 'BYOK AI Metadata Settings',
    subtitle: "Your BYOK credentials are encrypted and stored securely on Vectria servers. Keys are used only for metadata requests you authorize.",
    selectProvider: 'Select AI Provider',
    apiKeyLabel: (prov) => `${prov} API Keys`,
    apiKeysHint: 'One API key per line. Vectria tries the next key when the current key fails. Maximum 10 keys.',
    apiKeysCount: (count) => `${count}/10 keys`,
    getKey: 'Get Key',
    primaryModel: 'Primary AI Model',
    customModelPlaceholder: 'Enter custom model identifier (e.g. gemini-3.8-flash-lite)',
    fallbackModel: 'Fallback Model (Auto-switch if primary model fails/rate-limited)',
    fallbackNone: 'None (Fail immediately if primary model fails)',
    fallbackDesc: 'If the primary model returns 429 (rate-limit), 503 (overload), or fails, Vectria seamlessly retries with this fallback model.',
    customFallbackPlaceholder: 'Enter custom fallback model identifier',
    customEndpointLabel: 'Custom OpenAI-Compatible Endpoint URL',
    customEndpointDesc: 'Use OpenRouter or local Ollama / vLLM (e.g. http://localhost:11434/v1/chat/completions).',
    testConnection: 'Test Connection',
    testConnectionDesc: 'Validate API key and model availability',
    verifying: 'Verifying...',
    autoMetadataTitle: 'Enable Automated Metadata Generation',
    autoMetadataDesc: 'Automatically tag converted vectors with title, 35 keywords, and stock category.',
    vectriaFallbackTitle: 'Vectria AI fallback',
    vectriaFallbackDesc: 'Opt-in fallback uses Vectria AI Agent only after temporary BYOK failure. Each successful asset costs 1 AI Credit.',
    sourceByok: 'BYOK · 0 AI Credits',
    sourceManaged: 'Vectria AI Agent',
    sourceFallback: 'BYOK with Vectria fallback',
    estimatedCharge: (count) => `Up to ${count.toLocaleString()} AI Credits`,
    byokNoCharge: 'BYOK generation does not use AI Credits.',
    insufficientAiCredits: 'Not enough AI Credits for this managed AI batch.',
    resultSummary: (byok, managed, fallback, charged) => `${byok} BYOK · ${managed} managed · ${fallback} fallback · ${charged} AI Credits charged`,
    cancel: 'Cancel',
    saveConfig: 'Save Configuration'
  }
};

const id: StudioCopy = {
  header: {
    planNames: {
      free: 'Paket Gratis',
      starter: 'Paket Starter',
      pro: 'Paket Pro',
      studio: 'Paket Studio',
      agency: 'Paket Agency'
    },
    credits: (n) => `${n.toLocaleString()} Kredit`,
    vectorCredits: (n) => `${n.toLocaleString()} Vector Credit`,
    aiCredits: (n) => `${n.toLocaleString()} AI Credit`,
    unlimitedCredits: 'Vector Credit Tanpa Batas',
    upgrade: 'Tingkatkan',
    creditTooltip: '1 Vector Credit = 1 Konversi Vektor Lengkap (SVG + EPS 10 + Pratinjau). Klik untuk lihat paket atau isi ulang.',
    aiCreditTooltip: '1 AI Credit dipotong hanya setelah satu aset berhasil dibuat dengan Agen AI Vectria. BYOK memakai 0 AI Credit.',
    admin: 'Admin',
    userMenu: {
      accountPlan: 'Paket Akun:',
      remainingCredits: 'Sisa Saldo Kredit:',
      vectorCreditLabel: 'Kredit Vektor:',
      aiCreditLabel: 'Kredit AI:',
      creditNote: 'Vector Credit untuk konversi vektor. AI Credit hanya untuk metadata Agen AI Vectria yang berhasil.',
      upgradePlan: 'Upgrade Paket',
      managePlan: 'Kelola Langganan',
      workspace: 'Ruang Kerja',
      byokKeys: 'Kunci AI BYOK',
      themeLight: 'Tema Terang',
      themeDark: 'Tema Gelap',
      signOut: 'Keluar',
      adminConsole: 'Konsol Admin Pemilik',
      changePassword: 'Ubah Password',
      topupCredits: 'Beli Kredit (Top-up)'
    },
    collapseQueue: 'Sembunyikan Panel Antrean',
    expandQueue: 'Tampilkan Panel Antrean',
    toggleTheme: 'Ganti Tema'
  },
  leftSidebar: {
    autoOptimize: 'Mode Optimasi Otomatis',
    analyzing: 'Menganalisis Gambar…',
    autoNoticeEmpty: 'Unggah gambar terlebih dahulu untuk deteksi otomatis',
    autoTitleEmpty: 'Unggah gambar terlebih dahulu untuk mengaktifkan analisis Optimasi Otomatis',
    autoTitleActive: 'Analisis kontur, kerapatan garis, dan gabungkan warna serupa menjadi isian vektor bersih',
    perImagePrefix: 'Per-gambar:',
    quickPresets: 'Preset Cepat',
    guideBtn: 'Panduan',
    guideTitle: 'Panduan Referensi Preset',
    guideItems: [
      { name: 'Flat (6–8 Warna)', desc: 'Set ikon, logo minimalis, piktogram, dan simbol UI. Menghilangkan efek warna bertingkat (banding).' },
      { name: 'Vibrant (12–16 Warna)', desc: 'Stiker, lencana, logo maskot, dan ilustrasi kartun dengan bayangan sel dasar.' },
      { name: 'Detailed (24–32 Warna)', desc: 'Ilustrasi berlapis banyak dan aset game berdetail. Tidak disarankan untuk ikon flat.' },
      { name: 'Truecolor (64+ Warna)', desc: 'Lukisan digital pemandangan, lukisan artistik, dan subjek menyerupai foto.' },
      { name: 'Monoline (2 Warna)', desc: 'Seni garis murni, siluet, stempel, dan karya grafis hitam-putih kontras tinggi.' }
    ],
    presetChips: {
      'flat-6': 'Ikon Flat',
      'vibrant-12': 'Seni Vibrant',
      'detailed-32': 'Detail 32',
      'ultra-64': 'Ultra HD 64',
      'truecolor': 'Full Truecolor',
      'monoline': 'Monoline B&W'
    },
    presetTooltips: {
      'flat-6': 'Ikon Flat: 8 warna, filter noise rendah, sudut terlindungi, bebas erosi tepi',
      'vibrant-12': 'Seni Vibrant: setelan seimbang untuk stiker, badge, maskot, dan seni berlapis',
      'detailed-32': 'Detail 32: Untuk ilustrasi kompleks banyak lapisan & bayangan bertingkat',
      'ultra-64': 'Ultra HD 64: Seni kaya warna & pemandangan (hindari untuk ikon flat)',
      'truecolor': 'Full Truecolor: Untuk foto realistis & lukisan kompleks',
      'monoline': 'Monoline B&W: Seni garis murni, siluet, & cap monokrom'
    },
    outputFormats: 'Format Ekspor',
    formatBoth: 'SVG + EPS 10',
    formatSvg: 'Hanya SVG',
    formatEps: 'EPS 10',
    tuningTitle: 'Kontrol Penyetelan Vektor',
    colorLimit: 'Batas Palet Warna',
    truecolorLabel: 'Full Truecolor (256+)',
    colorsUnit: (n) => `${n} warna`,
    colorHints: {
      flat: 'Optimal untuk set ikon dan logo minimalis',
      vibrant: 'Terbaik untuk stiker, lencana, dan ilustrasi maskot',
      detailed: 'Karya seni multi-nada dan ilustrasi mendalam',
      photo: 'Disarankan untuk lanskap dan subjek fotografis'
    },
    speckleFilter: 'Filter Speckle (Noise)',
    cornerThreshold: 'Ambang Sudut (Corner)',
    curveSmoothness: 'Kehalusan Kurva',
    curveTolerance: 'Toleransi Presisi Kurva',
    colorMerge: 'Gabung Warna Serupa (Δ)',
    nativeTopology: 'Topologi Asli',
    validated: 'Tervalidasi',
    stripWhiteBg: 'Hapus Latar Belakang Putih',
    stripWhiteDesc: 'Bersihkan ruang negatif & efek halo tepi',
    whiteSensitivity: 'Sensitivitas Putih',
    antiHalo: 'Penyusutan Tepi (Anti-Halo)',
    computeEngine: 'Mesin Komputasi',
    engineAuto: 'Otomatis',
    engineDevice: 'Perangkat (WASM)',
    engineCloud: 'Cloud (VPS)',
    engineAutoDesc: (cores: number) => `Otomatis: ${cores >= 4 ? 'Perangkat Lokal' : 'Server Cloud'} (${cores} Core)`,
    engineDeviceDesc: 'Dijalankan di hardware perangkat Anda via WebAssembly. Lebih cepat & tanpa antrean server.',
    engineCloudDesc: 'Dijalankan di Cloud Server Vectria. Cocok untuk perangkat spesifikasi rendah atau hemat baterai.'
  },
  canvas: {
    dropTitle: 'Tarik & Lepas Gambar AI ke Sini',
    dropDesc: 'Mendukung format PNG, JPG, JPEG, dan WEBP dari Midjourney, Recraft, atau Leonardo AI.',
    browseBtn: 'Pilih File dari Perangkat',
    dualView: 'Tampilan Ganda',
    vectorView: 'Vektor',
    outlineView: 'Garis Luar',
    dualViewTitle: 'Tampilan Ganda: Piksel Asli & Hasil Vektor Berdampingan',
    vectorViewTitle: 'Tampilan Seni Vektor Penuh Warna',
    outlineViewTitle: 'Inspeksi Garis Luar Bézier (Mirip Illustrator Ctrl+Y)',
    downloadSvg: 'SVG',
    downloadEps: 'EPS 10',
    revectorize: 'Vektorisasi Ulang',
    revectorizeTitle: 'Jalankan vektorisasi kembali dengan opsi terkini',
    convertVector: 'Konversi Vektor',
    convertAll: 'Konversi Semua',
    vectorizing: 'Sedang Memvektorisasi...',
    computingCurves: 'Menghitung kurva Bézier halus & sudut presisi',
    noVectorTitle: 'Vektor Belum Dibuat',
    noVectorDesc: 'Ubah gambar raster ini menjadi format vektor SVG & EPS 10 komersial',
    vectorReadyTitle: 'Vektor Siap Dikonversi',
    vectorReadyDesc: 'Klik tombol di bawah untuk mengubah piksel raster menjadi kurva Bézier standar microstock',
    tagRaster: 'Piksel Raster',
    tagOriginal: 'Piksel Asli',
    tagVectorSvg: 'Vektor SVG (Bézier Murni)',
    tagVectorWireframe: 'Garis Luar (Bézier Murni)',
    tagOutlinePure: 'Wireframe Vektor',
    zoomOut: 'Perkecil Zoom (-)',
    resetZoom: 'Atur Ulang Zoom (100%)',
    zoomIn: 'Perbesar Zoom (+) — maks 800%',
    wireframeInspect: 'Periksa Garis Bézier Murni (Illustrator Ctrl+Y)',
    wireframeFills: 'Kembali ke Isian Vektor Warna Penuh',
    checkerboardSolid: 'Ganti ke Latar Belakang Gelap Solid',
    checkerboardTrans: 'Ganti ke Papan Catur Transparan',
    verifiedTitle: 'Siap Microstock',
    verifiedSpecs: '4000 × 4000 px • Vektor Murni',
    verifiedBadge: 'EPS 10'
  },
  rightSidebar: {
    queueTitle: (count) => `Queue (${count})`,
    queueProgress: (cur, tot, file) => `Memproses ${cur}/${tot}: ${file}`,
    queueConverted: (count) => `${count} terkonversi`,
    resetQueue: 'Reset',
    convertAll: 'Konversi Semua',
    queueEmptyTitle: 'Antrean masih kosong.',
    queueEmptyDesc: 'Tarik gambar ke kanvas atau klik pilih file.',
    statusVectorizing: (cur, tot) => `MEMPROSES ${cur}/${tot}`,
    metadataTitle: 'Metadata Microstock AI',
    generateBtn: 'Buat Metadata',
    regenerateBtn: 'Buat Ulang',
    categoryLabel: 'Kategori:',
    keywordsCount: (n) => `${n} Kata Kunci (Salin)`,
    keywordsMore: (n) => `+${n} lainnya`,
    noMetadataDesc: 'Belum ada metadata AI. Klik "Buat Metadata" untuk menghasilkan judul komersial, 35 kata kunci SEO, dan kategori.',
    copiedKeywords: 'Kata kunci disalin ke clipboard!',
    csvExporterTitle: 'Ekspor CSV Metadata Microstock',
    csvBtn: 'CSV',
    csvColumnsLabel: 'Kolom CSV:',
    batchExporterTitle: 'Eksportir Paket Vektor Batch',
    downloadZipBtn: 'Unduh Paket Batch ZIP',
    packagingZip: 'Mengemas file ZIP...',
    batchZipDesc: 'Mengemas seluruh file SVG, EPS 10, dan pratinjau yang telah dikonversi ke dalam 1 file ZIP.'
  },
  pricingModal: {
    title: 'Pilih Paket Vectria Anda',
    subtitle: 'Sesuaikan dengan volume produksi vektor Anda. 1 Kredit = 1 Konversi Vektor Lengkap (SVG + EPS 10 + Pratinjau Gambar).',
    rolloverNotice: 'Tanpa biaya tersembunyi. Sisa kredit langganan yang belum terpakai otomatis rollover selama tagihan aktif.',
    monthly: 'Bulanan',
    yearly: 'Tahunan',
    savePercent: 'Hemat hingga 20%',
    perMonth: ' / bulan',
    perYear: ' / tahun',
    currentPlan: 'Paket Saat Ini',
    popularBadge: 'Paling Populer',
    bestValueBadge: 'Nilai Terbaik',
    choosePlan: (name, price) => `Pilih ${name} (${price})`,
    bullets: {
      starter: [
        'Ekspor SVG Murni & EPS 10 Bebas Bitmap',
        'File Pratinjau PNG / JPG Resolusi Tinggi',
        'Studio Inspeksi Tampilan Ganda & Garis Luar',
        'Pemrosesan File Tunggal & Multi-File'
      ],
      pro: [
        'Semua fitur pada paket Starter',
        'Ekspor Batch ZIP (hingga 50 file sekaligus)',
        'Pembuat Kata Kunci Otomatis AI & Adobe CSV',
        'Validasi Bersertifikat Bebas Bitmap EPS 10',
        'Antrean pemrosesan vektor prioritas tinggi'
      ],
      studio: [
        'Semua fitur pada paket Pro',
        'Pemrosesan Antrean Batch Tanpa Batas',
        'Jalur Pemrosesan Dedicated Kecepatan Ultra',
        'Lisensi Tim Multi-Kursi & Dukungan Prioritas'
      ]
    },
    activeSubscription: (plan, credits) => `Paket Aktif: ${plan} • Tersedia ${credits.toLocaleString()} Kredit`,
    cancelSubscription: 'Batalkan Langganan',
    cancelConfirmTitle: 'Batalkan Langganan?',
    cancelConfirmDesc: (credits) => `Apakah Anda yakin ingin membatalkan langganan? Anda tetap dapat menggunakan ${credits.toLocaleString()} sisa kredit Anda, namun antrean prioritas dan fitur ekspor batch akan berhenti saat periode berakhir.`,
    cancelKeepBtn: 'Pertahankan Langganan',
    cancelProceedBtn: 'Ya, Batalkan Langganan',
    cancelledSuccess: 'Langganan Anda telah berhasil dibatalkan. Sisa kredit Anda tetap dapat digunakan.',
    renewsOn: (date) => `Memperpanjang otomatis pada ${date}`,
    cancelsOn: (date) => `Paket dibatalkan pada ${date} • Akses penuh tetap aktif hingga tanggal tersebut`,
    cancellationScheduledBadge: 'Pembatalan Terjadwal',
    activePeriod: (start, end) => `Periode Aktif: ${start} s/d ${end}`,
    cancelsOnNotice: (plan, date) => `Paket ${plan} Anda dijadwalkan berakhir pada ${date}. Akses fitur ${plan} dan sisa kredit tetap aktif sepenuhnya hingga tanggal tersebut.`,
    renewsOnNotice: (date, amount) => `Memperpanjang otomatis pada ${date} (${amount}).`,
    higherPlanAlert: 'Paket Anda saat ini lebih tinggi.',
    samePlanNotice: 'Anda sudah berlangganan paket ini. Untuk menambah kredit, silakan gunakan fitur Beli Kredit (Top-up).',
    includedBadge: 'Termasuk',
    includedInPlan: 'Termasuk dalam paket Anda',
    topupPrompt: 'Butuh kredit tambahan sebelum perpanjangan?',
    topupBtn: 'Beli Kredit (Top-up)'
  },
  passwordModal: {
    title: 'Ubah Password Akun',
    subtitle: 'Masukkan password Anda saat ini dan buat password baru yang aman.',
    currentPasswordLabel: 'Password Saat Ini',
    newPasswordLabel: 'Password Baru',
    confirmPasswordLabel: 'Konfirmasi Password Baru',
    placeholderCurrent: 'Masukkan password saat ini',
    placeholderNew: 'Minimal 8 karakter',
    placeholderConfirm: 'Ketik ulang password baru',
    submitBtn: 'Simpan Password Baru',
    updating: 'Memperbarui...',
    successMsg: 'Password Anda berhasil diperbarui!',
    mismatchError: 'Konfirmasi password baru tidak cocok.',
    minLenError: 'Password baru minimal 8 karakter.',
    sameError: 'Password baru harus berbeda dengan password lama.',
    cancelBtn: 'Batal',
  },
  topupModal: {
    title: 'Beli Kredit Tambahan (Top-up)',
    subtitle: 'Beli kredit ekstra secara instan tanpa memengaruhi tanggal siklus tagihan langganan Anda.',
    currentBalanceLabel: 'Saldo Akun Saat Ini:',
    vectorTab: 'Vector Credit',
    aiTab: 'AI Credit',
    vectorDesc: '1 Vector Credit = 1 konversi SVG + EPS 10 lengkap. Tidak pernah kedaluwarsa selama akun aktif.',
    aiDesc: 'AI Credit digunakan untuk pembuatan metadata AI otomatis. Hanya dipotong jika proses berhasil.',
    saveTag: (pct) => `Hemat ${pct}%`,
    perCredit: (cost) => `(${cost} / kredit)`,
    buyBtn: (name, price) => `Beli ${name} (${price})`,
    buyPack: (price) => `Beli (${price})`,
    popularBadge: 'Paling Populer',
    bestValueBadge: 'Nilai Terbaik',
    successAlert: (amount, type) => `Berhasil menambahkan +${amount.toLocaleString('id-ID')} Kredit ${type} ke saldo akun Anda!`,
    connectingCheckout: 'Menghubungkan ke pembayaran...',
    closeBtn: 'Tutup',
    packs: {
      vec_200: 'Mini Booster',
      vec_500: 'Growth Booster',
      vec_1500: 'Power Booster',
      ai_500: 'AI Mini Pack',
      ai_1500: 'AI Ultra Pack',
      ai_4000: 'AI Studio Pack'
    }
  },
  aiModal: {
    title: 'Pengaturan Kunci AI (BYOK)',
    subtitle: 'Kredensial BYOK dienkripsi dan disimpan aman di server Vectria. Kunci hanya digunakan untuk permintaan metadata yang Anda izinkan.',
    selectProvider: 'Pilih Penyedia AI',
    apiKeyLabel: (prov) => `Kunci API ${prov}`,
    apiKeysHint: 'Satu API key per baris. Vectria mencoba key berikutnya jika key aktif gagal. Maksimal 10 key.',
    apiKeysCount: (count) => `${count}/10 key`,
    getKey: 'Dapatkan Kunci',
    primaryModel: 'Model AI Utama',
    customModelPlaceholder: 'Masukkan identifier model kustom (contoh: gemini-3.8-flash-lite)',
    fallbackModel: 'Model Cadangan (Otomatis beralih jika model utama gagal/limit)',
    fallbackNone: 'Tidak ada (Langsung hentikan proses jika model utama gagal)',
    fallbackDesc: 'Jika model utama mengembalikan error 429 (rate-limit), 503 (sibuk), atau gagal, Vectria secara mulus mencoba ulang menggunakan model cadangan ini.',
    customFallbackPlaceholder: 'Masukkan identifier model cadangan kustom',
    customEndpointLabel: 'URL Endpoint Kustom Kompatibel OpenAI',
    customEndpointDesc: 'Gunakan OpenRouter atau instalasi lokal Ollama / vLLM (contoh: http://localhost:11434/v1/chat/completions).',
    testConnection: 'Uji Koneksi',
    testConnectionDesc: 'Validasi keaktifan kunci API dan ketersediaan model',
    verifying: 'Memverifikasi...',
    autoMetadataTitle: 'Aktifkan Pembuatan Metadata Otomatis',
    autoMetadataDesc: 'Otomatis melabeli vektor hasil konversi dengan judul, 35 kata kunci, dan kategori komersial.',
    vectriaFallbackTitle: 'Fallback AI Vectria',
    vectriaFallbackDesc: 'Fallback opsional memakai Agen AI Vectria hanya setelah gangguan sementara BYOK. Setiap aset berhasil memakai 1 AI Credit.',
    sourceByok: 'BYOK · 0 AI Credit',
    sourceManaged: 'Agen AI Vectria',
    sourceFallback: 'BYOK dengan fallback Vectria',
    estimatedCharge: (count) => `Maksimal ${count.toLocaleString()} AI Credit`,
    byokNoCharge: 'Pembuatan dengan BYOK tidak memakai AI Credit.',
    insufficientAiCredits: 'AI Credit tidak cukup untuk batch managed AI ini.',
    resultSummary: (byok, managed, fallback, charged) => `${byok} BYOK · ${managed} managed · ${fallback} fallback · ${charged} AI Credit dipotong`,
    cancel: 'Batal',
    saveConfig: 'Simpan Konfigurasi'
  }
};

const zh: StudioCopy = {
  header: {
    planNames: {
      free: '免费方案',
      starter: '入门方案 (Starter)',
      pro: '专业方案 (Pro)',
      studio: '工作室方案 (Studio)',
      agency: '机构方案 (Agency)'
    },
    credits: (n) => `${n.toLocaleString()} 点数`,
    vectorCredits: (n) => `${n.toLocaleString()} Vector Credit`,
    aiCredits: (n) => `${n.toLocaleString()} AI Credit`,
    unlimitedCredits: '无限 Vector Credit',
    upgrade: '升级方案',
    creditTooltip: '1 Vector Credit = 1 次完整商业级矢量转换 (SVG + EPS 10 + 高清预览)。点击查看方案或充值。',
    aiCreditTooltip: '仅当使用 Vectria AI 智能助手 成功生成一个素材时扣除 1 AI Credit。BYOK 不消耗 AI Credit。',
    admin: '管理后台',
    userMenu: {
      accountPlan: '当前方案:',
      remainingCredits: '剩余点数:',
      vectorCreditLabel: 'Vector Credit:',
      aiCreditLabel: 'AI Credit:',
      creditNote: 'Vector Credit 用于矢量转换；AI Credit 仅用于成功生成的 Vectria AI 智能助手 元数据。',
      upgradePlan: '升级方案',
      managePlan: '管理订阅',
      workspace: '工作空间',
      byokKeys: '自备 AI 密钥 (BYOK)',
      themeLight: '浅色主题',
      themeDark: '深色主题',
      signOut: '退出登录',
      adminConsole: '平台管理者后台',
      changePassword: '修改密码',
      topupCredits: '购买点数 (充值)'
    },
    collapseQueue: '收起队列面板',
    expandQueue: '展开队列面板',
    toggleTheme: '切换主题外观'
  },
  leftSidebar: {
    autoOptimize: '智能自动优化模式',
    analyzing: '正在智能分析图像…',
    autoNoticeEmpty: '请先上传图像以启用智能自动识别与参数推荐',
    autoTitleEmpty: '上传图像后可启动自动轮廓识别与分色分析',
    autoTitleActive: '分析图像边缘密度与色阶,智能合并相似色为规整矢量色块',
    perImagePrefix: '单图识别:',
    quickPresets: '快捷预设',
    guideBtn: '使用指南',
    guideTitle: '矢量预设参考指南',
    guideItems: [
      { name: '扁平图标 Flat (6–8 色)', desc: '适用于图标集、极简 Logo、标牌及 UI 符号。彻底消除色彩断层与色阶杂边。' },
      { name: '活力插画 Vibrant (12–16 色)', desc: '适用于贴纸、徽章、吉祥物及带有基础赛璐璐明暗阴影的卡通作品。' },
      { name: '精细插画 Detailed (24–32 色)', desc: '适用于多层级商业插画与细腻游戏原画。不推荐用于扁平极简图标。' },
      { name: '全彩质感 Truecolor (64+ 色)', desc: '适用于自然风景、数码绘画及富有写实质感的视觉插画。' },
      { name: '黑白单线 Monoline (2 色)', desc: '适用于纯线条画、剪影、版画印章及高对比黑白美术作品。' }
    ],
    presetChips: {
      'flat-6': '扁平图标',
      'vibrant-12': '活力插画',
      'detailed-32': '精细插画',
      'ultra-64': '超清 64 色',
      'truecolor': '全真色彩',
      'monoline': '黑白单线'
    },
    presetTooltips: {
      'flat-6': '扁平图标:8 色以内,低杂点过滤,保护尖锐转角,边缘无侵蚀',
      'vibrant-12': '活力艺术:适用于贴纸、徽章、IP 形象与分层图画的均衡配置',
      'detailed-32': '精细 32 色:适用于多层次复杂插画与带有细腻渐进阴影的角色',
      'ultra-64': '超清 64 色:色彩层次丰富的商业图画与风景(扁平图标请勿使用)',
      'truecolor': '全真色彩:用于写实照片级渲染与高度复杂的数码艺术品',
      'monoline': '黑白单线:纯净路径勾线、矢量剪影与单色印章印痕'
    },
    outputFormats: '输出文件格式',
    formatBoth: 'SVG + EPS 10',
    formatSvg: '仅 SVG',
    formatEps: 'EPS 10',
    tuningTitle: '矢量引擎精细微调',
    colorLimit: '颜色分色上限',
    truecolorLabel: '全真色彩 (256+)',
    colorsUnit: (n) => `${n} 色`,
    colorHints: {
      flat: '适合极简图标与商业标志矢量化',
      vibrant: '最适合插画贴纸、勋章与卡通形象',
      detailed: '适合色彩丰富、明暗层次分明的专业插画',
      photo: '建议用于自然风光与具象写实类图像'
    },
    speckleFilter: '杂点过滤 (微噪抑制)',
    cornerThreshold: '转角尖锐度阈值',
    curveSmoothness: '贝塞尔曲线平滑度',
    curveTolerance: '曲线拟合容差',
    colorMerge: '近似色容差合并 (Δ)',
    nativeTopology: '原生拓扑架构',
    validated: '通过校验',
    stripWhiteBg: '自动剥除纯白背景',
    stripWhiteDesc: '剔除负空间多余填充并抑制边缘白边',
    whiteSensitivity: '白色判定灵敏度',
    antiHalo: '边缘内缩 (去光晕白边)',
    computeEngine: '计算引擎',
    engineAuto: '自动',
    engineDevice: '设备 (WASM)',
    engineCloud: '云端 (VPS)',
    engineAutoDesc: (cores: number) => `自动：${cores >= 4 ? '设备 WASM' : '云服务器'}（${cores} 核）`,
    engineDeviceDesc: '通过 WebAssembly 在设备 CPU/GPU 上本地运行。速度快，无服务器队列。',
    engineCloudDesc: '在 Vectria 云 VPS 上远程运行。适合省电或低配置设备。'
  },
  canvas: {
    dropTitle: '拖拽 AI 图像至此区域',
    dropDesc: '支持 Midjourney、Recraft 或 Leonardo AI 输出的 PNG、JPG、JPEG 及 WEBP 格式。',
    browseBtn: '从本地设备浏览选择',
    dualView: '双联对比',
    vectorView: '纯矢量',
    outlineView: '路径线框',
    dualViewTitle: '双联对比:原始位图像素与矢量化成果左右对照',
    vectorViewTitle: '全彩矢量画面视图',
    outlineViewTitle: '纯贝塞尔曲线路径线框模式 (类 Illustrator Ctrl+Y)',
    downloadSvg: 'SVG',
    downloadEps: 'EPS 10',
    revectorize: '重新矢量化',
    revectorizeTitle: '按照当前最新调整的参数重新执行矢量转换',
    convertVector: '转换矢量',
    convertAll: '全部转换',
    vectorizing: '矢量计算中...',
    computingCurves: '正在计算高精度贝塞尔曲线与拐角锚点',
    noVectorTitle: '尚未生成矢量结果',
    noVectorDesc: '将该位图转换为标准 SVG 与零位图 EPS 10 商业格式',
    vectorReadyTitle: '准备就绪,可开始转换',
    vectorReadyDesc: '点击下方按钮将原图像素转换为符合微利图库审核标准的纯数学曲线',
    tagRaster: '位图像素',
    tagOriginal: '原图像素',
    tagVectorSvg: '矢量 SVG (纯贝塞尔曲线)',
    tagVectorWireframe: '路径线框 (纯贝塞尔曲线)',
    tagOutlinePure: '矢量轮廓线',
    zoomOut: '缩小画面 (-)',
    resetZoom: '恢复原始比例 (100%)',
    zoomIn: '放大画面 (+) — 最大 800%',
    wireframeInspect: '查看纯矢量路径轮廓线 (Illustrator Ctrl+Y)',
    wireframeFills: '切回完整色彩填充状态',
    checkerboardSolid: '切换为纯黑深色背景',
    checkerboardTrans: '切换为棋盘格透明指示背景',
    verifiedTitle: '图库就绪',
    verifiedSpecs: '4000 × 4000 px • 纯矢量',
    verifiedBadge: 'EPS 10'
  },
  rightSidebar: {
    queueTitle: (count) => `队列 (${count})`,
    queueProgress: (cur, tot, file) => `处理中 ${cur}/${tot}: ${file}`,
    queueConverted: (count) => `已完成 ${count} 项`,
    resetQueue: '清空',
    convertAll: '全部转换',
    queueEmptyTitle: '处理队列为空。',
    queueEmptyDesc: '将图像拖放至主画布或点击浏览文件添加。',
    statusVectorizing: (cur, tot) => `处理中 ${cur}/${tot}`,
    metadataTitle: 'AI 图库商业元数据',
    generateBtn: '一键生成',
    regenerateBtn: '重新生成',
    categoryLabel: '分类:',
    keywordsCount: (n) => `${n} 个标签 (点击复制)`,
    keywordsMore: (n) => `+ 还有 ${n} 个`,
    noMetadataDesc: '暂未生成 AI 元数据。点击"一键生成"即刻输出符合图库规范的商业标题、35 个高权重 SEO 关键词及分类。',
    copiedKeywords: '已成功复制关键词至剪贴板!',
    csvExporterTitle: '微利图库元数据 CSV 导出器',
    csvBtn: 'CSV',
    csvColumnsLabel: '包含字段:',
    batchExporterTitle: '批量矢量归档打包',
    downloadZipBtn: '下载批量 ZIP 压缩包',
    packagingZip: '正在打包压缩 ZIP 文件...',
    batchZipDesc: '将所有已成功转换的 SVG、EPS 10 与高清预览图一键归档为单个 ZIP。'
  },
  pricingModal: {
    ...en.pricingModal,
    title: '选择适合您的 Vectria 方案',
    subtitle: '根据您的每月矢量产出需求选择合适方案。1 点数 = 1 次完整商业转换 (SVG + EPS 10 + 预览图)。',
    rolloverNotice: '无隐藏费用。只要订阅处于有效状态,未使用的点数将在计费周期内自动累积结转(Rollover)。',
    monthly: '月度订阅',
    yearly: '年度订阅',
    savePercent: '年付立省 20%',
    perMonth: ' / 月',
    perYear: ' / 年',
    currentPlan: '当前方案',
    popularBadge: '最受欢迎',
    bestValueBadge: '最高性价比',
    choosePlan: (name, price) => `选择 ${name} (${price})`,
    bullets: {
      starter: [
        '纯净 SVG 与零位图 EPS 10 商业格式导出',
        '配套生成超高清 PNG / JPG 伴侣预览图',
        '双联对比视图与路径线框深度检查工具',
        '单张精修与多图连续作业支持',
      ],
      pro: [
        '包含入门方案 (Starter) 的所有功能',
        '批量 ZIP 归档下载 (单次可处理多达 50 张文件)',
        'AI 自动生成商业关键词与 Adobe CSV',
        '符合 Adobe Stock 标准的零位图 EPS 10 认证',
        '专享高优先级矢量转换高速排队通道'
      ],
      studio: [
        '包含专业方案 (Pro) 的所有特权',
        '无限量批量文件排队与全自动无缝流水线',
        '独享专属超高速专用渲染与曲线拟合通道',
        '支持团队多席位协同授权与 1 对 1 专家级技术支持'
      ]
    },
    activeSubscription: (plan, credits) => `当前生效方案:${plan} • 剩余 ${credits.toLocaleString()} 点数可用`,
    cancelSubscription: '取消自动续费订阅',
    cancelConfirmTitle: '确定取消订阅吗?',
    cancelConfirmDesc: (credits) => `确定要取消订阅吗?您目前持有的 ${credits.toLocaleString()} 个剩余点数将全数保留并可随时使用,但当前计费周期结束后,高速优先通道及批量 ZIP 导出功能将被关闭。`,
    cancelKeepBtn: '保留当前订阅',
    cancelProceedBtn: '确认取消订阅',
    cancelledSuccess: '您的订阅已成功取消,剩余点数依然完全有效。',
    higherPlanAlert: '您当前订购的方案级别更高。',
    samePlanNotice: '您已订阅此方案。如需增加点数，请使用“购买点数”充值功能。',
    includedBadge: '已包含',
    includedInPlan: '已包含在您当前方案中',
    topupPrompt: '在续费前点数不足？',
    topupBtn: '购买点数 (充值)'
  },
  passwordModal: {
    title: '修改账户密码',
    subtitle: '更新您的登录密码，长度至少为 8 位字符。',
    currentPasswordLabel: '当前密码',
    newPasswordLabel: '新密码',
    confirmPasswordLabel: '确认新密码',
    placeholderCurrent: '输入当前密码',
    placeholderNew: '至少 8 个字符',
    placeholderConfirm: '再次输入新密码',
    submitBtn: '保存新密码',
    updating: '正在更新...',
    successMsg: '密码已成功修改！',
    mismatchError: '两次输入的新密码不一致。',
    minLenError: '新密码长度至少需要 8 个字符。',
    sameError: '新密码必须与当前原密码不同。',
    cancelBtn: '取消'
  },
  topupModal: {
    title: '购买额外点数 (加电充值)',
    subtitle: '即时补充矢量点数或 AI 点数，完全不影响您现有的订阅周期与扣费日期。',
    currentBalanceLabel: '当前账户可用点数:',
    vectorTab: 'Vector Credit 矢量点数',
    aiTab: 'AI Credit 智能点数',
    vectorDesc: '1 个 Vector Credit = 1 次完整商业级矢量转换 (SVG + EPS 10)。账户有效期间永久可用，永不过期。',
    aiDesc: 'AI 点数用于 Vectria AI 智能助手商业元数据打标。仅在调用生成成功时扣除。',
    saveTag: (pct) => `立省 ${pct}%`,
    perCredit: (cost) => `(${cost} / 点)`,
    buyBtn: (name, price) => `购买 ${name} (${price})`,
    buyPack: (price) => `立即购买 (${price})`,
    popularBadge: '最受欢迎',
    bestValueBadge: '最高性价比',
    successAlert: (amount, type) => `成功为您充值 +${amount.toLocaleString()} ${type === 'vector' ? 'Vector' : 'AI'} Credit！`,
    connectingCheckout: '正在跳转至安全结账网关...',
    closeBtn: '关闭',
    packs: {
      vec_200: '迷你扩容包',
      vec_500: '进阶成长包',
      vec_1500: '高能强力包',
      ai_500: 'AI 迷你包',
      ai_1500: 'AI 进阶包',
      ai_4000: 'AI 旗舰包'
    }
  },
  aiModal: {
    title: '自备密钥 AI 元数据配置 (BYOK)',
    subtitle: 'BYOK API 凭据经加密后安全存储在 Vectria 服务器中。密钥仅用于您授权的元数据请求。',
    selectProvider: '选择 AI 服务提供商',
    apiKeyLabel: (prov) => `${prov} API 密钥`,
    apiKeysHint: '每行输入一个 API 密钥。当前密钥失败时,Vectria 会尝试下一个。最多 10 个密钥。',
    apiKeysCount: (count) => `${count}/10 个密钥`,
    getKey: '获取密钥',
    primaryModel: '首选 AI 主模型',
    customModelPlaceholder: '输入自定义模型识别码 (例如 gemini-3.8-flash-lite)',
    fallbackModel: '容灾备选模型 (主模型遭遇限流或故障时自动无缝接管)',
    fallbackNone: '无备选 (主模型失败时直接报错)',
    fallbackDesc: '当首选模型出现 429 (频率受限) 或 503 (服务器繁忙) 时,Vectria 会立即自动切换至备选模型完成关键词打标。',
    customFallbackPlaceholder: '输入自定义备选模型标识符',
    customEndpointLabel: '自定义 OpenAI 兼容协议 Endpoint URL',
    customEndpointDesc: '支持 OpenRouter 中转或本地私有化 Ollama / vLLM (例如 http://localhost:11434/v1/chat/completions)。',
    testConnection: '测试接口连通性',
    testConnectionDesc: '验证 API 密钥有效性及模型接口可达性',
    verifying: '正在验证连通性...',
    autoMetadataTitle: '启用矢量化完成自动生成元数据',
    autoMetadataDesc: '矢量转换完毕后自动调用 AI 生成英文标题、35 个热搜关键词及图库分类。',
    vectriaFallbackTitle: 'Vectria AI 后备服务',
    vectriaFallbackDesc: '仅在 BYOK 暂时故障后，才会按您的选择改用 Vectria AI 智能助手。每个成功素材消耗 1 AI Credit。',
    sourceByok: 'BYOK · 0 AI Credit',
    sourceManaged: 'Vectria AI 智能助手',
    sourceFallback: 'BYOK + Vectria 后备服务',
    estimatedCharge: (count) => `最多 ${count.toLocaleString()} AI Credit`,
    byokNoCharge: '使用 BYOK 生成不会消耗 AI Credit。',
    insufficientAiCredits: 'AI Credit 不足，无法提交此托管 AI 批次。',
    resultSummary: (byok, managed, fallback, charged) => `${byok} 个 BYOK · ${managed} 个托管 · ${fallback} 个后备 · 已扣 ${charged} AI Credit`,
    cancel: '取消',
    saveConfig: '保存配置'
  }
};

const ja: StudioCopy = {
  header: {
    planNames: {
      free: '無料プラン',
      starter: 'スタータープラン',
      pro: 'プロプラン',
      studio: 'スタジオプラン',
      agency: 'エージェンシープラン'
    },
    credits: (n) => `${n.toLocaleString()} クレジット`,
    vectorCredits: (n) => `${n.toLocaleString()} Vector Credit`,
    aiCredits: (n) => `${n.toLocaleString()} AI Credit`,
    unlimitedCredits: 'Vector Credit 無制限',
    upgrade: 'アップグレード',
    creditTooltip: '1 Vector Credit = 1 回の完全ベクター書き出し (SVG + EPS 10 + プレビュー)。プラン確認またはチャージ。',
    aiCreditTooltip: 'Vectria AI エージェント で素材の生成に成功した場合のみ 1 AI Credit を消費します。BYOK は AI Credit を消費しません。',
    admin: '管理コンソール',
    userMenu: {
      accountPlan: '契約プラン:',
      remainingCredits: '残りクレジット:',
      vectorCreditLabel: 'Vector Credit:',
      aiCreditLabel: 'AI Credit:',
      creditNote: 'Vector Credit はベクター変換用です。AI Credit は Vectria AI エージェント で成功したメタデータ生成にのみ使用します。',
      upgradePlan: 'プランをアップグレード',
      managePlan: 'サブスクリプション管理',
      workspace: 'ワークスペース',
      byokKeys: 'BYOK AI キー設定',
      themeLight: 'ライトテーマ',
      themeDark: 'ダークテーマ',
      signOut: 'ログアウト',
      adminConsole: 'オーナー管理画面',
      changePassword: 'パスワード変更',
      topupCredits: 'クレジット追加購入 (チャージ)'
    },
    collapseQueue: 'キューパネルを折りたたむ',
    expandQueue: 'キューパネルを展開する',
    toggleTheme: 'テーマ切り替え'
  },
  leftSidebar: {
    autoOptimize: '自動最適化モード',
    analyzing: '画像を解析中…',
    autoNoticeEmpty: '自動解析を有効にするには、まず画像をアップロードしてください',
    autoTitleEmpty: '画像をアップロードすると自動最適化解析が有効になります',
    autoTitleActive: '輪郭と線密度を解析し、類似色を滑らかなベクター塗りに統合します',
    perImagePrefix: '画像別解析:',
    quickPresets: 'プリセット',
    guideBtn: 'ガイド',
    guideTitle: 'プリセットリファレンスガイド',
    guideItems: [
      { name: 'フラット (6〜8色)', desc: 'アイコンセット、ミニマルロゴ、ピクトグラム、UIシンボル向け。色ムラやトーンジャンプを防止。' },
      { name: 'ビビッド (12〜16色)', desc: 'ステッカー、バッジ、マスコット、セル画調のシンプルなアニメ調イラスト向け。' },
      { name: '詳細 (24〜32色)', desc: 'レイヤーの多いイラストや陰影のあるゲーム素材。フラットアイコンには非推奨。' },
      { name: 'フルカラー (64色以上)', desc: '風景画、デジタルペインティング、写実的なイラスト表現向け。' },
      { name: 'モノライン (2色)', desc: '純粋な線画、シルエット、スタンプ、高コントラストの白黒アートワーク向け。' }
    ],
    presetChips: {
      'flat-6': 'フラットアイコン',
      'vibrant-12': 'ビビッドアート',
      'detailed-32': '詳細 32色',
      'ultra-64': '高精細 64色',
      'truecolor': 'フルカラー',
      'monoline': 'モノクロ線画'
    },
    presetTooltips: {
      'flat-6': 'フラットアイコン:8色以内、ノイズ除去重視、鋭角コーナー保護、輪郭侵食なし',
      'vibrant-12': 'ビビッドアート:ステッカー、バッジ、キャラクター向けのバランス調整',
      'detailed-32': '詳細 32色:階層の深いイラストや陰影のあるキャラクター向け',
      'ultra-64': '高精細 64色:色彩豊かな風景・アートワーク向け(フラットアイコンは非推奨)',
      'truecolor': 'フルカラー:写実的な写真調アートや複雑な絵画向け',
      'monoline': 'モノクロ線画:純粋なラインアート、シルエット、スタンプ向け'
    },
    outputFormats: '書き出し形式',
    formatBoth: 'SVG + EPS 10',
    formatSvg: 'SVG のみ',
    formatEps: 'EPS 10',
    tuningTitle: 'ベクター詳細パラメータ調整',
    colorLimit: '色数上限',
    truecolorLabel: 'フルカラー (256色以上)',
    colorsUnit: (n) => `${n} 色`,
    colorHints: {
      flat: 'アイコンやミニマルなロゴに最適',
      vibrant: 'ステッカーやキャラクターイラストに最適',
      detailed: 'グラデーションや陰影のある本格的イラスト向け',
      photo: '風景や写実的アートワークに推奨'
    },
    speckleFilter: 'スペックルノイズ除去',
    cornerThreshold: 'コーナーしきい値',
    curveSmoothness: 'ベジェ曲線スムージング',
    curveTolerance: '曲線フィッティング許容値',
    colorMerge: '近似色の統合 (Δ)',
    nativeTopology: 'ネイティブトポロジー',
    validated: '検証済み',
    stripWhiteBg: '白背景を自動透過',
    stripWhiteDesc: 'ネガティブスペースと白フチを除去',
    whiteSensitivity: '白の感度',
    antiHalo: 'エッジインセット (ハロー防止)',
    computeEngine: '計算エンジン',
    engineAuto: '自動',
    engineDevice: 'デバイス (WASM)',
    engineCloud: 'クラウド (VPS)',
    engineAutoDesc: (cores: number) => `自動：${cores >= 4 ? 'デバイス WASM' : 'クラウドサーバー'}（${cores} コア）`,
    engineDeviceDesc: 'WebAssembly を介して端末の CPU/GPU でローカル実行します。高速で、サーバー待ち時間はありません。',
    engineCloudDesc: 'Vectria Cloud VPS でリモート実行します。省電力または低スペック端末に最適です。'
  },
  canvas: {
    dropTitle: 'ここに AI 生成画像をドラッグ&ドロップ',
    dropDesc: 'Midjourney、Recraft、Leonardo AI などの PNG、JPG、JPEG、WEBP 形式に対応。',
    browseBtn: '端末からファイルを選択',
    dualView: 'デュアル表示',
    vectorView: 'ベクター',
    outlineView: 'アウトライン',
    dualViewTitle: 'デュアル表示:元画像とベクター結果の左右並列プレビュー',
    vectorViewTitle: 'フルカラーベクター画面',
    outlineViewTitle: '純粋ベジェ曲線のアウトライン検査 (Illustrator の Ctrl+Y 相当)',
    downloadSvg: 'SVG',
    downloadEps: 'EPS 10',
    revectorize: '再ベクター化',
    revectorizeTitle: '現在のパラメータ設定で再度ベクター変換を実行します',
    convertVector: 'ベクター変換',
    convertAll: 'すべて変換',
    vectorizing: 'ベクター変換中...',
    computingCurves: '滑らかなベジェ曲線と鋭角コーナーを計算中',
    noVectorTitle: 'ベクターデータがまだありません',
    noVectorDesc: 'このラスター画像を SVG および EPS 10 ベクター形式に変換します',
    vectorReadyTitle: '変換準備完了',
    vectorReadyDesc: '下のボタンをクリックして、元画像をマイクロストック審査基準適合のベジェ曲線に変換します',
    tagRaster: 'ラスター画素',
    tagOriginal: '元画像',
    tagVectorSvg: 'ベクター SVG (純粋ベジェ曲線)',
    tagVectorWireframe: 'アウトライン (純粋ベジェ曲線)',
    tagOutlinePure: 'ベクター輪郭線',
    zoomOut: '縮小 (-)',
    resetZoom: '100% 表示に戻す',
    zoomIn: '拡大 (+) — 最大 800%',
    wireframeInspect: '純粋ベジェ曲線のアウトラインを検査 (Illustrator Ctrl+Y)',
    wireframeFills: 'フルカラー塗りに切り替える',
    checkerboardSolid: '暗い背景色に切り替える',
    checkerboardTrans: '透明チェッカーボードに切り替える',
    verifiedTitle: 'ストック対応',
    verifiedSpecs: '4000 × 4000 px • ベクターのみ',
    verifiedBadge: 'EPS 10'
  },
  rightSidebar: {
    queueTitle: (count) => `キュー (${count})`,
    queueProgress: (cur, tot, file) => `処理中 ${cur}/${tot}: ${file}`,
    queueConverted: (count) => `${count} 件完了`,
    resetQueue: 'リセット',
    convertAll: 'すべて変換',
    queueEmptyTitle: 'キューは空です。',
    queueEmptyDesc: 'キャンバスに画像をドラッグするか、ファイルを選択してください。',
    statusVectorizing: (cur, tot) => `処理中 ${cur}/${tot}`,
    metadataTitle: 'AI マイクロストックメタデータ',
    generateBtn: '自動生成',
    regenerateBtn: '再生成',
    categoryLabel: 'カテゴリ:',
    keywordsCount: (n) => `${n} 個のタグ (コピー)`,
    keywordsMore: (n) => `ほか ${n} 件`,
    noMetadataDesc: 'AI メタデータがまだ生成されていません。「自動生成」をクリックすると、商用タイトル、35 個の SEO キーワード、カテゴリが作成されます。',
    copiedKeywords: 'キーワードをクリップボードにコピーしました!',
    csvExporterTitle: 'マイクロストックメタデータ CSV 出力',
    csvBtn: 'CSV',
    csvColumnsLabel: '含まれる列:',
    batchExporterTitle: '一括ベクター ZIP エクスポート',
    downloadZipBtn: '一括 ZIP パッケージをダウンロード',
    packagingZip: 'ZIP をパッケージング中...',
    batchZipDesc: '変換済みのすべての SVG、EPS 10、プレビュー画像を 1 つの ZIP にまとめてダウンロードします。'
  },
  pricingModal: {
    ...en.pricingModal,
    title: 'Vectria プランを選択',
    subtitle: '制作ボリュームに合わせて最適なプランをお選びください。1 クレジット = 1 回の完全ベクター書き出し (SVG + EPS 10 + プレビュー)。',
    rolloverNotice: '隠れた費用はありません。サブスクリプション有効期間中、未使用のクレジットは次回請求時に自動で繰り越されます(Rollover)。',
    monthly: '月払い',
    yearly: '年払い',
    savePercent: '年払いで最大 20% お得',
    perMonth: ' / 月',
    perYear: ' / 年',
    currentPlan: '現在のプラン',
    popularBadge: '人気',
    bestValueBadge: '最もお得',
    choosePlan: (name, price) => `${name} を選択 (${price})`,
    bullets: {
      starter: [
        '純粋 SVG およびゼロビットマップ EPS 10 書き出し',
        '高解像度 PNG / JPG プレビュー画像の同時生成',
        'デュアル表示&アウトライン検査ツール',
        '単一・複数ファイルの安定処理',
      ],
      pro: [
        'スタータープランの全機能を含む',
        '一括 ZIP エクスポート (最大 50 ファイル同時処理)',
        'AI キーワード自動生成& Adobe Stock 準拠 CSV',
        'Adobe Stock 認定基準適合の EPS 10 検証',
        '優先ベクター処理キュー'
      ],
      studio: [
        'プロプランの全機能を含む',
        '無制限の一括キュー処理',
        '超高速専用レンダリングパイプライン',
        'チーム複数席ライセンス&優先専任サポート'
      ]
    },
    activeSubscription: (plan, credits) => `ご利用中のプラン:${plan} • 残り ${credits.toLocaleString()} クレジット`,
    cancelSubscription: 'サブスクリプションを解約',
    cancelConfirmTitle: 'サブスクリプションを解約しますか?',
    cancelConfirmDesc: (credits) => `解約後も、保有している ${credits.toLocaleString()} クレジットは失効せずそのままご利用いただけます。ただし、現在の請求期間終了後に優先キューおよび一括 ZIP 書き出し機能は無効となります。`,
    cancelKeepBtn: 'サブスクリプションを継続',
    cancelProceedBtn: '解約を実行する',
    cancelledSuccess: 'サブスクリプションを解約しました。残りクレジットはそのまま保持されます。',
    higherPlanAlert: '現在ご契約中のプランの方が上位です。',
    samePlanNotice: 'すでにこのプランをご契約中です。クレジットを追加するには「クレジット追加購入」をご利用ください。',
    includedBadge: '含まれています',
    includedInPlan: '現在のご契約プランに含まれています',
    topupPrompt: '更新前に追加クレジットが必要ですか？',
    topupBtn: 'クレジット追加購入'
  },
  passwordModal: {
    title: 'パスワード変更',
    subtitle: 'アカウントのログインパスワードを更新します。8文字以上で設定してください。',
    currentPasswordLabel: '現在のパスワード',
    newPasswordLabel: '新しいパスワード',
    confirmPasswordLabel: '新しいパスワード (確認)',
    placeholderCurrent: '現在のパスワードを入力',
    placeholderNew: '8文字以上',
    placeholderConfirm: '新しいパスワードを再入力',
    submitBtn: 'パスワードを更新',
    updating: '更新中...',
    successMsg: 'パスワードが正常に変更されました。',
    mismatchError: '新しいパスワードが一致しません。',
    minLenError: 'パスワードは8文字以上である必要があります。',
    sameError: '新しいパスワードは現在のパスワードと異なるものを指定してください。',
    cancelBtn: 'キャンセル'
  },
  topupModal: {
    title: 'クレジット追加購入 (チャージ)',
    subtitle: 'サブスクリプションの更新周期や次回請求日に影響を与えることなく、今すぐ追加クレジットを補充できます。',
    currentBalanceLabel: '現在のアカウント残高:',
    vectorTab: 'Vector Credit (ベクター)',
    aiTab: 'AI Credit (AI メタデータ)',
    vectorDesc: '1 Vector Credit = 1回の完全ベクター変換 (SVG + EPS 10)。アカウントが有効な限り有効期限はありません。',
    aiDesc: 'AI Credit は Vectria AI エージェントによる商用メタデータ生成に使用されます。成功時のみ消費されます。',
    saveTag: (pct) => `${pct}% お得`,
    perCredit: (cost) => `(${cost} / 1クレジット)`,
    buyBtn: (name, price) => `${name} を購入 (${price})`,
    buyPack: (price) => `パックを購入 (${price})`,
    popularBadge: '人気',
    bestValueBadge: '一番お得',
    successAlert: (amount, type) => `+${amount.toLocaleString()} ${type === 'vector' ? 'Vector' : 'AI'} Credit が正常に追加されました！`,
    connectingCheckout: '安全な決済ページへ移動しています...',
    closeBtn: '閉じる',
    packs: {
      vec_200: 'ミニブースター',
      vec_500: 'グロースブースター',
      vec_1500: 'パワーブースター',
      ai_500: 'AI ミニパック',
      ai_1500: 'AI ウルトラパック',
      ai_4000: 'AI スタジオパック'
    }
  },
  aiModal: {
    title: 'BYOK AI メタデータ設定 (キー持参)',
    subtitle: 'BYOK API 認証情報は暗号化され、Vectria サーバーに安全に保存されます。キーは許可されたメタデータ生成にのみ使用されます。',
    selectProvider: 'AI プロバイダを選択',
    apiKeyLabel: (prov) => `${prov} API キー`,
    apiKeysHint: '1 行につき API キーを 1 つ入力します。現在のキーが失敗すると次のキーを試します。最大 10 個。',
    apiKeysCount: (count) => `${count}/10 キー`,
    getKey: 'キーを取得',
    primaryModel: '主モデル',
    customModelPlaceholder: 'カスタムモデル ID を入力 (例: gemini-3.8-flash-lite)',
    fallbackModel: 'フォールバックモデル (主モデルが制限・失敗した際の自動代替)',
    fallbackNone: 'なし (主モデル失敗時は即座に停止)',
    fallbackDesc: '主モデルが 429 (レート制限) や 503 (過負荷) を返した場合、Vectria はこのフォールバックモデルに自動で切り替えて処理を継続します。',
    customFallbackPlaceholder: 'カスタムフォールバックモデル ID を入力',
    customEndpointLabel: 'OpenAI 互換カスタムエンドポイント URL',
    customEndpointDesc: 'OpenRouter やローカルの Ollama / vLLM を利用可能 (例: http://localhost:11434/v1/chat/completions)。',
    testConnection: '接続テスト',
    testConnectionDesc: 'API キーの有効性とモデルの応答を確認します',
    verifying: '検証中...',
    autoMetadataTitle: 'ベクター化完了時の自動メタデータ生成',
    autoMetadataDesc: '変換完了時にタイトル、35 個のキーワード、ストックカテゴリを自動付与します。',
    vectriaFallbackTitle: 'Vectria AI フォールバック',
    vectriaFallbackDesc: 'BYOK の一時的な障害後、許可された場合のみ Vectria AI エージェント を使用します。成功した素材ごとに 1 AI Credit を消費します。',
    sourceByok: 'BYOK · 0 AI Credit',
    sourceManaged: 'Vectria AI エージェント',
    sourceFallback: 'BYOK + Vectria フォールバック',
    estimatedCharge: (count) => `最大 ${count.toLocaleString()} AI Credit`,
    byokNoCharge: 'BYOK 生成では AI Credit を消費しません。',
    insufficientAiCredits: 'このマネージド AI バッチに必要な AI Credit が不足しています。',
    resultSummary: (byok, managed, fallback, charged) => `BYOK ${byok}件 · マネージド ${managed}件 · フォールバック ${fallback}件 · ${charged} AI Credit 消費`,
    cancel: 'キャンセル',
    saveConfig: '設定を保存'
  }
};

const ko: StudioCopy = {
  header: {
    planNames: {
      free: '무료 플랜',
      starter: '스타터 플랜',
      pro: '프로 플랜',
      studio: '스튜디오 플랜',
      agency: '에이전시 플랜'
    },
    credits: (n) => `${n.toLocaleString()} 크레딧`,
    vectorCredits: (n) => `${n.toLocaleString()} Vector Credit`,
    aiCredits: (n) => `${n.toLocaleString()} AI Credit`,
    unlimitedCredits: 'Vector Credit 무제한',
    upgrade: '업그레이드',
    creditTooltip: '1 Vector Credit = 1회 완전한 벡터 변환 (SVG + EPS 10 + 미리보기). 플랜 확인 또는 충전.',
    aiCreditTooltip: 'Vectria AI 에이전트로 에셋 생성에 성공한 경우에만 1 AI Credit이 차감됩니다. BYOK는 AI Credit을 사용하지 않습니다.',
    admin: '관리 콘솔',
    userMenu: {
      accountPlan: '이용 플랜:',
      remainingCredits: '잔여 크레딧:',
      vectorCreditLabel: 'Vector Credit:',
      aiCreditLabel: 'AI Credit:',
      creditNote: 'Vector Credit은 벡터 변환용입니다. AI Credit은 성공한 Vectria AI 에이전트 메타데이터 생성에만 사용됩니다.',
      upgradePlan: '플랜 업그레이드',
      managePlan: '구독 관리',
      workspace: '워크스페이스',
      byokKeys: 'BYOK AI 키 설정',
      themeLight: '라이트 테마',
      themeDark: '다크 테마',
      signOut: '로그아웃',
      adminConsole: '소유자 관리자 콘솔',
      changePassword: '비밀번호 변경',
      topupCredits: '크레딧 추가 충전'
    },
    collapseQueue: '대기열 패널 접기',
    expandQueue: '대기열 패널 펼치기',
    toggleTheme: '테마 전환'
  },
  leftSidebar: {
    autoOptimize: '스마트 자동 최적화 모드',
    analyzing: '이미지 정밀 분석 중…',
    autoNoticeEmpty: '자동 감지를 활성화하려면 먼저 이미지를 업로드해 주세요',
    autoTitleEmpty: '이미지를 업로드하면 자동 최적화 분석이 활성화됩니다',
    autoTitleActive: '외곽선 및 선 밀도를 분석하여 유사 색상을 매끄러운 벡터 면으로 병합합니다',
    perImagePrefix: '이미지별 감지:',
    quickPresets: '빠른 프리셋',
    guideBtn: '가이드',
    guideTitle: '프리셋 상세 가이드',
    guideItems: [
      { name: '플랫 (6–8 색상)', desc: '아이콘 세트, 미니멀 로고, 픽토그램 및 UI 심볼용. 계단 현상과 색상 번짐을 완벽 차단.' },
      { name: '비비드 (12–16 색상)', desc: '스티커, 뱃지, 마스코트 로고, 셀 애니메이션 스타일 캐릭터에 최적화.' },
      { name: '디테일 (24–32 색상)', desc: '다층 구조의 일러스트 및 정밀한 게임 에셋용. 미니멀 아이콘에는 권장하지 않음.' },
      { name: '트루컬러 (64+ 색상)', desc: '풍경 아트워크, 디지털 페인팅 및 사실적인 색채 표현용.' },
      { name: '모노라인 (2 색상)', desc: '순수 라인 아트, 실루엣, 스탬프 및 고대비 흑백 작품용.' }
    ],
    presetChips: {
      'flat-6': '플랫 아이콘',
      'vibrant-12': '비비드 아트',
      'detailed-32': '디테일 32',
      'ultra-64': '울트라 HD 64',
      'truecolor': '풀 트루컬러',
      'monoline': '흑백 모노라인'
    },
    presetTooltips: {
      'flat-6': '플랫 아이콘: 8색 이하, 노이즈 필터링, 모서리 보존, 외곽선 침식 방지',
      'vibrant-12': '비비드 아트: 스티커, 뱃지, 마스코트 및 레이어드 아트에 균형 잡힌 설정',
      'detailed-32': '디테일 32: 다층 일러스트레이션 및 음영이 풍부한 캐릭터용',
      'ultra-64': '울트라 HD 64: 풍부한 색채의 일러스트 및 풍경화 (플랫 아이콘 제외)',
      'truecolor': '풀 트루컬러: 사실적인 사진 및 복합 디지털 페인팅용',
      'monoline': '흑백 모노라인: 순수 라인 드로잉, 실루엣 및 흑백 스탬프용'
    },
    outputFormats: '출력 형식',
    formatBoth: 'SVG + EPS 10',
    formatSvg: 'SVG 전용',
    formatEps: 'EPS 10',
    tuningTitle: '벡터 엔진 미세 튜닝',
    colorLimit: '색상 팔레트 제한',
    truecolorLabel: '풀 트루컬러 (256+)',
    colorsUnit: (n) => `${n}개 색상`,
    colorHints: {
      flat: '아이콘 세트 및 미니멀 로고에 최적',
      vibrant: '스티커, 뱃지 및 마스코트 일러스트에 가장 적합',
      detailed: '다채로운 색조와 정밀한 일러스트레이션용',
      photo: '풍경 및 사실적인 사진 스타일에 권장'
    },
    speckleFilter: '얼룩·노이즈 필터링',
    cornerThreshold: '코너 각도 임계값',
    curveSmoothness: '곡선 부드러움',
    curveTolerance: '곡선 피팅 허용 오차',
    colorMerge: '유사 색상 병합 (Δ)',
    nativeTopology: '네이티브 토폴로지',
    validated: '검증 완료',
    stripWhiteBg: '흰색 배경 자동 제거',
    stripWhiteDesc: '여백 공간 정리 및 외곽 흰색 헤일로 제거',
    whiteSensitivity: '흰색 판별 감도',
    antiHalo: '외곽선 미세 수축 (헤일로 방지)',
    computeEngine: '컴퓨팅 엔진',
    engineAuto: '자동',
    engineDevice: '기기 (WASM)',
    engineCloud: '클라우드 (VPS)',
    engineAutoDesc: (cores: number) => `자동: ${cores >= 4 ? '기기 WASM' : '클라우드 서버'} (${cores}코어)`,
    engineDeviceDesc: 'WebAssembly를 통해 기기 CPU/GPU에서 로컬 실행합니다. 빠르며 서버 대기열이 없습니다.',
    engineCloudDesc: 'Vectria Cloud VPS에서 원격 실행합니다. 배터리 절약 또는 저사양 기기에 적합합니다.'
  },
  canvas: {
    dropTitle: '여기에 AI 이미지를 드래그 앤 드롭하세요',
    dropDesc: 'Midjourney, Recraft, Leonardo AI 등에서 생성된 PNG, JPG, JPEG, WEBP 형식을 지원합니다.',
    browseBtn: '기기에서 파일 찾기',
    dualView: '듀얼 뷰',
    vectorView: '벡터',
    outlineView: '외곽선',
    dualViewTitle: '듀얼 뷰: 원본 픽셀과 벡터 결과를 나란히 비교',
    vectorViewTitle: '풀 컬러 벡터 화면',
    outlineViewTitle: '베지어 곡선 외곽선 검사 (Illustrator Ctrl+Y 상당)',
    downloadSvg: 'SVG',
    downloadEps: 'EPS 10',
    revectorize: '재벡터화',
    revectorizeTitle: '현재 설정된 옵션으로 벡터 변환을 다시 실행합니다',
    convertVector: '벡터 변환',
    convertAll: '모두 변환',
    vectorizing: '벡터 연산 중...',
    computingCurves: '부드러운 베지어 곡선과 선명한 코너 계산 중',
    noVectorTitle: '생성된 벡터가 없습니다',
    noVectorDesc: '이 래스터 이미지를 SVG 및 EPS 10 벡터 형식으로 변환하세요',
    vectorReadyTitle: '변환 준비 완료',
    vectorReadyDesc: '아래 버튼을 눌러 원본 픽셀을 스톡 플랫폼 심사 표준에 맞는 베지어 곡선으로 변환하세요',
    tagRaster: '래스터 픽셀',
    tagOriginal: '원본 픽셀',
    tagVectorSvg: '벡터 SVG (순수 베지어 곡선)',
    tagVectorWireframe: '외곽선 (순수 베지어 곡선)',
    tagOutlinePure: '벡터 윤곽선',
    zoomOut: '축소 (-)',
    resetZoom: '배율 초기화 (100%)',
    zoomIn: '확대 (+) — 최대 800%',
    wireframeInspect: '순수 베지어 곡선 윤곽선 검사 (Illustrator Ctrl+Y)',
    wireframeFills: '풀 컬러 채우기 화면으로 전환',
    checkerboardSolid: '단색 어두운 배경으로 전환',
    checkerboardTrans: '투명도 체크보드로 전환',
    verifiedTitle: '스톡 제출 준비 완료',
    verifiedSpecs: '4000 × 4000 px • 벡터 전용',
    verifiedBadge: 'EPS 10'
  },
  rightSidebar: {
    queueTitle: (count) => `대기열 (${count})`,
    queueProgress: (cur, tot, file) => `변환 중 ${cur}/${tot}: ${file}`,
    queueConverted: (count) => `${count}개 완료`,
    resetQueue: '초기화',
    convertAll: '모두 변환',
    queueEmptyTitle: '대기열이 비어 있습니다.',
    queueEmptyDesc: '캔버스로 이미지를 끌어오거나 파일 찾기를 클릭하세요.',
    statusVectorizing: (cur, tot) => `변환 중 ${cur}/${tot}`,
    metadataTitle: 'AI 스톡 상업용 메타데이터',
    generateBtn: '생성',
    regenerateBtn: '재생성',
    categoryLabel: '카테고리:',
    keywordsCount: (n) => `${n}개 키워드 (복사)`,
    keywordsMore: (n) => `+외 ${n}개`,
    noMetadataDesc: '아직 AI 메타데이터가 없습니다. "생성"을 누르면 상업용 제목, 35개 SEO 키워드 및 카테고리가 자동 생성됩니다.',
    copiedKeywords: '키워드를 클립보드에 복사했습니다!',
    csvExporterTitle: '스톡 마켓플레이스 메타데이터 CSV 내보내기',
    csvBtn: 'CSV',
    csvColumnsLabel: '포함 컬럼:',
    batchExporterTitle: '일괄 벡터 패키지 내보내기',
    downloadZipBtn: '일괄 ZIP 패키지 다운로드',
    packagingZip: 'ZIP 파일 압축 중...',
    batchZipDesc: '변환 완료된 모든 SVG, EPS 10 및 미리보기 파일을 단일 ZIP으로 묶어 다운로드합니다.'
  },
  pricingModal: {
    ...en.pricingModal,
    title: 'Vectria 플랜 선택',
    subtitle: '작업량에 맞는 최적의 플랜을 선택하세요. 1 크레딧 = 1회 완전한 벡터 변환 (SVG + EPS 10 + 미리보기 이미지).',
    rolloverNotice: '숨겨진 수수료 없음. 구독이 활성화되어 있는 동안 미사용 크레딧은 다음 청구 주기로 자동 이월(Rollover)됩니다.',
    monthly: '월간 결제',
    yearly: '연간 결제',
    savePercent: '연간 결제 시 최대 20% 할인',
    perMonth: ' / 월',
    perYear: ' / 년',
    currentPlan: '현재 이용 중인 플랜',
    popularBadge: '가장 인기 있는',
    bestValueBadge: '최고의 가성비',
    choosePlan: (name, price) => `${name} 선택 (${price})`,
    bullets: {
      starter: [
        '순수 SVG 및 제로 비트맵 EPS 10 상업용 내보내기',
        '고해상도 PNG / JPG 컴패니언 미리보기 동시 생성',
        '듀얼 뷰 및 외곽선 스튜디오 정밀 검사 도구',
        '단일 파일 및 다중 파일 변환 지원'
      ],
      pro: [
        '스타터 플랜의 모든 혜택 포함',
        '일괄 ZIP 내보내기 (최대 50개 파일 동시 패키징)',
        'AI 키워드 자동 생성 및 Adobe Stock 맞춤 CSV',
        'Adobe Stock 기준 제로 비트맵 인증 규격 검증',
        '우선순위 고속 벡터 변환 대기열'
      ],
      studio: [
        '프로 플랜의 모든 혜택 포함',
        '무제한 일괄 대기열 처리 파이프라인',
        '초고속 전용 벡터 렌더링 파이프라인',
        '팀 다중 좌석 라이선스 및 최우선 전담 지원'
      ]
    },
    activeSubscription: (plan, credits) => `활성 이용 플랜: ${plan} • 잔여 ${credits.toLocaleString()} 크레딧 사용 가능`,
    cancelSubscription: '구독 해지하기',
    cancelConfirmTitle: '정말 구독을 해지하시겠습니까?',
    cancelConfirmDesc: (credits) => `구독을 해지하시겠습니까? 보유 중인 ${credits.toLocaleString()} 잔여 크레딧은 사라지지 않고 계속 사용할 수 있습니다. 단, 현재 이용 기간 종료 후 고속 대기열 및 일괄 ZIP 내보내기 혜택은 비활성화됩니다.`,
    cancelKeepBtn: '구독 유지하기',
    cancelProceedBtn: '구독 해지 확인',
    cancelledSuccess: '구독이 성공적으로 해지되었습니다. 잔여 크레딧은 계속 이용하실 수 있습니다.',
    higherPlanAlert: '현재 이용 중인 플랜이 더 상위 등급입니다.',
    samePlanNotice: '이미 구독 중인 플랜입니다. 크레딧을 추가하려면 크레딧 충전 기능을 이용해 주세요.',
    includedBadge: '포함됨',
    includedInPlan: '현재 플랜에 포함됨',
    topupPrompt: '다음 갱신일 전에 크레딧이 더 필요하신가요?',
    topupBtn: '크레딧 추가 충전'
  },
  passwordModal: {
    title: '비밀번호 변경',
    subtitle: '계정 비밀번호를 변경합니다. 최소 8자 이상이어야 합니다.',
    currentPasswordLabel: '현재 비밀번호',
    newPasswordLabel: '새 비밀번호',
    confirmPasswordLabel: '새 비밀번호 확인',
    placeholderCurrent: '현재 비밀번호 입력',
    placeholderNew: '최소 8자 이상',
    placeholderConfirm: '새 비밀번호 재입력',
    submitBtn: '비밀번호 업데이트',
    updating: '변경 중...',
    successMsg: '비밀번호가 성공적으로 변경되었습니다.',
    mismatchError: '새 비밀번호가 일치하지 않습니다.',
    minLenError: '비밀번호는 최소 8자 이상이어야 합니다.',
    sameError: '새 비밀번호는 현재 비밀번호와 달라야 합니다.',
    cancelBtn: '취소'
  },
  topupModal: {
    title: '크레딧 추가 충전',
    subtitle: '기존 구독 갱신 주기나 결제일에 영향을 주지 않고 즉시 추가 크레딧을 충전할 수 있습니다.',
    currentBalanceLabel: '현재 계정 보유 잔여 크레딧:',
    vectorTab: 'Vector Credit (벡터)',
    aiTab: 'AI Credit (AI 메타데이터)',
    vectorDesc: '1 Vector Credit = 1회의 완전한 SVG + EPS 10 변환. 계정이 활성 상태인 동안 만료되지 않습니다.',
    aiDesc: 'AI Credit은 Vectria AI 에이전트를 통한 상업용 메타데이터 생성에 사용됩니다. 생성 성공 시에만 차감됩니다.',
    saveTag: (pct) => `${pct}% 할인`,
    perCredit: (cost) => `(${cost} / 크레딧당)`,
    buyBtn: (name, price) => `${name} 구매 (${price})`,
    buyPack: (price) => `팩 구매 (${price})`,
    popularBadge: '인기',
    bestValueBadge: '최고 혜택',
    successAlert: (amount, type) => `+${amount.toLocaleString()} ${type === 'vector' ? 'Vector' : 'AI'} Credit이 성공적으로 충전되었습니다!`,
    connectingCheckout: '보안 결제 페이지로 연결 중...',
    closeBtn: '닫기',
    packs: {
      vec_200: '미니 부스터',
      vec_500: '그로스 부스터',
      vec_1500: '파워 부스터',
      ai_500: 'AI 미니 팩',
      ai_1500: 'AI 울트라 팩',
      ai_4000: 'AI 스튜디오 팩'
    }
  },
  aiModal: {
    title: 'BYOK AI 메타데이터 설정 (개인 API 키)',
    subtitle: 'BYOK API 인증 정보는 암호화되어 Vectria 서버에 안전하게 저장됩니다. 키는 사용자가 승인한 메타데이터 요청에만 사용됩니다.',
    selectProvider: 'AI 제공업체 선택',
    apiKeyLabel: (prov) => `${prov} API 키`,
    apiKeysHint: '한 줄에 API 키 하나를 입력하세요. 현재 키가 실패하면 다음 키를 시도합니다. 최대 10개.',
    apiKeysCount: (count) => `${count}/10 키`,
    getKey: '키 발급받기',
    primaryModel: '주력 AI 모델',
    customModelPlaceholder: '사용자 지정 모델 ID 입력 (예: gemini-3.8-flash-lite)',
    fallbackModel: '대체 모델 (주 모델 오류 또는 속도 제한 시 자동 전환)',
    fallbackNone: '없음 (주 모델 실패 시 즉시 중단)',
    fallbackDesc: '주 모델에서 429(속도 제한) 또는 503(서버 부하) 오류가 발생하면 Vectria가 이 대체 모델로 즉시 자동 재시도합니다.',
    customFallbackPlaceholder: '사용자 지정 대체 모델 ID 입력',
    customEndpointLabel: 'OpenAI 호환 커스텀 엔드포인트 URL',
    customEndpointDesc: 'OpenRouter 또는 로컬 Ollama / vLLM 활용 가능 (예: http://localhost:11434/v1/chat/completions).',
    testConnection: '연결 테스트',
    testConnectionDesc: 'API 키 유효성 및 모델 응답 상태 검증',
    verifying: '검증 중...',
    autoMetadataTitle: '벡터화 완료 시 자동 메타데이터 생성 활성화',
    autoMetadataDesc: '벡터 변환이 끝나면 제목, 35개 키워드, 상업용 카테고리를 자동으로 태깅합니다.',
    vectriaFallbackTitle: 'Vectria AI 대체 처리',
    vectriaFallbackDesc: 'BYOK의 일시적 장애 후 사용자가 허용한 경우에만 Vectria AI 에이전트를 사용합니다. 성공한 에셋당 1 AI Credit이 차감됩니다.',
    sourceByok: 'BYOK · 0 AI Credit',
    sourceManaged: 'Vectria AI 에이전트',
    sourceFallback: 'BYOK + Vectria 대체 처리',
    estimatedCharge: (count) => `최대 ${count.toLocaleString()} AI Credit`,
    byokNoCharge: 'BYOK 생성은 AI Credit을 사용하지 않습니다.',
    insufficientAiCredits: '이 관리형 AI 일괄 작업에 필요한 AI Credit이 부족합니다.',
    resultSummary: (byok, managed, fallback, charged) => `BYOK ${byok}개 · 관리형 ${managed}개 · 대체 ${fallback}개 · ${charged} AI Credit 차감`,
    cancel: '취소',
    saveConfig: '설정 저장'
  }
};

const es: StudioCopy = {
  ...en,
  header:{...en.header,planNames:{free:'Plan Gratis',starter:'Plan Starter',pro:'Plan Pro',studio:'Plan Studio',agency:'Plan Agency'},credits:n=>`${n.toLocaleString()} créditos`,vectorCredits:n=>`${n.toLocaleString()} créditos vectoriales`,aiCredits:n=>`${n.toLocaleString()} créditos de IA`,unlimitedCredits:'Créditos ilimitados',upgrade:'Mejorar plan',creditTooltip:'1 crédito vectorial = 1 conversión (SVG + EPS 10 + vista previa). Haz clic para ver planes o recargar.',aiCreditTooltip:'Solo se cobra 1 crédito de IA cuando el Agente de IA de Vectria genera correctamente un recurso. BYOK consume 0 créditos de IA.',admin:'Administración',userMenu:{accountPlan:'Plan de la cuenta:',remainingCredits:'Créditos restantes:',vectorCreditLabel:'Crédito vectorial:',aiCreditLabel:'Crédito de IA:',creditNote:'Los créditos vectoriales se usan para convertir vectores. Los créditos de IA solo se cobran por metadatos generados correctamente por el Agente de IA de Vectria.',upgradePlan:'Mejorar plan',managePlan:'Gestionar suscripción',workspace:'Espacio de trabajo',byokKeys:'Claves de IA BYOK',themeLight:'Tema claro',themeDark:'Tema oscuro',signOut:'Cerrar sesión',adminConsole:'Consola de administración',changePassword:'Cambiar contraseña',topupCredits:'Recargar créditos'},collapseQueue:'Contraer panel de cola',expandQueue:'Expandir panel de cola',toggleTheme:'Cambiar tema'},
  leftSidebar:{...en.leftSidebar,autoOptimize:'Optimización automática',analyzing:'Analizando imagen…',autoNoticeEmpty:'Sube primero una imagen para activar la detección automática',autoTitleEmpty:'Sube primero una imagen para activar el análisis automático',autoTitleActive:'Analiza contornos, densidad de líneas y combina colores similares en rellenos vectoriales limpios',perImagePrefix:'Por imagen:',quickPresets:'Ajustes rápidos',guideBtn:'Guía',guideTitle:'Guía de ajustes predefinidos',guideItems:[{name:'Plano (6–8 colores)',desc:'Iconos, logotipos minimalistas, pictogramas y símbolos de interfaz.'},{name:'Vibrante (12–16 colores)',desc:'Pegatinas, insignias, mascotas y arte de estilo cartoon.'},{name:'Detallado (24–32 colores)',desc:'Ilustraciones con varias capas y recursos de juego detallados.'},{name:'Color real (64+ colores)',desc:'Paisajes, pintura digital y sujetos fotográficos.'},{name:'Monolínea (2 colores)',desc:'Dibujos lineales, siluetas, sellos y arte monocromo.'}],presetChips:{'flat-6':'Icono plano','vibrant-12':'Arte vibrante','detailed-32':'Detallado 32','ultra-64':'Ultra HD 64','truecolor':'Color real','monoline':'Monolínea B/N'},presetTooltips:{'flat-6':'Icono plano: 8 colores, poco filtrado de ruido y esquinas protegidas','vibrant-12':'Arte vibrante: ajustes equilibrados para pegatinas, insignias y mascotas','detailed-32':'Detallado 32: ilustraciones multicapa y personajes sombreados','ultra-64':'Ultra HD 64: arte y paisajes de color intenso','truecolor':'Color real: fotografías y pinturas complejas','monoline':'Monolínea B/N: arte lineal, siluetas y sellos monocromos'},outputFormats:'Formatos de salida',formatBoth:'SVG + EPS 10',formatSvg:'Solo SVG',formatEps:'EPS 10',tuningTitle:'Controles de ajuste vectorial',colorLimit:'Límite de paleta',truecolorLabel:'Color real completo (256+)',colorsUnit:n=>`${n} colores`,colorHints:{flat:'Ideal para iconos y logotipos minimalistas',vibrant:'Ideal para pegatinas, insignias y mascotas',detailed:'Arte multitono e ilustraciones detalladas',photo:'Recomendado para paisajes y fotografías'},speckleFilter:'Filtro de ruido',cornerThreshold:'Umbral de esquinas',curveSmoothness:'Suavidad de curvas',curveTolerance:'Tolerancia de ajuste',colorMerge:'Combinar colores similares',nativeTopology:'Topología nativa',validated:'Validado',stripWhiteBg:'Eliminar fondo blanco',stripWhiteDesc:'Limpia espacios negativos y halos',whiteSensitivity:'Sensibilidad al blanco',antiHalo:'Contracción de bordes (antihalo)'},
  canvas:{...en.canvas,dropTitle:'Arrastra y suelta aquí imágenes de IA',dropDesc:'Admite PNG, JPG, JPEG y WEBP de Midjourney, Recraft o Leonardo AI.',browseBtn:'Buscar archivos en el dispositivo',dualView:'Vista doble',vectorView:'Vector',outlineView:'Contorno',dualViewTitle:'Vista doble: píxeles originales y resultado vectorial',vectorViewTitle:'Arte vectorial a todo color',outlineViewTitle:'Inspección de contornos y curvas Bézier',revectorize:'Vectorizar de nuevo',revectorizeTitle:'Repite la vectorización con las opciones actuales',convertVector:'Convertir vector',convertAll:'Convertir todo',vectorizing:'Vectorizando...',computingCurves:'Calculando curvas Bézier y esquinas definidas',noVectorTitle:'Aún no se generó ningún vector',noVectorDesc:'Convierte esta imagen rasterizada a SVG y EPS 10',vectorReadyTitle:'Vector listo para generar',vectorReadyDesc:'Haz clic abajo para convertir los píxeles en curvas Bézier comerciales',tagRaster:'Píxeles rasterizados',tagOriginal:'Píxeles originales',tagVectorSvg:'SVG vectorial (Bézier puro)',tagVectorWireframe:'Malla de contorno (Bézier puro)',tagOutlinePure:'Malla vectorial',zoomOut:'Alejar (-)',resetZoom:'Restablecer zoom (100 %)',zoomIn:'Acercar (+) • máx. 800 %',wireframeInspect:'Inspeccionar contornos vectoriales puros',wireframeFills:'Cambiar a rellenos a todo color',checkerboardSolid:'Cambiar a fondo oscuro sólido',checkerboardTrans:'Cambiar a cuadrícula de transparencia',verifiedTitle:'Listo para microstock',verifiedSpecs:'4000 × 4000 px • Solo vector',verifiedBadge:'EPS 10'},
  rightSidebar:{...en.rightSidebar,queueTitle:n=>`Cola (${n})`,queueProgress:(c,t,f)=>`Vectorizando ${c}/${t}: ${f}`,queueConverted:n=>`${n} convertidos`,resetQueue:'Restablecer',convertAll:'Convertir todo',queueEmptyTitle:'La cola está vacía.',queueEmptyDesc:'Arrastra imágenes al lienzo o pulsa Buscar.',statusVectorizing:(c,t)=>`VECTORIZANDO ${c}/${t}`,metadataTitle:'Metadatos de microstock con IA',generateBtn:'Generar',regenerateBtn:'Generar de nuevo',categoryLabel:'Categoría:',keywordsCount:n=>`${n} palabras clave (copiar)`,keywordsMore:n=>`+${n} más`,noMetadataDesc:'Aún no se generaron metadatos. Pulsa «Generar» para crear un título comercial, 35 palabras clave SEO y una categoría.',copiedKeywords:'Palabras clave copiadas.',csvExporterTitle:'Exportador de metadatos microstock',csvBtn:'CSV',csvColumnsLabel:'Columnas:',batchExporterTitle:'Exportador vectorial por lotes',downloadZipBtn:'Descargar paquete ZIP',packagingZip:'Empaquetando ZIP...',batchZipDesc:'Agrupa todos los SVG, EPS 10 y archivos de vista previa convertidos en un solo ZIP.'},
  pricingModal:{...en.pricingModal,title:'Elige tu plan de Vectria',subtitle:'Selecciona el plan adecuado para tu volumen. 1 crédito = 1 conversión completa (SVG + EPS 10 + PNG).',rolloverNotice:'Sin cargos ocultos. Los créditos no usados se acumulan mientras la suscripción siga activa.',monthly:'Mensual',yearly:'Anual',savePercent:'Ahorra hasta un 20 %',perMonth:' / mes',perYear:' / año',currentPlan:'Plan actual',popularBadge:'Popular',bestValueBadge:'Mejor opción',choosePlan:(n,p)=>`Elegir ${n} (${p})`,bullets:{starter:['SVG puro y EPS 10 sin mapas de bits','Vista previa PNG/JPG en alta resolución','Inspector de vista doble y contornos','Procesamiento individual y múltiple'],pro:['Todas las funciones de Starter','Exportación ZIP por lotes (hasta 50 archivos)','Palabras clave con IA y CSV de Adobe','Validación técnica EPS 10 sin mapas de bits','Cola de procesamiento prioritaria'],studio:['Todas las funciones de Pro','Procesamiento ilimitado por lotes','Canal dedicado de alta velocidad','Licencia para equipos y soporte prioritario']},activeSubscription:(p,c)=>`Plan activo: ${p} • ${c.toLocaleString()} créditos disponibles`,cancelSubscription:'Cancelar suscripción',cancelConfirmTitle:'¿Cancelar la suscripción?',cancelConfirmDesc:c=>`Conservarás tus ${c.toLocaleString()} créditos restantes, pero la cola prioritaria y las herramientas ZIP terminarán al acabar el periodo actual.`,cancelKeepBtn:'Mantener suscripción',cancelProceedBtn:'Sí, cancelar',cancelledSuccess:'La suscripción se canceló. Tus créditos restantes siguen activos.',higherPlanAlert:'Tu plan actual es superior.',samePlanNotice:'Ya estás suscrito a este plan. Para añadir más créditos, utiliza la opción Recargar créditos.',includedBadge:'Incluido',includedInPlan:'Incluido en tu plan',topupPrompt:'¿Necesitas más créditos antes de la renovación?',topupBtn:'Recargar créditos'},
  passwordModal:{title:'Cambiar contraseña',subtitle:'Actualiza la contraseña de tu cuenta. Debe tener al menos 8 caracteres.',currentPasswordLabel:'Contraseña actual',newPasswordLabel:'Nueva contraseña',confirmPasswordLabel:'Confirmar nueva contraseña',placeholderCurrent:'Introduce tu contraseña actual',placeholderNew:'Mínimo 8 caracteres',placeholderConfirm:'Repite la nueva contraseña',submitBtn:'Actualizar contraseña',updating:'Actualizando...',successMsg:'Tu contraseña se ha cambiado correctamente.',mismatchError:'Las nuevas contraseñas no coinciden.',minLenError:'La contraseña debe tener al menos 8 caracteres.',sameError:'La nueva contraseña debe ser diferente de la actual.',cancelBtn:'Cancelar'},
  topupModal:{title:'Recargar créditos',subtitle:'Añade créditos vectoriales o de IA al instante sin alterar tu ciclo de facturación actual.',currentBalanceLabel:'Saldo actual de la cuenta:',vectorTab:'Créditos vectoriales',aiTab:'Créditos de IA',vectorDesc:'1 crédito vectorial = 1 conversión completa (SVG + EPS 10). No caduca mientras tu cuenta siga activa.',aiDesc:'Los créditos de IA se utilizan para generar metadatos con el Agente de IA. Solo se descuentan al completarse con éxito.',saveTag:pct=>`Ahorra ${pct}%`,perCredit:cost=>`(${cost} / crédito)`,buyBtn:(name,price)=>`Comprar ${name} (${price})`,buyPack:price=>`Obtener paquete (${price})`,popularBadge:'Más popular',bestValueBadge:'Mejor valor',successAlert:(amount,type)=>`¡Se han añadido +${amount.toLocaleString()} créditos de ${type === 'vector' ? 'vector' : 'IA'} a tu cuenta!`,connectingCheckout:'Conectando con la pasarela de pago...',closeBtn:'Cerrar',packs:{vec_200:'Paquete Mini',vec_500:'Paquete Crecimiento',vec_1500:'Paquete Pro Potencia',ai_500:'Paquete IA Mini',ai_1500:'Paquete IA Ultra',ai_4000:'Paquete IA Studio'}},
  aiModal:{...en.aiModal,title:'Configuración de metadatos de IA BYOK',subtitle:'Tus credenciales BYOK se cifran y almacenan de forma segura. Solo se usan para solicitudes de metadatos que autorices.',selectProvider:'Seleccionar proveedor de IA',apiKeyLabel:p=>`Claves API de ${p}`,apiKeysHint:'Una clave por línea. Vectria prueba la siguiente si una falla. Máximo 10 claves.',apiKeysCount:c=>`${c}/10 claves`,getKey:'Obtener clave',primaryModel:'Modelo principal de IA',customModelPlaceholder:'Introduce el identificador del modelo personalizado',fallbackModel:'Modelo alternativo (cambio automático si falla el principal)',fallbackNone:'Ninguno (fallar de inmediato)',fallbackDesc:'Si el modelo principal devuelve 429, 503 o falla, Vectria vuelve a intentarlo con este modelo.',customFallbackPlaceholder:'Introduce el modelo alternativo',customEndpointLabel:'URL de endpoint compatible con OpenAI',customEndpointDesc:'Usa OpenRouter u Ollama/vLLM local.',testConnection:'Probar conexión',testConnectionDesc:'Validar clave API y disponibilidad del modelo',verifying:'Verificando...',autoMetadataTitle:'Activar generación automática de metadatos',autoMetadataDesc:'Añade automáticamente título, 35 palabras clave y categoría.',vectriaFallbackTitle:'Alternativa de IA de Vectria',vectriaFallbackDesc:'La alternativa opcional usa el Agente de IA de Vectria solo tras un fallo temporal de BYOK. Cada recurso correcto cuesta 1 crédito de IA.',sourceByok:'BYOK • 0 créditos de IA',sourceManaged:'Agente de IA de Vectria',sourceFallback:'BYOK con alternativa de Vectria',estimatedCharge:c=>`Hasta ${c.toLocaleString()} créditos de IA`,byokNoCharge:'BYOK no consume créditos de IA.',insufficientAiCredits:'No hay suficientes créditos de IA para este lote.',resultSummary:(b,m,f,c)=>`${b} BYOK • ${m} gestionados • ${f} alternativos • ${c} créditos de IA cobrados`,cancel:'Cancelar',saveConfig:'Guardar configuración'}
};

const ptBr: StudioCopy = {
  ...en,
  header:{...en.header,planNames:{free:'Plano Gratuito',starter:'Plano Starter',pro:'Plano Pro',studio:'Plano Studio',agency:'Plano Agency'},credits:n=>`${n.toLocaleString('pt-BR')} créditos`,vectorCredits:n=>`${n.toLocaleString('pt-BR')} créditos vetoriais`,aiCredits:n=>`${n.toLocaleString('pt-BR')} créditos de IA`,unlimitedCredits:'Créditos ilimitados',upgrade:'Fazer upgrade',creditTooltip:'1 crédito vetorial = 1 conversão (SVG + EPS 10 + prévia). Clique para ver planos ou recarregar.',aiCreditTooltip:'1 crédito de IA só é cobrado após um ativo ser gerado com sucesso pelo Agente de IA da Vectria. BYOK usa 0 créditos de IA.',admin:'Administração',userMenu:{accountPlan:'Plano da conta:',remainingCredits:'Créditos restantes:',vectorCreditLabel:'Crédito vetorial:',aiCreditLabel:'Crédito de IA:',creditNote:'Créditos vetoriais são usados nas conversões. Créditos de IA só pagam metadados gerados com sucesso pelo Agente de IA da Vectria.',upgradePlan:'Fazer upgrade do plano',managePlan:'Gerenciar assinatura',workspace:'Espaço de trabalho',byokKeys:'Chaves de IA BYOK',themeLight:'Tema claro',themeDark:'Tema escuro',signOut:'Sair',adminConsole:'Painel administrativo do proprietário',changePassword:'Alterar senha',topupCredits:'Recarregar créditos'},collapseQueue:'Recolher painel da fila',expandQueue:'Expandir painel da fila',toggleTheme:'Alternar tema'},
  leftSidebar:{...en.leftSidebar,autoOptimize:'Modo de otimização automática',analyzing:'Analisando imagem…',autoNoticeEmpty:'Envie uma imagem primeiro para ativar a detecção automática',autoTitleEmpty:'Envie uma imagem primeiro para ativar a análise automática',autoTitleActive:'Analisa contornos e densidade de linhas e combina cores semelhantes em preenchimentos vetoriais limpos',perImagePrefix:'Por imagem:',quickPresets:'Predefinições rápidas',guideBtn:'Guia',guideTitle:'Guia de predefinições',guideItems:[{name:'Plano (6–8 cores)',desc:'Ícones, logotipos minimalistas, pictogramas e símbolos de interface.'},{name:'Vibrante (12–16 cores)',desc:'Adesivos, emblemas, mascotes e ilustrações com sombreamento simples.'},{name:'Detalhado (24–32 cores)',desc:'Ilustrações em várias camadas e ativos de jogos detalhados.'},{name:'Cores reais (64+ cores)',desc:'Paisagens, pinturas digitais e temas fotográficos.'},{name:'Monolinear (2 cores)',desc:'Desenhos lineares, silhuetas, carimbos e arte em preto e branco.'}],presetChips:{'flat-6':'Ícone plano','vibrant-12':'Arte vibrante','detailed-32':'Detalhado 32','ultra-64':'Ultra HD 64','truecolor':'Cores reais','monoline':'Monolinear P&B'},presetTooltips:{'flat-6':'Ícone plano: 8 cores, pouco filtro de ruído e cantos protegidos','vibrant-12':'Arte vibrante: ajustes equilibrados para adesivos, emblemas e mascotes','detailed-32':'Detalhado 32: ilustrações em camadas e personagens sombreados','ultra-64':'Ultra HD 64: arte rica em cores e paisagens','truecolor':'Cores reais: fotos e pinturas complexas','monoline':'Monolinear P&B: arte linear, silhuetas e carimbos monocromáticos'},outputFormats:'Formatos de saída',formatBoth:'SVG + EPS 10',formatSvg:'Somente SVG',formatEps:'EPS 10',tuningTitle:'Controles de ajuste vetorial',colorLimit:'Limite da paleta de cores',truecolorLabel:'Cores reais completas (256+)',colorsUnit:n=>`${n} cores`,colorHints:{flat:'Ideal para ícones e logotipos minimalistas',vibrant:'Melhor para adesivos, emblemas e mascotes',detailed:'Arte com vários tons e ilustrações detalhadas',photo:'Recomendado para paisagens e temas fotográficos'},speckleFilter:'Filtro de ruído',cornerThreshold:'Limite de cantos',curveSmoothness:'Suavidade das curvas',curveTolerance:'Tolerância de ajuste das curvas',colorMerge:'Combinar cores semelhantes',nativeTopology:'Topologia nativa',validated:'Validado',stripWhiteBg:'Remover fundo branco',stripWhiteDesc:'Limpa espaços negativos e halos',whiteSensitivity:'Sensibilidade ao branco',antiHalo:'Recuo de borda (anti-halo)'},
  canvas:{...en.canvas,dropTitle:'Arraste e solte imagens de IA aqui',dropDesc:'Aceita PNG, JPG, JPEG e WEBP do Midjourney, Recraft ou Leonardo AI.',browseBtn:'Procurar arquivos no dispositivo',dualView:'Vista dupla',vectorView:'Vetor',outlineView:'Contorno',dualViewTitle:'Vista dupla: pixels originais e resultado vetorial',vectorViewTitle:'Arte vetorial em cores',outlineViewTitle:'Inspeção de contornos e curvas Bézier',revectorize:'Vetorizar novamente',revectorizeTitle:'Executar a vetorização novamente com as opções atuais',convertVector:'Converter vetor',convertAll:'Converter tudo',vectorizing:'Vetorizando...',computingCurves:'Calculando curvas Bézier e cantos definidos',noVectorTitle:'Nenhum vetor gerado ainda',noVectorDesc:'Converta esta arte rasterizada em SVG e EPS 10',vectorReadyTitle:'Vetor pronto para gerar',vectorReadyDesc:'Clique abaixo para transformar pixels em curvas Bézier comerciais',tagRaster:'Pixels rasterizados',tagOriginal:'Pixels originais',tagVectorSvg:'SVG vetorial (Bézier puro)',tagVectorWireframe:'Malha de contorno (Bézier puro)',tagOutlinePure:'Malha vetorial',zoomOut:'Diminuir zoom (-)',resetZoom:'Redefinir zoom (100%)',zoomIn:'Aumentar zoom (+) — máx. 800%',wireframeInspect:'Inspecionar contornos vetoriais puros',wireframeFills:'Alternar para preenchimentos em cores',checkerboardSolid:'Alternar para fundo escuro sólido',checkerboardTrans:'Alternar para grade de transparência',verifiedTitle:'Pronto para microstock',verifiedSpecs:'4000 × 4000 px • Somente vetor',verifiedBadge:'EPS 10'},
  rightSidebar:{...en.rightSidebar,queueTitle:n=>`Fila (${n})`,queueProgress:(c,t,f)=>`Vetorizando ${c}/${t}: ${f}`,queueConverted:n=>`${n} convertidos`,resetQueue:'Redefinir',convertAll:'Converter tudo',queueEmptyTitle:'A fila está vazia.',queueEmptyDesc:'Arraste imagens para a tela ou clique em Procurar.',statusVectorizing:(c,t)=>`VETORIZANDO ${c}/${t}`,metadataTitle:'Metadados de microstock com IA',generateBtn:'Gerar',regenerateBtn:'Gerar novamente',categoryLabel:'Categoria:',keywordsCount:n=>`${n} palavras-chave (copiar)`,keywordsMore:n=>`+${n} outras`,noMetadataDesc:'Nenhum metadado de IA foi gerado. Clique em “Gerar” para criar um título comercial, 35 palavras-chave SEO e uma categoria.',copiedKeywords:'Palavras-chave copiadas.',csvExporterTitle:'Exportador de metadados para microstock',csvBtn:'CSV',csvColumnsLabel:'Colunas:',batchExporterTitle:'Exportador vetorial em lote',downloadZipBtn:'Baixar pacote ZIP em lote',packagingZip:'Compactando ZIP...',batchZipDesc:'Reúne todos os arquivos SVG, EPS 10 e prévias convertidos em um único ZIP.'},
  pricingModal:{...en.pricingModal,title:'Escolha seu plano Vectria',subtitle:'Escolha o plano ideal para seu volume. 1 crédito = 1 conversão completa (SVG + EPS 10 + prévia PNG).',rolloverNotice:'Sem taxas ocultas. Créditos não usados são acumulados enquanto a assinatura estiver ativa.',monthly:'Mensal',yearly:'Anual',savePercent:'Economize até 20%',perMonth:' / mês',perYear:' / ano',currentPlan:'Plano atual',popularBadge:'Popular',bestValueBadge:'Melhor custo-benefício',choosePlan:(n,p)=>`Escolher ${n} (${p})`,bullets:{starter:['SVG puro e EPS 10 sem bitmaps','Prévia PNG/JPG em alta resolução','Inspector com vista dupla e contornos','Processamento individual e de vários arquivos'],pro:['Todos os recursos do Starter','Exportação ZIP em lote (até 50 arquivos)','Palavras-chave com IA e CSV da Adobe','Validação técnica EPS 10 sem bitmaps','Fila de processamento prioritária'],studio:['Todos os recursos do Pro','Processamento ilimitado da fila em lote','Pipeline dedicado de alta velocidade','Licença para equipes e suporte prioritário']},activeSubscription:(p,c)=>`Plano ativo: ${p} • ${c.toLocaleString('pt-BR')} créditos disponíveis`,cancelSubscription:'Cancelar assinatura',cancelConfirmTitle:'Cancelar assinatura?',cancelConfirmDesc:c=>`Você manterá os ${c.toLocaleString('pt-BR')} créditos restantes, mas a fila prioritária e as ferramentas ZIP serão encerradas ao fim do período atual.`,cancelKeepBtn:'Manter assinatura',cancelProceedBtn:'Sim, cancelar',cancelledSuccess:'Sua assinatura foi cancelada. Seus créditos restantes continuam ativos.',higherPlanAlert:'Seu plano atual é superior.',samePlanNotice:'Você já assina este plano. Para adicionar mais créditos, use a opção Recarregar créditos.',includedBadge:'Incluído',includedInPlan:'Incluído no seu plano',topupPrompt:'Precisa de mais créditos antes da renovação?',topupBtn:'Recarregar créditos'},
  passwordModal:{title:'Alterar senha',subtitle:'Atualize a senha da sua conta. Mínimo de 8 caracteres.',currentPasswordLabel:'Senha atual',newPasswordLabel:'Nova senha',confirmPasswordLabel:'Confirmar nova senha',placeholderCurrent:'Digite sua senha atual',placeholderNew:'Mínimo de 8 caracteres',placeholderConfirm:'Repita a nova senha',submitBtn:'Atualizar senha',updating:'Atualizando...',successMsg:'Sua senha foi alterada com sucesso.',mismatchError:'As novas senhas não coincidem.',minLenError:'A senha deve conter no mínimo 8 caracteres.',sameError:'A nova senha deve ser diferente da senha atual.',cancelBtn:'Cancelar'},
  topupModal:{title:'Recarregar créditos',subtitle:'Adicione créditos vetoriais ou de IA instantaneamente sem alterar a data de renovação da assinatura.',currentBalanceLabel:'Saldo atual da conta:',vectorTab:'Créditos vetoriais',aiTab:'Créditos de IA',vectorDesc:'1 crédito vetorial = 1 conversão completa (SVG + EPS 10). Não expira enquanto sua conta estiver ativa.',aiDesc:'Créditos de IA pagam a geração de metadados com o Agente de IA. Cobrados apenas em operações bem-sucedidas.',saveTag:pct=>`Economize ${pct}%`,perCredit:cost=>`(${cost} / crédito)`,buyBtn:(name,price)=>`Comprar ${name} (${price})`,buyPack:price=>`Comprar pacote (${price})`,popularBadge:'Mais popular',bestValueBadge:'Melhor custo-benefício',successAlert:(amount,type)=>`+${amount.toLocaleString('pt-BR')} créditos de ${type === 'vector' ? 'vetor' : 'IA'} adicionados com sucesso!`,connectingCheckout:'Conectando ao checkout seguro...',closeBtn:'Fechar',packs:{vec_200:'Pacote Mini',vec_500:'Pacote Crescimento',vec_1500:'Pacote Potência',ai_500:'Pacote IA Mini',ai_1500:'Pacote IA Ultra',ai_4000:'Pacote IA Studio'}},
  aiModal:{...en.aiModal,title:'Configurações de metadados de IA BYOK',subtitle:'Suas credenciais BYOK são criptografadas e armazenadas com segurança. Elas só são usadas em solicitações de metadados autorizadas por você.',selectProvider:'Selecionar provedor de IA',apiKeyLabel:p=>`Chaves de API da ${p}`,apiKeysHint:'Uma chave por linha. A Vectria tenta a próxima quando uma falha. Máximo de 10 chaves.',apiKeysCount:c=>`${c}/10 chaves`,getKey:'Obter chave',primaryModel:'Modelo principal de IA',customModelPlaceholder:'Informe o identificador do modelo personalizado',fallbackModel:'Modelo alternativo (troca automática se o principal falhar)',fallbackNone:'Nenhum (falhar imediatamente)',fallbackDesc:'Se o modelo principal retornar 429, 503 ou falhar, a Vectria tentará novamente com este modelo.',customFallbackPlaceholder:'Informe o modelo alternativo personalizado',customEndpointLabel:'URL de endpoint compatível com OpenAI',customEndpointDesc:'Use OpenRouter ou Ollama/vLLM local.',testConnection:'Testar conexão',testConnectionDesc:'Validar chave de API e disponibilidade do modelo',verifying:'Verificando...',autoMetadataTitle:'Ativar geração automática de metadados',autoMetadataDesc:'Adiciona automaticamente título, 35 palavras-chave e categoria aos vetores convertidos.',vectriaFallbackTitle:'Contingência de IA da Vectria',vectriaFallbackDesc:'A contingência opcional usa o Agente de IA da Vectria somente após uma falha temporária do BYOK. Cada ativo gerado com sucesso custa 1 crédito de IA.',sourceByok:'BYOK • 0 créditos de IA',sourceManaged:'Agente de IA da Vectria',sourceFallback:'BYOK com contingência da Vectria',estimatedCharge:c=>`Até ${c.toLocaleString('pt-BR')} créditos de IA`,byokNoCharge:'A geração com BYOK não usa créditos de IA.',insufficientAiCredits:'Créditos de IA insuficientes para este lote.',resultSummary:(b,m,f,c)=>`${b} BYOK • ${m} gerenciados • ${f} contingências • ${c} créditos de IA cobrados`,cancel:'Cancelar',saveConfig:'Salvar configuração'}
};

const ru: StudioCopy = {
  ...en,
  header:{...en.header,planNames:{free:'Бесплатный тариф',starter:'Тариф Starter',pro:'Тариф Pro',studio:'Тариф Studio',agency:'Тариф Agency'},credits:n=>`${n.toLocaleString('ru-RU')} кредитов`,vectorCredits:n=>`${n.toLocaleString('ru-RU')} векторных кредитов`,aiCredits:n=>`${n.toLocaleString('ru-RU')} кредитов ИИ`,unlimitedCredits:'Неограниченные кредиты',upgrade:'Улучшить тариф',creditTooltip:'1 векторный кредит = 1 конвертация (SVG + EPS 10 + превью). Нажмите, чтобы посмотреть тарифы или пополнить баланс.',aiCreditTooltip:'1 кредит ИИ списывается только после успешной обработки материала ИИ-агентом Vectria. BYOK использует 0 кредитов ИИ.',admin:'Администрирование',userMenu:{accountPlan:'Тариф аккаунта:',remainingCredits:'Осталось кредитов:',vectorCreditLabel:'Векторный кредит:',aiCreditLabel:'Кредит ИИ:',creditNote:'Векторные кредиты используются для конвертации. Кредиты ИИ списываются только за успешно созданные метаданные ИИ-агентом Vectria.',upgradePlan:'Улучшить тариф',managePlan:'Управление подпиской',workspace:'Рабочее пространство',byokKeys:'Ключи ИИ BYOK',themeLight:'Светлая тема',themeDark:'Тёмная тема',signOut:'Выйти',adminConsole:'Панель владельца',changePassword:'Сменить пароль',topupCredits:'Пополнить кредиты'},collapseQueue:'Свернуть панель очереди',expandQueue:'Развернуть панель очереди',toggleTheme:'Переключить тему'},
  leftSidebar:{...en.leftSidebar,autoOptimize:'Автоматическая оптимизация',analyzing:'Анализ изображения…',autoNoticeEmpty:'Сначала загрузите изображение для автоматического определения',autoTitleEmpty:'Сначала загрузите изображение для автоматического анализа',autoTitleActive:'Анализирует контуры и плотность линий, объединяет похожие цвета в чистые векторные заливки',perImagePrefix:'Для изображения:',quickPresets:'Быстрые настройки',guideBtn:'Справка',guideTitle:'Справочник предустановок',guideItems:[{name:'Плоский стиль (6–8 цветов)',desc:'Значки, минималистичные логотипы, пиктограммы и символы интерфейса.'},{name:'Яркий стиль (12–16 цветов)',desc:'Стикеры, эмблемы, талисманы и мультяшные иллюстрации.'},{name:'Детальный стиль (24–32 цвета)',desc:'Многослойные иллюстрации и детализированные игровые материалы.'},{name:'Полноцветный стиль (64+ цвета)',desc:'Пейзажи, цифровая живопись и фотографические сюжеты.'},{name:'Монолиния (2 цвета)',desc:'Линейные рисунки, силуэты, штампы и контрастная чёрно-белая графика.'}],presetChips:{'flat-6':'Плоский значок','vibrant-12':'Яркая графика','detailed-32':'Детально 32','ultra-64':'Ultra HD 64','truecolor':'Полный цвет','monoline':'Монолиния Ч/Б'},presetTooltips:{'flat-6':'Плоский значок: 8 цветов, слабая фильтрация шума и защищённые углы','vibrant-12':'Яркая графика: сбалансированные настройки для стикеров, эмблем и талисманов','detailed-32':'Детально 32: многослойные иллюстрации и персонажи с тенями','ultra-64':'Ultra HD 64: насыщенная графика и пейзажи','truecolor':'Полный цвет: реалистичные фото и сложная живопись','monoline':'Монолиния Ч/Б: линейная графика, силуэты и монохромные штампы'},outputFormats:'Форматы вывода',formatBoth:'SVG + EPS 10',formatSvg:'Только SVG',formatEps:'EPS 10',tuningTitle:'Настройка векторизации',colorLimit:'Лимит палитры',truecolorLabel:'Полный цвет (256+)',colorsUnit:n=>`${n} цветов`,colorHints:{flat:'Оптимально для значков и минималистичных логотипов',vibrant:'Лучше всего для стикеров, эмблем и талисманов',detailed:'Многотонная графика и детальные иллюстрации',photo:'Рекомендуется для пейзажей и фотографических сюжетов'},speckleFilter:'Фильтр мелкого шума',cornerThreshold:'Порог углов',curveSmoothness:'Сглаживание кривых',curveTolerance:'Допуск аппроксимации кривых',colorMerge:'Объединение похожих цветов',nativeTopology:'Нативная топология',validated:'Проверено',stripWhiteBg:'Удалить белый фон',stripWhiteDesc:'Очищает пустые области и ореолы',whiteSensitivity:'Чувствительность к белому',antiHalo:'Отступ края (антиореол)'},
  canvas:{...en.canvas,dropTitle:'Перетащите сюда изображения ИИ',dropDesc:'Поддерживаются PNG, JPG, JPEG и WEBP из Midjourney, Recraft или Leonardo AI.',browseBtn:'Выбрать файлы на устройстве',dualView:'Двойной вид',vectorView:'Вектор',outlineView:'Контуры',dualViewTitle:'Двойной вид: исходные пиксели и результат векторизации',vectorViewTitle:'Полноцветная векторная графика',outlineViewTitle:'Проверка контуров и кривых Безье',revectorize:'Векторизовать заново',revectorizeTitle:'Повторить векторизацию с текущими настройками',convertVector:'Преобразовать в вектор',convertAll:'Преобразовать всё',vectorizing:'Векторизация...',computingCurves:'Расчёт кривых Безье и чётких углов',noVectorTitle:'Вектор ещё не создан',noVectorDesc:'Преобразуйте это растровое изображение в SVG и EPS 10',vectorReadyTitle:'Вектор готов к созданию',vectorReadyDesc:'Нажмите ниже, чтобы преобразовать пиксели в коммерческие кривые Безье',tagRaster:'Растровые пиксели',tagOriginal:'Исходные пиксели',tagVectorSvg:'Вектор SVG (чистые кривые Безье)',tagVectorWireframe:'Каркас контуров (чистые кривые Безье)',tagOutlinePure:'Векторный каркас',zoomOut:'Уменьшить (-)',resetZoom:'Сбросить масштаб (100%)',zoomIn:'Увеличить (+) — макс. 800%',wireframeInspect:'Проверить чистые векторные контуры',wireframeFills:'Переключить на полноцветные заливки',checkerboardSolid:'Переключить на тёмный сплошной фон',checkerboardTrans:'Переключить на сетку прозрачности',verifiedTitle:'Готово для микростоков',verifiedSpecs:'4000 × 4000 пикселей • Только вектор',verifiedBadge:'EPS 10'},
  rightSidebar:{...en.rightSidebar,queueTitle:n=>`Очередь (${n})`,queueProgress:(c,t,f)=>`Векторизация ${c}/${t}: ${f}`,queueConverted:n=>`Преобразовано: ${n}`,resetQueue:'Сбросить',convertAll:'Преобразовать всё',queueEmptyTitle:'Очередь пуста.',queueEmptyDesc:'Перетащите изображения на холст или выберите файлы.',statusVectorizing:(c,t)=>`ВЕКТОРИЗАЦИЯ ${c}/${t}`,metadataTitle:'Метаданные для микростоков с ИИ',generateBtn:'Создать',regenerateBtn:'Создать заново',categoryLabel:'Категория:',keywordsCount:n=>`Ключевые слова: ${n} (копировать)`,keywordsMore:n=>`ещё ${n}`,noMetadataDesc:'Метаданные ИИ ещё не созданы. Нажмите «Создать», чтобы получить коммерческое название, 35 ключевых слов SEO и категорию.',copiedKeywords:'Ключевые слова скопированы.',csvExporterTitle:'Экспорт метаданных для микростоков',csvBtn:'CSV',csvColumnsLabel:'Столбцы:',batchExporterTitle:'Пакетный экспорт векторов',downloadZipBtn:'Скачать пакетный ZIP',packagingZip:'Создание ZIP...',batchZipDesc:'Объединяет все преобразованные SVG, EPS 10 и превью в один ZIP-архив.'},
  pricingModal:{...en.pricingModal,title:'Выберите тариф Vectria',subtitle:'Выберите тариф для своего объёма производства. 1 кредит = 1 полная конвертация (SVG + EPS 10 + PNG-превью).',rolloverNotice:'Без скрытых платежей. Неиспользованные кредиты переносятся, пока подписка активна.',monthly:'Ежемесячно',yearly:'Ежегодно',savePercent:'Экономия до 20%',perMonth:' / месяц',perYear:' / год',currentPlan:'Текущий тариф',popularBadge:'Популярный',bestValueBadge:'Выгодный выбор',choosePlan:(n,p)=>`Выбрать ${n} (${p})`,bullets:{starter:['Чистый SVG и EPS 10 без растровых вставок','Превью PNG/JPG высокого разрешения','Инспектор с двойным видом и контурами','Обработка одного или нескольких файлов'],pro:['Все возможности Starter','Пакетный экспорт ZIP до 50 файлов','Ключевые слова с ИИ и CSV Adobe','Техническая проверка EPS 10 без растровых вставок','Приоритетная очередь обработки'],studio:['Все возможности Pro','Неограниченная пакетная очередь','Выделенный высокоскоростной конвейер','Командная лицензия и приоритетная поддержка']},activeSubscription:(p,c)=>`Активный тариф: ${p} • Доступно ${c.toLocaleString('ru-RU')} кредитов`,cancelSubscription:'Отменить подписку',cancelConfirmTitle:'Отменить подписку?',cancelConfirmDesc:c=>`Оставшиеся ${c.toLocaleString('ru-RU')} кредитов сохранятся, но приоритетная очередь и инструменты ZIP станут недоступны после окончания текущего периода.`,cancelKeepBtn:'Сохранить подписку',cancelProceedBtn:'Да, отменить',cancelledSuccess:'Подписка отменена. Оставшиеся кредиты продолжают действовать.',higherPlanAlert:'Ваш текущий тариф выше.',samePlanNotice:'Вы уже подписаны на этот тариф. Чтобы добавить кредиты, воспользуйтесь пополнением баланса.',includedBadge:'Включено',includedInPlan:'Включено в ваш тариф',topupPrompt:'Нужно больше кредитов до продления?',topupBtn:'Пополнить кредиты'},
  passwordModal:{title:'Смена пароля',subtitle:'Обновите пароль учётной записи. Минимальная длина — 8 символов.',currentPasswordLabel:'Текущий пароль',newPasswordLabel:'Новый пароль',confirmPasswordLabel:'Подтвердите новый пароль',placeholderCurrent:'Введите текущий пароль',placeholderNew:'Не менее 8 символов',placeholderConfirm:'Повторите новый пароль',submitBtn:'Сохранить пароль',updating:'Обновление...',successMsg:'Пароль успешно изменён.',mismatchError:'Новые пароли не совпадают.',minLenError:'Пароль должен содержать не менее 8 символов.',sameError:'Новый пароль должен отличаться от текущего.',cancelBtn:'Отмена'},
  topupModal:{title:'Пополнение кредитов',subtitle:'Мгновенно докупайте векторные кредиты или кредиты ИИ без изменения даты расчётного периода подписки.',currentBalanceLabel:'Текущий баланс аккаунта:',vectorTab:'Векторные кредиты',aiTab:'Кредиты ИИ',vectorDesc:'1 векторный кредит = 1 полная конвертация (SVG + EPS 10). Не сгорает, пока аккаунт активен.',aiDesc:'Кредиты ИИ оплачивают генерацию метаданных ИИ-агентом Vectria. Списываются только при успешном результате.',saveTag:pct=>`Скидка ${pct}%`,perCredit:cost=>`(${cost} / кредит)`,buyBtn:(name,price)=>`Купить ${name} (${price})`,buyPack:price=>`Купить пакет (${price})`,popularBadge:'Популярный',bestValueBadge:'Выгодный выбор',successAlert:(amount,type)=>`Успешно начислено +${amount.toLocaleString('ru-RU')} ${type === 'vector' ? 'векторных' : 'ИИ'} кредитов!`,connectingCheckout:'Переход к безопасной оплате...',closeBtn:'Закрыть',packs:{vec_200:'Мини-пакет',vec_500:'Пакет «Рост»',vec_1500:'Пакет «Мощность»',ai_500:'ИИ-пакет Mini',ai_1500:'ИИ-пакет Ultra',ai_4000:'ИИ-пакет Studio'}},
  aiModal:{...en.aiModal,title:'Настройки метаданных ИИ BYOK',subtitle:'Ваши учётные данные BYOK шифруются и безопасно хранятся. Они используются только для разрешённых вами запросов метаданных.',selectProvider:'Выберите поставщика ИИ',apiKeyLabel:p=>`Ключи API ${p}`,apiKeysHint:'Один ключ в строке. При ошибке Vectria пробует следующий. Не более 10 ключей.',apiKeysCount:c=>`${c}/10 ключей`,getKey:'Получить ключ',primaryModel:'Основная модель ИИ',customModelPlaceholder:'Введите идентификатор пользовательской модели',fallbackModel:'Резервная модель (автопереключение при сбое основной)',fallbackNone:'Нет (сразу завершить с ошибкой)',fallbackDesc:'Если основная модель возвращает 429, 503 или другую ошибку, Vectria повторяет запрос с этой моделью.',customFallbackPlaceholder:'Введите резервную пользовательскую модель',customEndpointLabel:'URL endpoint, совместимого с OpenAI',customEndpointDesc:'Используйте OpenRouter или локальный Ollama/vLLM.',testConnection:'Проверить соединение',testConnectionDesc:'Проверить ключ API и доступность модели',verifying:'Проверка...',autoMetadataTitle:'Включить автоматическое создание метаданных',autoMetadataDesc:'Автоматически добавляет название, 35 ключевых слов и категорию к преобразованным векторам.',vectriaFallbackTitle:'Резервный ИИ Vectria',vectriaFallbackDesc:'Дополнительный резерв использует ИИ-агент Vectria только после временного сбоя BYOK. Каждый успешный материал стоит 1 кредит ИИ.',sourceByok:'BYOK • 0 кредитов ИИ',sourceManaged:'ИИ-агент Vectria',sourceFallback:'BYOK с резервом Vectria',estimatedCharge:c=>`До ${c.toLocaleString('ru-RU')} кредитов ИИ`,byokNoCharge:'Генерация BYOK не использует кредиты ИИ.',insufficientAiCredits:'Недостаточно кредитов ИИ для этого пакета.',resultSummary:(b,m,f,c)=>`${b} BYOK • ${m} управляемых • ${f} резервных • списано ${c} кредитов ИИ`,cancel:'Отмена',saveConfig:'Сохранить настройки'}
};

const tr: StudioCopy = {
  ...en,
  header:{...en.header,planNames:{free:'Ücretsiz Plan',starter:'Starter Planı',pro:'Pro Planı',studio:'Studio Planı',agency:'Agency Planı'},credits:n=>`${n.toLocaleString('tr-TR')} kredi`,vectorCredits:n=>`${n.toLocaleString('tr-TR')} vektör kredisi`,aiCredits:n=>`${n.toLocaleString('tr-TR')} AI kredisi`,unlimitedCredits:'Sınırsız kredi',upgrade:'Planı Yükselt',creditTooltip:'1 vektör kredisi = 1 dönüştürme (SVG + EPS 10 + önizleme). Planları görmek veya kredi yüklemek için tıklayın.',aiCreditTooltip:'Yalnızca Vectria AI Aracısı bir varlığı başarıyla işlediğinde 1 AI kredisi alınır. BYOK 0 AI kredisi kullanır.',admin:'Yönetim',userMenu:{accountPlan:'Hesap planı:',remainingCredits:'Kalan krediler:',vectorCreditLabel:'Vektör kredisi:',aiCreditLabel:'AI kredisi:',creditNote:'Vektör kredileri dönüştürmelerde kullanılır. AI kredileri yalnızca Vectria AI Aracısının başarıyla oluşturduğu meta veriler için alınır.',upgradePlan:'Planı yükselt',managePlan:'Aboneliği yönet',workspace:'Çalışma alanı',byokKeys:'BYOK AI anahtarları',themeLight:'Açık tema',themeDark:'Koyu tema',signOut:'Çıkış yap',adminConsole:'Sahip yönetim paneli',changePassword:'Şifreyi Değiştir',topupCredits:'Kredi Yükle'},collapseQueue:'Kuyruk panelini daralt',expandQueue:'Kuyruk panelini genişlet',toggleTheme:'Temayı değiştir'},
  leftSidebar:{...en.leftSidebar,autoOptimize:'Otomatik optimizasyon modu',analyzing:'Görsel analiz ediliyor…',autoNoticeEmpty:'Otomatik algılamayı etkinleştirmek için önce bir görsel yükleyin',autoTitleEmpty:'Otomatik analizi etkinleştirmek için önce bir görsel yükleyin',autoTitleActive:'Kenarları ve çizgi yoğunluğunu analiz eder, benzer renkleri temiz vektör dolgularında birleştirir',perImagePrefix:'Görsel başına:',quickPresets:'Hızlı ön ayarlar',guideBtn:'Kılavuz',guideTitle:'Ön ayar kılavuzu',guideItems:[{name:'Düz (6–8 renk)',desc:'Simgeler, minimalist logolar, piktogramlar ve arayüz sembolleri.'},{name:'Canlı (12–16 renk)',desc:'Çıkartmalar, rozetler, maskotlar ve basit gölgeli illüstrasyonlar.'},{name:'Ayrıntılı (24–32 renk)',desc:'Çok katmanlı illüstrasyonlar ve ayrıntılı oyun varlıkları.'},{name:'Gerçek renk (64+ renk)',desc:'Manzaralar, dijital resimler ve fotoğrafik konular.'},{name:'Tek çizgi (2 renk)',desc:'Çizgi sanatı, siluetler, damgalar ve siyah-beyaz grafikler.'}],presetChips:{'flat-6':'Düz simge','vibrant-12':'Canlı sanat','detailed-32':'Ayrıntılı 32','ultra-64':'Ultra HD 64','truecolor':'Gerçek renk','monoline':'Tek çizgi S/B'},presetTooltips:{'flat-6':'Düz simge: 8 renk, düşük parazit filtresi ve korunan köşeler','vibrant-12':'Canlı sanat: çıkartma, rozet ve maskotlar için dengeli ayarlar','detailed-32':'Ayrıntılı 32: katmanlı illüstrasyonlar ve gölgeli karakterler','ultra-64':'Ultra HD 64: renk açısından zengin sanat ve manzaralar','truecolor':'Gerçek renk: gerçekçi fotoğraflar ve karmaşık resimler','monoline':'Tek çizgi S/B: çizgi sanatı, siluetler ve tek renkli damgalar'},outputFormats:'Çıktı biçimleri',formatBoth:'SVG + EPS 10',formatSvg:'Yalnızca SVG',formatEps:'EPS 10',tuningTitle:'Vektör ayarlama kontrolleri',colorLimit:'Renk paleti sınırı',truecolorLabel:'Tam gerçek renk (256+)',colorsUnit:n=>`${n} renk`,colorHints:{flat:'Simgeler ve minimalist logolar için ideal',vibrant:'Çıkartmalar, rozetler ve maskotlar için en iyi',detailed:'Çok tonlu sanat ve ayrıntılı illüstrasyonlar',photo:'Manzaralar ve fotoğrafik konular için önerilir'},speckleFilter:'Benek filtresi',cornerThreshold:'Köşe eşiği',curveSmoothness:'Eğri yumuşaklığı',curveTolerance:'Eğri uydurma toleransı',colorMerge:'Benzer renkleri birleştir',nativeTopology:'Yerel topoloji',validated:'Doğrulandı',stripWhiteBg:'Beyaz arka planı kaldır',stripWhiteDesc:'Negatif alanları ve haleleri temizler',whiteSensitivity:'Beyaz hassasiyeti',antiHalo:'Kenar içe çekme (hale önleme)'},
  canvas:{...en.canvas,dropTitle:'AI görsellerini buraya sürükleyip bırakın',dropDesc:'Midjourney, Recraft veya Leonardo AI kaynaklı PNG, JPG, JPEG ve WEBP dosyalarını kabul eder.',browseBtn:'Cihazdaki dosyalara göz at',dualView:'Çift görünüm',vectorView:'Vektör',outlineView:'Kontur',dualViewTitle:'Çift görünüm: özgün pikseller ve vektör sonucu',vectorViewTitle:'Tam renkli vektör çalışması',outlineViewTitle:'Kontur ve Bézier eğrisi incelemesi',revectorize:'Yeniden vektörleştir',revectorizeTitle:'Geçerli seçeneklerle vektörleştirmeyi yeniden çalıştır',convertVector:'Vektöre dönüştür',convertAll:'Tümünü dönüştür',vectorizing:'Vektörleştiriliyor...',computingCurves:'Bézier eğrileri ve keskin köşeler hesaplanıyor',noVectorTitle:'Henüz vektör oluşturulmadı',noVectorDesc:'Bu raster çalışmayı SVG ve EPS 10 biçimine dönüştürün',vectorReadyTitle:'Vektör oluşturulmaya hazır',vectorReadyDesc:'Pikselleri ticari Bézier eğrilerine dönüştürmek için aşağıya tıklayın',tagRaster:'Raster pikseller',tagOriginal:'Özgün pikseller',tagVectorSvg:'Vektör SVG (saf Bézier)',tagVectorWireframe:'Kontur tel kafesi (saf Bézier)',tagOutlinePure:'Vektör tel kafesi',zoomOut:'Uzaklaştır (-)',resetZoom:'Yakınlaştırmayı sıfırla (%100)',zoomIn:'Yakınlaştır (+) — en fazla %800',wireframeInspect:'Saf vektör konturlarını incele',wireframeFills:'Tam renkli dolgulara geç',checkerboardSolid:'Düz koyu arka plana geç',checkerboardTrans:'Şeffaflık ızgarasına geç',verifiedTitle:'Mikro stok için hazır',verifiedSpecs:'4000 × 4000 piksel • Yalnızca vektör',verifiedBadge:'EPS 10'},
  rightSidebar:{...en.rightSidebar,queueTitle:n=>`Kuyruk (${n})`,queueProgress:(c,t,f)=>`Vektörleştiriliyor ${c}/${t}: ${f}`,queueConverted:n=>`${n} dönüştürüldü`,resetQueue:'Sıfırla',convertAll:'Tümünü dönüştür',queueEmptyTitle:'Kuyruk boş.',queueEmptyDesc:'Görselleri tuvale sürükleyin veya Göz At’a tıklayın.',statusVectorizing:(c,t)=>`VEKTÖRLEŞTİRİLİYOR ${c}/${t}`,metadataTitle:'AI destekli mikro stok meta verileri',generateBtn:'Oluştur',regenerateBtn:'Yeniden oluştur',categoryLabel:'Kategori:',keywordsCount:n=>`${n} anahtar kelime (kopyala)`,keywordsMore:n=>`+${n} daha`,noMetadataDesc:'Henüz AI meta verisi oluşturulmadı. Ticari başlık, 35 SEO anahtar kelimesi ve kategori oluşturmak için “Oluştur”a tıklayın.',copiedKeywords:'Anahtar kelimeler kopyalandı.',csvExporterTitle:'Mikro stok meta veri dışa aktarıcısı',csvBtn:'CSV',csvColumnsLabel:'Sütunlar:',batchExporterTitle:'Toplu vektör dışa aktarıcısı',downloadZipBtn:'Toplu ZIP paketini indir',packagingZip:'ZIP paketleniyor...',batchZipDesc:'Dönüştürülen tüm SVG, EPS 10 ve önizlemeleri tek ZIP dosyasında toplar.'},
  pricingModal:{...en.pricingModal,title:'Vectria planınızı seçin',subtitle:'Üretim hacminize uygun planı seçin. 1 kredi = 1 tam dönüştürme (SVG + EPS 10 + PNG önizleme).',rolloverNotice:'Gizli ücret yoktur. Kullanılmayan krediler abonelik etkin olduğu sürece devreder.',monthly:'Aylık',yearly:'Yıllık',savePercent:'%20’ye kadar tasarruf',perMonth:' / ay',perYear:' / yıl',currentPlan:'Geçerli plan',popularBadge:'Popüler',bestValueBadge:'En iyi değer',choosePlan:(n,p)=>`${n} planını seç (${p})`,bullets:{starter:['Bitmap içermeyen saf SVG ve EPS 10','Yüksek çözünürlüklü PNG/JPG önizlemesi','Çift görünüm ve kontur denetçisi','Tekli ve çoklu dosya işleme'],pro:['Tüm Starter özellikleri','Toplu ZIP dışa aktarma (50 dosyaya kadar)','AI anahtar kelimeleri ve Adobe CSV','Bitmap içermeyen EPS 10 teknik doğrulaması','Öncelikli işlem kuyruğu'],studio:['Tüm Pro özellikleri','Sınırsız toplu işlem kuyruğu','Özel yüksek hızlı işlem hattı','Ekip lisansı ve öncelikli destek']},activeSubscription:(p,c)=>`Etkin plan: ${p} • ${c.toLocaleString('tr-TR')} kredi kullanılabilir`,cancelSubscription:'Aboneliği iptal et',cancelConfirmTitle:'Abonelik iptal edilsin mi?',cancelConfirmDesc:c=>`Kalan ${c.toLocaleString('tr-TR')} krediniz korunur; ancak öncelikli kuyruk ve ZIP araçları geçerli dönem sonunda sona erer.`,cancelKeepBtn:'Aboneliği koru',cancelProceedBtn:'Evet, iptal et',cancelledSuccess:'Aboneliğiniz iptal edildi. Kalan kredileriniz etkin kalır.',higherPlanAlert:'Mevcut planınız daha yüksek bir kademededir.',samePlanNotice:'Bu plana zaten abonesiniz. Daha fazla kredi eklemek için lütfen Kredi Yükle özelliğini kullanın.',includedBadge:'Dahil',includedInPlan:'Planınıza dahil',topupPrompt:'Yenilemeden önce ek krediye mi ihtiyacınız var?',topupBtn:'Kredi Yükle'},
  passwordModal:{title:'Şifreyi Değiştir',subtitle:'Hesap şifrenizi güncelleyin. En az 8 karakter uzunluğunda olmalıdır.',currentPasswordLabel:'Mevcut Şifre',newPasswordLabel:'Yeni Şifre',confirmPasswordLabel:'Yeni Şifreyi Onayla',placeholderCurrent:'Mevcut şifrenizi girin',placeholderNew:'En az 8 karakter',placeholderConfirm:'Yeni şifrenizi tekrar girin',submitBtn:'Şifreyi Güncelle',updating:'Güncelleniyor...',successMsg:'Şifreniz başarıyla değiştirildi.',mismatchError:'Yeni şifreler eşleşmiyor.',minLenError:'Şifre en az 8 karakter olmalıdır.',sameError:'Yeni şifre mevcut şifrenizden farklı olmalıdır.',cancelBtn:'İptal'},
  topupModal:{title:'Ek Kredi Yükle',subtitle:'Abonelik yenileme tarihinizi değiştirmeden anında ek Vektör veya AI Kredisi satın alın.',currentBalanceLabel:'Mevcut Hesap Bakiyesi:',vectorTab:'Vektör Kredisi',aiTab:'AI Kredisi',vectorDesc:'1 Vektör Kredisi = 1 tam SVG + EPS 10 dönüştürmesi. Hesabınız aktif olduğu sürece süresi dolmaz.',aiDesc:'AI Kredileri, Vectria AI Aracısı ile ticari meta veri oluşturmayı sağlar. Yalnızca başarılı işlemlerde düşülür.',saveTag:pct=>`%${pct} Tasarruf`,perCredit:cost=>`(${cost} / kredi)`,buyBtn:(name,price)=>`${name} Satın Al (${price})`,buyPack:price=>`Paketi Al (${price})`,popularBadge:'Popüler',bestValueBadge:'En İyi Değer',successAlert:(amount,type)=>`+${amount.toLocaleString('tr-TR')} ${type === 'vector' ? 'Vektör' : 'AI'} Kredisi hesabınıza başarıyla eklendi!`,connectingCheckout:'Güvenli ödeme sayfasına bağlanıyor...',closeBtn:'Kapat',packs:{vec_200:'Mini Paket',vec_500:'Büyüme Paketi',vec_1500:'Güç Paketi',ai_500:'AI Mini Paket',ai_1500:'AI Ultra Paket',ai_4000:'AI Studio Paket'}},
  aiModal:{...en.aiModal,title:'BYOK AI meta veri ayarları',subtitle:'BYOK kimlik bilgileriniz şifrelenir ve güvenle saklanır. Yalnızca yetkilendirdiğiniz meta veri isteklerinde kullanılır.',selectProvider:'AI sağlayıcısı seçin',apiKeyLabel:p=>`${p} API anahtarları`,apiKeysHint:'Her satıra bir anahtar. Biri başarısız olursa Vectria sonrakini dener. En fazla 10 anahtar.',apiKeysCount:c=>`${c}/10 anahtar`,getKey:'Anahtar al',primaryModel:'Birincil AI modeli',customModelPlaceholder:'Özel model kimliğini girin',fallbackModel:'Yedek model (birincil başarısız olursa otomatik geçiş)',fallbackNone:'Yok (hemen başarısız olsun)',fallbackDesc:'Birincil model 429, 503 veya başka bir hata döndürürse Vectria bu modelle yeniden dener.',customFallbackPlaceholder:'Özel yedek modeli girin',customEndpointLabel:'OpenAI uyumlu endpoint URL’si',customEndpointDesc:'OpenRouter veya yerel Ollama/vLLM kullanın.',testConnection:'Bağlantıyı test et',testConnectionDesc:'API anahtarını ve model kullanılabilirliğini doğrula',verifying:'Doğrulanıyor...',autoMetadataTitle:'Otomatik meta veri oluşturmayı etkinleştir',autoMetadataDesc:'Dönüştürülen vektörlere otomatik olarak başlık, 35 anahtar kelime ve kategori ekler.',vectriaFallbackTitle:'Vectria AI yedeği',vectriaFallbackDesc:'İsteğe bağlı yedek, yalnızca geçici BYOK hatasından sonra Vectria AI Aracısını kullanır. Başarılı her varlık 1 AI kredisine mal olur.',sourceByok:'BYOK • 0 AI kredisi',sourceManaged:'Vectria AI Aracısı',sourceFallback:'Vectria yedekli BYOK',estimatedCharge:c=>`En fazla ${c.toLocaleString('tr-TR')} AI kredisi`,byokNoCharge:'BYOK üretimi AI kredisi kullanmaz.',insufficientAiCredits:'Bu toplu işlem için yeterli AI kredisi yok.',resultSummary:(b,m,f,c)=>`${b} BYOK • ${m} yönetilen • ${f} yedek • ${c} AI kredisi alındı`,cancel:'İptal',saveConfig:'Yapılandırmayı kaydet'}
};

const vi: StudioCopy = {
  ...en,
  header:{...en.header,planNames:{free:'Gói miễn phí',starter:'Gói Starter',pro:'Gói Pro',studio:'Gói Studio',agency:'Gói Agency'},credits:n=>`${n.toLocaleString('vi-VN')} tín dụng`,vectorCredits:n=>`${n.toLocaleString('vi-VN')} tín dụng vector`,aiCredits:n=>`${n.toLocaleString('vi-VN')} tín dụng AI`,unlimitedCredits:'Tín dụng không giới hạn',upgrade:'Nâng cấp gói',creditTooltip:'1 tín dụng vector = 1 lần chuyển đổi (SVG + EPS 10 + bản xem trước). Nhấp để xem gói hoặc nạp tín dụng.',aiCreditTooltip:'Chỉ trừ 1 tín dụng AI khi Tác nhân AI Vectria xử lý thành công một tài sản. BYOK dùng 0 tín dụng AI.',admin:'Quản trị',userMenu:{accountPlan:'Gói tài khoản:',remainingCredits:'Tín dụng còn lại:',vectorCreditLabel:'Tín dụng vector:',aiCreditLabel:'Tín dụng AI:',creditNote:'Tín dụng vector dùng cho chuyển đổi. Tín dụng AI chỉ bị trừ khi Tác nhân AI Vectria tạo metadata thành công.',upgradePlan:'Nâng cấp gói',managePlan:'Quản lý thuê bao',workspace:'Không gian làm việc',byokKeys:'Khóa AI BYOK',themeLight:'Giao diện sáng',themeDark:'Giao diện tối',signOut:'Đăng xuất',adminConsole:'Bảng quản trị chủ sở hữu',changePassword:'Đổi mật khẩu',topupCredits:'Nạp tín dụng'},collapseQueue:'Thu gọn bảng hàng đợi',expandQueue:'Mở rộng bảng hàng đợi',toggleTheme:'Đổi giao diện'},
  leftSidebar:{...en.leftSidebar,autoOptimize:'Chế độ tối ưu tự động',analyzing:'Đang phân tích ảnh…',autoNoticeEmpty:'Hãy tải ảnh lên trước để bật tự động nhận diện',autoTitleEmpty:'Hãy tải ảnh lên trước để bật phân tích tự động',autoTitleActive:'Phân tích cạnh và mật độ đường nét, hợp nhất màu tương tự thành vùng vector sạch',perImagePrefix:'Mỗi ảnh:',quickPresets:'Cài đặt nhanh',guideBtn:'Hướng dẫn',guideTitle:'Hướng dẫn cài đặt sẵn',guideItems:[{name:'Phẳng (6–8 màu)',desc:'Biểu tượng, logo tối giản, pictogram và ký hiệu giao diện.'},{name:'Rực rỡ (12–16 màu)',desc:'Nhãn dán, huy hiệu, linh vật và minh họa có bóng đơn giản.'},{name:'Chi tiết (24–32 màu)',desc:'Minh họa nhiều lớp và tài sản trò chơi chi tiết.'},{name:'Màu thực (64+ màu)',desc:'Phong cảnh, tranh kỹ thuật số và chủ thể giống ảnh.'},{name:'Đơn nét (2 màu)',desc:'Line art, hình bóng, con dấu và đồ họa đen trắng.'}],presetChips:{'flat-6':'Biểu tượng phẳng','vibrant-12':'Đồ họa rực rỡ','detailed-32':'Chi tiết 32','ultra-64':'Ultra HD 64','truecolor':'Màu thực','monoline':'Đơn nét Đ/T'},presetTooltips:{'flat-6':'Biểu tượng phẳng: 8 màu, lọc nhiễu thấp và bảo vệ góc','vibrant-12':'Đồ họa rực rỡ: cân bằng cho nhãn dán, huy hiệu và linh vật','detailed-32':'Chi tiết 32: minh họa nhiều lớp và nhân vật có bóng','ultra-64':'Ultra HD 64: đồ họa giàu màu và phong cảnh','truecolor':'Màu thực: ảnh chân thực và tranh phức tạp','monoline':'Đơn nét Đ/T: line art, hình bóng và con dấu đơn sắc'},outputFormats:'Định dạng đầu ra',formatBoth:'SVG + EPS 10',formatSvg:'Chỉ SVG',formatEps:'EPS 10',tuningTitle:'Điều chỉnh vector',colorLimit:'Giới hạn bảng màu',truecolorLabel:'Màu thực đầy đủ (256+)',colorsUnit:n=>`${n} màu`,colorHints:{flat:'Lý tưởng cho biểu tượng và logo tối giản',vibrant:'Tốt nhất cho nhãn dán, huy hiệu và linh vật',detailed:'Đồ họa nhiều tông và minh họa chi tiết',photo:'Khuyên dùng cho phong cảnh và chủ thể giống ảnh'},speckleFilter:'Lọc đốm nhiễu',cornerThreshold:'Ngưỡng góc',curveSmoothness:'Độ mượt đường cong',curveTolerance:'Dung sai khớp đường cong',colorMerge:'Hợp nhất màu tương tự',nativeTopology:'Cấu trúc liên kết gốc',validated:'Đã xác thực',stripWhiteBg:'Loại bỏ nền trắng',stripWhiteDesc:'Làm sạch vùng trống và quầng viền',whiteSensitivity:'Độ nhạy màu trắng',antiHalo:'Thu mép chống quầng'},
  canvas:{...en.canvas,dropTitle:'Kéo và thả ảnh AI vào đây',dropDesc:'Chấp nhận PNG, JPG, JPEG và WEBP từ Midjourney, Recraft hoặc Leonardo AI.',browseBtn:'Chọn tệp trên thiết bị',dualView:'Chế độ kép',vectorView:'Vector',outlineView:'Đường viền',dualViewTitle:'Chế độ kép: điểm ảnh gốc và kết quả vector',vectorViewTitle:'Tác phẩm vector đầy đủ màu',outlineViewTitle:'Kiểm tra đường viền và đường cong Bézier',revectorize:'Vector hóa lại',revectorizeTitle:'Chạy lại vector hóa với tùy chọn hiện tại',convertVector:'Chuyển thành vector',convertAll:'Chuyển đổi tất cả',vectorizing:'Đang vector hóa...',computingCurves:'Đang tính đường cong Bézier và góc sắc',noVectorTitle:'Chưa tạo vector',noVectorDesc:'Chuyển tác phẩm raster này thành SVG và EPS 10',vectorReadyTitle:'Sẵn sàng tạo vector',vectorReadyDesc:'Nhấp bên dưới để chuyển điểm ảnh thành đường cong Bézier thương mại',tagRaster:'Điểm ảnh raster',tagOriginal:'Điểm ảnh gốc',tagVectorSvg:'Vector SVG (Bézier thuần)',tagVectorWireframe:'Khung đường viền (Bézier thuần)',tagOutlinePure:'Khung vector',zoomOut:'Thu nhỏ (-)',resetZoom:'Đặt lại thu phóng (100%)',zoomIn:'Phóng to (+) — tối đa 800%',wireframeInspect:'Kiểm tra đường viền vector thuần',wireframeFills:'Chuyển sang vùng tô đầy đủ màu',checkerboardSolid:'Chuyển sang nền tối đặc',checkerboardTrans:'Chuyển sang lưới trong suốt',verifiedTitle:'Sẵn sàng cho microstock',verifiedSpecs:'4000 × 4000 điểm ảnh • Chỉ vector',verifiedBadge:'EPS 10'},
  rightSidebar:{...en.rightSidebar,queueTitle:n=>`Hàng đợi (${n})`,queueProgress:(c,t,f)=>`Đang vector hóa ${c}/${t}: ${f}`,queueConverted:n=>`Đã chuyển đổi ${n}`,resetQueue:'Đặt lại',convertAll:'Chuyển đổi tất cả',queueEmptyTitle:'Hàng đợi trống.',queueEmptyDesc:'Kéo ảnh vào canvas hoặc nhấp Chọn tệp.',statusVectorizing:(c,t)=>`ĐANG VECTOR HÓA ${c}/${t}`,metadataTitle:'Metadata microstock hỗ trợ bởi AI',generateBtn:'Tạo',regenerateBtn:'Tạo lại',categoryLabel:'Danh mục:',keywordsCount:n=>`${n} từ khóa (sao chép)`,keywordsMore:n=>`+${n} từ khác`,noMetadataDesc:'Chưa tạo metadata AI. Nhấp “Tạo” để nhận tiêu đề thương mại, 35 từ khóa SEO và danh mục.',copiedKeywords:'Đã sao chép từ khóa.',csvExporterTitle:'Xuất metadata microstock',csvBtn:'CSV',csvColumnsLabel:'Cột:',batchExporterTitle:'Xuất vector hàng loạt',downloadZipBtn:'Tải gói ZIP hàng loạt',packagingZip:'Đang đóng gói ZIP...',batchZipDesc:'Gộp mọi SVG, EPS 10 và bản xem trước đã chuyển đổi vào một tệp ZIP.'},
  pricingModal:{...en.pricingModal,title:'Chọn gói Vectria',subtitle:'Chọn gói phù hợp với sản lượng của bạn. 1 tín dụng = 1 lần chuyển đổi đầy đủ (SVG + EPS 10 + bản xem trước PNG).',rolloverNotice:'Không có phí ẩn. Tín dụng chưa dùng được chuyển tiếp khi thuê bao còn hoạt động.',monthly:'Hàng tháng',yearly:'Hàng năm',savePercent:'Tiết kiệm đến 20%',perMonth:' / tháng',perYear:' / năm',currentPlan:'Gói hiện tại',popularBadge:'Phổ biến',bestValueBadge:'Giá trị tốt nhất',choosePlan:(n,p)=>`Chọn ${n} (${p})`,bullets:{starter:['SVG thuần và EPS 10 không bitmap','Bản xem trước PNG/JPG độ phân giải cao','Trình kiểm tra chế độ kép và đường viền','Xử lý một hoặc nhiều tệp'],pro:['Mọi tính năng Starter','Xuất ZIP hàng loạt đến 50 tệp','Từ khóa AI và CSV Adobe','Xác thực kỹ thuật EPS 10 không bitmap','Hàng đợi xử lý ưu tiên'],studio:['Mọi tính năng Pro','Hàng đợi hàng loạt không giới hạn','Luồng chuyên dụng tốc độ cao','Giấy phép nhóm và hỗ trợ ưu tiên']},activeSubscription:(p,c)=>`Gói đang hoạt động: ${p} • Còn ${c.toLocaleString('vi-VN')} tín dụng`,cancelSubscription:'Hủy thuê bao',cancelConfirmTitle:'Hủy thuê bao?',cancelConfirmDesc:c=>`${c.toLocaleString('vi-VN')} tín dụng còn lại được giữ, nhưng hàng đợi ưu tiên và công cụ ZIP sẽ hết hiệu lực khi kỳ hiện tại kết thúc.`,cancelKeepBtn:'Giữ thuê bao',cancelProceedBtn:'Có, hủy',cancelledSuccess:'Đã hủy thuê bao. Tín dụng còn lại vẫn có hiệu lực.',higherPlanAlert:'Gói hiện tại của bạn cao hơn.',samePlanNotice:'Bạn đã đăng ký gói này. Để bổ sung thêm tín dụng, vui lòng dùng tính năng Nạp tín dụng.',includedBadge:'Đã bao gồm',includedInPlan:'Đã bao gồm trong gói của bạn',topupPrompt:'Cần thêm tín dụng trước kỳ gia hạn?',topupBtn:'Nạp tín dụng'},
  passwordModal:{title:'Đổi mật khẩu tài khoản',subtitle:'Cập nhật mật khẩu tài khoản của bạn. Mật khẩu phải có ít nhất 8 ký tự.',currentPasswordLabel:'Mật khẩu hiện tại',newPasswordLabel:'Mật khẩu mới',confirmPasswordLabel:'Xác nhận mật khẩu mới',placeholderCurrent:'Nhập mật khẩu hiện tại',placeholderNew:'Tối thiểu 8 ký tự',placeholderConfirm:'Nhập lại mật khẩu mới',submitBtn:'Cập nhật mật khẩu',updating:'Đang cập nhật...',successMsg:'Mật khẩu của bạn đã được thay đổi thành công.',mismatchError:'Mật khẩu mới nhập lại không khớp.',minLenError:'Mật khẩu phải dài tối thiểu 8 ký tự.',sameError:'Mật khẩu mới phải khác mật khẩu hiện tại.',cancelBtn:'Hủy'},
  topupModal:{title:'Nạp thêm tín dụng',subtitle:'Mua thêm tín dụng Vector hoặc AI ngay lập tức mà không ảnh hưởng tới chu kỳ gia hạn thuê bao.',currentBalanceLabel:'Số dư tài khoản hiện tại:',vectorTab:'Tín dụng Vector',aiTab:'Tín dụng AI',vectorDesc:'1 tín dụng Vector = 1 lần chuyển đổi SVG + EPS 10 hoàn chỉnh. Không bao giờ hết hạn khi tài khoản còn hoạt động.',aiDesc:'Tín dụng AI dùng để tạo metadata thương mại với Tác nhân AI Vectria. Chỉ tính phí khi tạo thành công.',saveTag:pct=>`Tiết kiệm ${pct}%`,perCredit:cost=>`(${cost} / tín dụng)`,buyBtn:(name,price)=>`Mua ${name} (${price})`,buyPack:price=>`Mua gói (${price})`,popularBadge:'Phổ biến',bestValueBadge:'Giá trị tốt nhất',successAlert:(amount,type)=>`Đã nạp thành công +${amount.toLocaleString('vi-VN')} tín dụng ${type === 'vector' ? 'Vector' : 'AI'}!`,connectingCheckout:'Đang kết nối cổng thanh toán an toàn...',closeBtn:'Đóng',packs:{vec_200:'Gói Mini',vec_500:'Gói Tăng Tốc',vec_1500:'Gói Siêu Cường',ai_500:'Gói AI Mini',ai_1500:'Gói AI Ultra',ai_4000:'Gói AI Studio'}},
  aiModal:{...en.aiModal,title:'Cài đặt metadata AI BYOK',subtitle:'Thông tin xác thực BYOK được mã hóa và lưu an toàn. Chúng chỉ được dùng cho yêu cầu metadata bạn cho phép.',selectProvider:'Chọn nhà cung cấp AI',apiKeyLabel:p=>`Khóa API ${p}`,apiKeysHint:'Mỗi dòng một khóa. Nếu một khóa lỗi, Vectria thử khóa tiếp theo. Tối đa 10 khóa.',apiKeysCount:c=>`${c}/10 khóa`,getKey:'Lấy khóa',primaryModel:'Mô hình AI chính',customModelPlaceholder:'Nhập mã mô hình tùy chỉnh',fallbackModel:'Mô hình dự phòng (tự chuyển khi mô hình chính lỗi)',fallbackNone:'Không (báo lỗi ngay)',fallbackDesc:'Nếu mô hình chính trả về 429, 503 hoặc lỗi khác, Vectria thử lại bằng mô hình này.',customFallbackPlaceholder:'Nhập mô hình dự phòng tùy chỉnh',customEndpointLabel:'URL endpoint tương thích OpenAI',customEndpointDesc:'Dùng OpenRouter hoặc Ollama/vLLM cục bộ.',testConnection:'Kiểm tra kết nối',testConnectionDesc:'Xác minh khóa API và khả dụng của mô hình',verifying:'Đang xác minh...',autoMetadataTitle:'Bật tự động tạo metadata',autoMetadataDesc:'Tự động thêm tiêu đề, 35 từ khóa và danh mục cho vector đã chuyển đổi.',vectriaFallbackTitle:'AI dự phòng Vectria',vectriaFallbackDesc:'Dự phòng tùy chọn chỉ dùng Tác nhân AI Vectria sau lỗi BYOK tạm thời. Mỗi tài sản thành công tốn 1 tín dụng AI.',sourceByok:'BYOK • 0 tín dụng AI',sourceManaged:'Tác nhân AI Vectria',sourceFallback:'BYOK với dự phòng Vectria',estimatedCharge:c=>`Tối đa ${c.toLocaleString('vi-VN')} tín dụng AI`,byokNoCharge:'Tạo bằng BYOK không dùng tín dụng AI.',insufficientAiCredits:'Không đủ tín dụng AI cho lô này.',resultSummary:(b,m,f,c)=>`${b} BYOK • ${m} được quản lý • ${f} dự phòng • đã trừ ${c} tín dụng AI`,cancel:'Hủy',saveConfig:'Lưu cấu hình'}
};

const th: StudioCopy = {
  ...en,
  header:{...en.header,planNames:{free:'แพ็กเกจฟรี',starter:'แพ็กเกจ Starter',pro:'แพ็กเกจ Pro',studio:'แพ็กเกจ Studio',agency:'แพ็กเกจ Agency'},credits:n=>`${n.toLocaleString('th-TH')} เครดิต`,vectorCredits:n=>`${n.toLocaleString('th-TH')} เครดิตเวกเตอร์`,aiCredits:n=>`${n.toLocaleString('th-TH')} เครดิต AI`,unlimitedCredits:'เครดิตไม่จำกัด',upgrade:'อัปเกรดแพ็กเกจ',creditTooltip:'1 เครดิตเวกเตอร์ = การแปลง 1 ครั้ง (SVG + EPS 10 + ภาพตัวอย่าง) คลิกเพื่อดูแพ็กเกจหรือเติมเครดิต',aiCreditTooltip:'หัก 1 เครดิต AI เฉพาะเมื่อเอเจนต์ AI ของ Vectria ประมวลผลสินทรัพย์สำเร็จ BYOK ใช้ 0 เครดิต AI',admin:'ผู้ดูแลระบบ',userMenu:{accountPlan:'แพ็กเกจบัญชี:',remainingCredits:'เครดิตคงเหลือ:',vectorCreditLabel:'เครดิตเวกเตอร์:',aiCreditLabel:'เครดิต AI:',creditNote:'เครดิตเวกเตอร์ใช้สำหรับการแปลง เครดิต AI จะถูกหักเฉพาะเมทาดาทาที่เอเจนต์ AI ของ Vectria สร้างสำเร็จ',upgradePlan:'อัปเกรดแพ็กเกจ',managePlan:'จัดการการสมัครสมาชิก',workspace:'พื้นที่ทำงาน',byokKeys:'คีย์ AI แบบ BYOK',themeLight:'ธีมสว่าง',themeDark:'ธีมมืด',signOut:'ออกจากระบบ',adminConsole:'แผงผู้ดูแลระบบเจ้าของ',changePassword:'เปลี่ยนรหัสผ่าน',topupCredits:'เติมเครดิต'},collapseQueue:'ย่อแผงคิว',expandQueue:'ขยายแผงคิว',toggleTheme:'สลับธีม'},
  leftSidebar:{...en.leftSidebar,autoOptimize:'โหมดปรับให้เหมาะสมอัตโนมัติ',analyzing:'กำลังวิเคราะห์ภาพ…',autoNoticeEmpty:'อัปโหลดภาพก่อนเพื่อเปิดการตรวจจับอัตโนมัติ',autoTitleEmpty:'อัปโหลดภาพก่อนเพื่อเปิดการวิเคราะห์อัตโนมัติ',autoTitleActive:'วิเคราะห์ขอบและความหนาแน่นของเส้น พร้อมรวมสีที่คล้ายกันเป็นพื้นที่เวกเตอร์ที่สะอาด',perImagePrefix:'ต่อภาพ:',quickPresets:'พรีเซ็ตด่วน',guideBtn:'คู่มือ',guideTitle:'คู่มือพรีเซ็ต',guideItems:[{name:'สีเรียบ (6–8 สี)',desc:'ไอคอน โลโก้มินิมอล พิกโตแกรม และสัญลักษณ์อินเทอร์เฟซ'},{name:'สีสด (12–16 สี)',desc:'สติกเกอร์ ตรา มาสคอต และภาพประกอบที่มีเงาเรียบง่าย'},{name:'รายละเอียดสูง (24–32 สี)',desc:'ภาพประกอบหลายเลเยอร์และสินทรัพย์เกมที่มีรายละเอียด'},{name:'สีสมจริง (64+ สี)',desc:'ภูมิทัศน์ ภาพวาดดิจิทัล และวัตถุคล้ายภาพถ่าย'},{name:'เส้นเดียว (2 สี)',desc:'ลายเส้น เงารูป ตราประทับ และกราฟิกขาวดำ'}],presetChips:{'flat-6':'ไอคอนสีเรียบ','vibrant-12':'ภาพสีสด','detailed-32':'ละเอียด 32','ultra-64':'Ultra HD 64','truecolor':'สีสมจริง','monoline':'เส้นเดียว ข/ด'},presetTooltips:{'flat-6':'ไอคอนสีเรียบ: 8 สี กรองจุดรบกวนต่ำ และรักษามุม','vibrant-12':'ภาพสีสด: สมดุลสำหรับสติกเกอร์ ตรา และมาสคอต','detailed-32':'ละเอียด 32: ภาพประกอบหลายเลเยอร์และตัวละครที่มีเงา','ultra-64':'Ultra HD 64: งานศิลป์สีหลากหลายและภูมิทัศน์','truecolor':'สีสมจริง: ภาพถ่ายและภาพวาดซับซ้อน','monoline':'เส้นเดียว ข/ด: ลายเส้น เงารูป และตราประทับสีเดียว'},outputFormats:'รูปแบบผลลัพธ์',formatBoth:'SVG + EPS 10',formatSvg:'SVG เท่านั้น',formatEps:'EPS 10',tuningTitle:'ตัวควบคุมการปรับเวกเตอร์',colorLimit:'ขีดจำกัดจานสี',truecolorLabel:'สีสมจริงเต็มรูปแบบ (256+)',colorsUnit:n=>`${n} สี`,colorHints:{flat:'เหมาะสำหรับไอคอนและโลโก้มินิมอล',vibrant:'ดีที่สุดสำหรับสติกเกอร์ ตรา และมาสคอต',detailed:'งานหลายโทนและภาพประกอบที่มีรายละเอียด',photo:'แนะนำสำหรับภูมิทัศน์และวัตถุคล้ายภาพถ่าย'},speckleFilter:'ตัวกรองจุดรบกวน',cornerThreshold:'เกณฑ์มุม',curveSmoothness:'ความเรียบของเส้นโค้ง',curveTolerance:'ค่าความคลาดเคลื่อนการฟิตเส้นโค้ง',colorMerge:'รวมสีที่คล้ายกัน',nativeTopology:'โทโพโลยีแบบเนทีฟ',validated:'ตรวจสอบแล้ว',stripWhiteBg:'ลบพื้นหลังสีขาว',stripWhiteDesc:'ล้างพื้นที่ว่างและขอบเรือง',whiteSensitivity:'ความไวต่อสีขาว',antiHalo:'หดขอบป้องกันขอบเรือง'},
  canvas:{...en.canvas,dropTitle:'ลากและวางภาพ AI ที่นี่',dropDesc:'รองรับ PNG, JPG, JPEG และ WEBP จาก Midjourney, Recraft หรือ Leonardo AI',browseBtn:'เลือกไฟล์จากอุปกรณ์',dualView:'มุมมองคู่',vectorView:'เวกเตอร์',outlineView:'เส้นขอบ',dualViewTitle:'มุมมองคู่: พิกเซลต้นฉบับและผลลัพธ์เวกเตอร์',vectorViewTitle:'งานเวกเตอร์สีเต็ม',outlineViewTitle:'ตรวจสอบเส้นขอบและเส้นโค้ง Bézier',revectorize:'แปลงเวกเตอร์ใหม่',revectorizeTitle:'เรียกใช้การแปลงเวกเตอร์อีกครั้งด้วยตัวเลือกปัจจุบัน',convertVector:'แปลงเป็นเวกเตอร์',convertAll:'แปลงทั้งหมด',vectorizing:'กำลังแปลงเวกเตอร์...',computingCurves:'กำลังคำนวณเส้นโค้ง Bézier และมุมคม',noVectorTitle:'ยังไม่ได้สร้างเวกเตอร์',noVectorDesc:'แปลงงานราสเตอร์นี้เป็น SVG และ EPS 10',vectorReadyTitle:'พร้อมสร้างเวกเตอร์',vectorReadyDesc:'คลิกด้านล่างเพื่อเปลี่ยนพิกเซลเป็นเส้นโค้ง Bézier เชิงพาณิชย์',tagRaster:'พิกเซลราสเตอร์',tagOriginal:'พิกเซลต้นฉบับ',tagVectorSvg:'เวกเตอร์ SVG (Bézier บริสุทธิ์)',tagVectorWireframe:'โครงเส้นเวกเตอร์ (Bézier บริสุทธิ์)',tagOutlinePure:'โครงเส้นเวกเตอร์',zoomOut:'ซูมออก (-)',resetZoom:'รีเซ็ตการซูม (100%)',zoomIn:'ซูมเข้า (+) — สูงสุด 800%',wireframeInspect:'ตรวจสอบเส้นขอบเวกเตอร์บริสุทธิ์',wireframeFills:'สลับเป็นพื้นที่สีเต็ม',checkerboardSolid:'สลับเป็นพื้นหลังสีเข้มทึบ',checkerboardTrans:'สลับเป็นตารางโปร่งใส',verifiedTitle:'พร้อมสำหรับไมโครสต็อก',verifiedSpecs:'4000 × 4000 พิกเซล • เวกเตอร์เท่านั้น',verifiedBadge:'EPS 10'},
  rightSidebar:{...en.rightSidebar,queueTitle:n=>`คิว (${n})`,queueProgress:(c,t,f)=>`กำลังแปลงเวกเตอร์ ${c}/${t}: ${f}`,queueConverted:n=>`แปลงแล้ว ${n} รายการ`,resetQueue:'รีเซ็ต',convertAll:'แปลงทั้งหมด',queueEmptyTitle:'คิวว่าง',queueEmptyDesc:'ลากภาพลงบนแคนวาสหรือคลิก เลือกไฟล์',statusVectorizing:(c,t)=>`กำลังแปลงเวกเตอร์ ${c}/${t}`,metadataTitle:'เมทาดาทาไมโครสต็อกที่ช่วยสร้างด้วย AI',generateBtn:'สร้าง',regenerateBtn:'สร้างใหม่',categoryLabel:'หมวดหมู่:',keywordsCount:n=>`${n} คีย์เวิร์ด (คัดลอก)`,keywordsMore:n=>`+อีก ${n}`,noMetadataDesc:'ยังไม่ได้สร้างเมทาดาทา AI คลิก “สร้าง” เพื่อรับชื่อเชิงพาณิชย์ คีย์เวิร์ด SEO 35 คำ และหมวดหมู่',copiedKeywords:'คัดลอกคีย์เวิร์ดแล้ว',csvExporterTitle:'ส่งออกเมทาดาทาไมโครสต็อก',csvBtn:'CSV',csvColumnsLabel:'คอลัมน์:',batchExporterTitle:'ส่งออกเวกเตอร์แบบกลุ่ม',downloadZipBtn:'ดาวน์โหลดแพ็กเกจ ZIP แบบกลุ่ม',packagingZip:'กำลังจัดแพ็กเกจ ZIP...',batchZipDesc:'รวม SVG, EPS 10 และภาพตัวอย่างที่แปลงแล้วทั้งหมดไว้ในไฟล์ ZIP เดียว'},
  pricingModal:{...en.pricingModal,title:'เลือกแพ็กเกจ Vectria',subtitle:'เลือกแพ็กเกจที่เหมาะกับปริมาณการผลิตของคุณ 1 เครดิต = การแปลงเต็มรูปแบบ 1 ครั้ง (SVG + EPS 10 + ภาพตัวอย่าง PNG)',rolloverNotice:'ไม่มีค่าธรรมเนียมแอบแฝง เครดิตที่ไม่ได้ใช้จะสะสมต่อไปเมื่อการสมัครสมาชิกยังใช้งานอยู่',monthly:'รายเดือน',yearly:'รายปี',savePercent:'ประหยัดสูงสุด 20%',perMonth:' / เดือน',perYear:' / ปี',currentPlan:'แพ็กเกจปัจจุบัน',popularBadge:'ยอดนิยม',bestValueBadge:'คุ้มค่าที่สุด',choosePlan:(n,p)=>`เลือก ${n} (${p})`,bullets:{starter:['SVG บริสุทธิ์และ EPS 10 ที่ไม่มีบิตแมป','ภาพตัวอย่าง PNG/JPG ความละเอียดสูง','ตัวตรวจสอบมุมมองคู่และเส้นขอบ','ประมวลผลไฟล์เดี่ยวและหลายไฟล์'],pro:['คุณสมบัติทั้งหมดของ Starter','ส่งออก ZIP แบบกลุ่มสูงสุด 50 ไฟล์','คีย์เวิร์ด AI และ CSV สำหรับ Adobe','ตรวจสอบทางเทคนิค EPS 10 ที่ไม่มีบิตแมป','คิวประมวลผลแบบเร่งด่วน'],studio:['คุณสมบัติทั้งหมดของ Pro','คิวแบบกลุ่มไม่จำกัด','ไปป์ไลน์เฉพาะความเร็วสูง','สิทธิ์ใช้งานสำหรับทีมและการสนับสนุนเร่งด่วน']},activeSubscription:(p,c)=>`แพ็กเกจที่ใช้งาน: ${p} • มี ${c.toLocaleString('th-TH')} เครดิต`,cancelSubscription:'ยกเลิกการสมัครสมาชิก',cancelConfirmTitle:'ยกเลิกการสมัครสมาชิก?',cancelConfirmDesc:c=>`เครดิตที่เหลือ ${c.toLocaleString('th-TH')} เครดิตจะยังอยู่ แต่คิวเร่งด่วนและเครื่องมือ ZIP จะสิ้นสุดเมื่อครบรอบปัจจุบัน`,cancelKeepBtn:'คงการสมัครสมาชิก',cancelProceedBtn:'ใช่ ยกเลิก',cancelledSuccess:'ยกเลิกการสมัครสมาชิกแล้ว เครดิตที่เหลือยังใช้งานได้',higherPlanAlert:'แพ็กเกจปัจจุบันของคุณสูงกว่าอยู่แล้ว',samePlanNotice:'คุณสมัครแพ็กเกจนี้อยู่แล้ว หากต้องการเพิ่มเครดิต กรุณาใช้ฟีเจอร์เติมเครดิต',includedBadge:'รวมอยู่แล้ว',includedInPlan:'รวมอยู่ในแพ็กเกจของคุณแล้ว',topupPrompt:'ต้องการเครดิตเพิ่มก่อนถึงรอบต่ออายุหรือไม่?',topupBtn:'เติมเครดิต'},
  passwordModal:{title:'เปลี่ยนรหัสผ่านบัญชี',subtitle:'อัปเดตรหัสผ่านบัญชีของคุณ ต้องมีความยาวอย่างน้อย 8 ตัวอักษร',currentPasswordLabel:'รหัสผ่านปัจจุบัน',newPasswordLabel:'รหัสผ่านใหม่',confirmPasswordLabel:'ยืนยันรหัสผ่านใหม่',placeholderCurrent:'กรอกรหัสผ่านปัจจุบัน',placeholderNew:'อย่างน้อย 8 ตัวอักษร',placeholderConfirm:'กรอกรหัสผ่านใหม่อีกครั้ง',submitBtn:'อัปเดตรหัสผ่าน',updating:'กำลังอัปเดต...',successMsg:'เปลี่ยนรหัสผ่านของคุณเรียบร้อยแล้ว',mismatchError:'รหัสผ่านใหม่ไม่ตรงกัน',minLenError:'รหัสผ่านต้องมีความยาวอย่างน้อย 8 ตัวอักษร',sameError:'รหัสผ่านใหม่ต้องไม่ซ้ำกับรหัสผ่านเดิม',cancelBtn:'ยกเลิก'},
  topupModal:{title:'เติมเครดิตเพิ่มเติม',subtitle:'ซื้อเครดิตเวกเตอร์หรือเครดิต AI เพิ่มได้ทันทีโดยไม่กระทบต่อรอบบิลการสมัครสมาชิกของคุณ',currentBalanceLabel:'ยอดคงเหลือในบัญชีปัจจุบัน:',vectorTab:'เครดิตเวกเตอร์',aiTab:'เครดิต AI',vectorDesc:'1 เครดิตเวกเตอร์ = การแปลง SVG + EPS 10 เต็มรูปแบบ 1 ครั้ง ไม่มีวันหมดอายุเมื่อบัญชียังใช้งานอยู่',aiDesc:'เครดิต AI ใช้สร้างเมทาดาทาเชิงพาณิชย์ด้วยเอเจนต์ AI ของ Vectria หักเครดิตเฉพาะเมื่อประมวลผลสำเร็จเท่านั้น',saveTag:pct=>`ประหยัด ${pct}%`,perCredit:cost=>`(${cost} / เครดิต)`,buyBtn:(name,price)=>`ซื้อ ${name} (${price})`,buyPack:price=>`ซื้อแพ็กเกจ (${price})`,popularBadge:'ยอดนิยม',bestValueBadge:'คุ้มค่าที่สุด',successAlert:(amount,type)=>`เพิ่ม +${amount.toLocaleString('th-TH')} เครดิต ${type === 'vector' ? 'เวกเตอร์' : 'AI'} สำเร็จแล้ว!`,connectingCheckout:'กำลังเชื่อมต่อไปยังหน้าระบบชำระเงิน...',closeBtn:'ปิด',packs:{vec_200:'มินิบูสเตอร์',vec_500:'โกรทบูสเตอร์',vec_1500:'พาวเวอร์บูสเตอร์',ai_500:'AI มินิแพ็ก',ai_1500:'AI อัลตราแพ็ก',ai_4000:'AI สตูดิโอแพ็ก'}},
  aiModal:{...en.aiModal,title:'การตั้งค่าเมทาดาทา AI แบบ BYOK',subtitle:'ข้อมูลรับรอง BYOK ของคุณถูกเข้ารหัสและจัดเก็บอย่างปลอดภัย ใช้เฉพาะคำขอเมทาดาทาที่คุณอนุญาต',selectProvider:'เลือกผู้ให้บริการ AI',apiKeyLabel:p=>`คีย์ API ของ ${p}`,apiKeysHint:'หนึ่งคีย์ต่อบรรทัด หากคีย์หนึ่งล้มเหลว Vectria จะลองคีย์ถัดไป สูงสุด 10 คีย์',apiKeysCount:c=>`${c}/10 คีย์`,getKey:'รับคีย์',primaryModel:'โมเดล AI หลัก',customModelPlaceholder:'กรอกรหัสโมเดลที่กำหนดเอง',fallbackModel:'โมเดลสำรอง (สลับอัตโนมัติเมื่อโมเดลหลักล้มเหลว)',fallbackNone:'ไม่มี (แจ้งข้อผิดพลาดทันที)',fallbackDesc:'หากโมเดลหลักส่งคืน 429, 503 หรือข้อผิดพลาดอื่น Vectria จะลองอีกครั้งด้วยโมเดลนี้',customFallbackPlaceholder:'กรอกโมเดลสำรองที่กำหนดเอง',customEndpointLabel:'URL endpoint ที่เข้ากันได้กับ OpenAI',customEndpointDesc:'ใช้ OpenRouter หรือ Ollama/vLLM ภายในเครื่อง',testConnection:'ทดสอบการเชื่อมต่อ',testConnectionDesc:'ตรวจสอบคีย์ API และความพร้อมใช้งานของโมเดล',verifying:'กำลังตรวจสอบ...',autoMetadataTitle:'เปิดการสร้างเมทาดาทาอัตโนมัติ',autoMetadataDesc:'เพิ่มชื่อ คีย์เวิร์ด 35 คำ และหมวดหมู่ให้เวกเตอร์ที่แปลงแล้วโดยอัตโนมัติ',vectriaFallbackTitle:'AI สำรองของ Vectria',vectriaFallbackDesc:'ระบบสำรองแบบเลือกใช้จะใช้เอเจนต์ AI ของ Vectria หลังเกิดข้อผิดพลาด BYOK ชั่วคราวเท่านั้น สินทรัพย์ที่สำเร็จแต่ละรายการใช้ 1 เครดิต AI',sourceByok:'BYOK • 0 เครดิต AI',sourceManaged:'เอเจนต์ AI ของ Vectria',sourceFallback:'BYOK พร้อมระบบสำรอง Vectria',estimatedCharge:c=>`สูงสุด ${c.toLocaleString('th-TH')} เครดิต AI`,byokNoCharge:'การสร้างด้วย BYOK ไม่ใช้เครดิต AI',insufficientAiCredits:'เครดิต AI ไม่เพียงพอสำหรับงานกลุ่มนี้',resultSummary:(b,m,f,c)=>`${b} BYOK • ${m} จัดการให้ • ${f} สำรอง • หัก ${c} เครดิต AI`,cancel:'ยกเลิก',saveConfig:'บันทึกการตั้งค่า'}
};

const ar: StudioCopy = {
  ...en,
  header:{...en.header,planNames:{free:'الخطة المجانية',starter:'خطة Starter',pro:'خطة Pro',studio:'خطة Studio',agency:'خطة Agency'},credits:n=>`${n.toLocaleString('ar')} رصيد`,vectorCredits:n=>`${n.toLocaleString('ar')} رصيد متجهي`,aiCredits:n=>`${n.toLocaleString('ar')} رصيد ذكاء اصطناعي`,unlimitedCredits:'أرصدة غير محدودة',upgrade:'ترقية الخطة',creditTooltip:'رصيد متجهي واحد = تحويل واحد (SVG + EPS 10 + معاينة). انقر لعرض الخطط أو إضافة أرصدة',aiCreditTooltip:'يُخصم رصيد ذكاء اصطناعي واحد فقط عندما يعالج وكيل Vectria للذكاء الاصطناعي أصلًا بنجاح. يستخدم BYOK صفرًا من الأرصدة',admin:'المشرف',userMenu:{accountPlan:'خطة الحساب:',remainingCredits:'الأرصدة المتبقية:',vectorCreditLabel:'الأرصدة المتجهية:',aiCreditLabel:'أرصدة الذكاء الاصطناعي:',creditNote:'تُستخدم الأرصدة المتجهية للتحويل. لا تُخصم أرصدة الذكاء الاصطناعي إلا للبيانات الوصفية التي ينشئها وكيل Vectria للذكاء الاصطناعي بنجاح',upgradePlan:'ترقية الخطة',managePlan:'إدارة الاشتراك',workspace:'مساحة العمل',byokKeys:'مفاتيح AI بنظام BYOK',themeLight:'السمة الفاتحة',themeDark:'السمة الداكنة',signOut:'تسجيل الخروج',adminConsole:'لوحة تحكم المالك',changePassword:'تغيير كلمة المرور',topupCredits:'شحن الرصيد'},collapseQueue:'طي لوحة الطابور',expandQueue:'توسيع لوحة الطابور',toggleTheme:'تبديل السمة'},
  leftSidebar:{...en.leftSidebar,autoOptimize:'وضع التحسين التلقائي',analyzing:'جارٍ تحليل الصورة…',autoNoticeEmpty:'حمّل صورة أولًا لتفعيل الاكتشاف التلقائي',autoTitleEmpty:'حمّل صورة أولًا لتفعيل التحليل التلقائي',autoTitleActive:'يحلل الحواف وكثافة الخطوط ويدمج الألوان المتشابهة في تعبئات متجهية نظيفة',perImagePrefix:'لكل صورة:',quickPresets:'إعدادات سريعة',guideBtn:'الدليل',guideTitle:'دليل الإعدادات المسبقة',guideItems:[{name:'مسطح (6–8 ألوان)',desc:'أيقونات وشعارات بسيطة ورموز واجهة.'},{name:'زاهٍ (12–16 لونًا)',desc:'ملصقات وشارات وشخصيات ورسومات بتظليل بسيط.'},{name:'مفصل (24–32 لونًا)',desc:'رسومات متعددة الطبقات وأصول ألعاب مفصلة.'},{name:'ألوان حقيقية (64+ لونًا)',desc:'مناظر طبيعية ولوحات رقمية وموضوعات فوتوغرافية.'},{name:'خط أحادي (لونان)',desc:'رسومات خطية وظلال وأختام وفن أحادي اللون.'}],presetChips:{'flat-6':'أيقونة مسطحة','vibrant-12':'فن زاهٍ','detailed-32':'مفصل 32','ultra-64':'Ultra HD 64','truecolor':'ألوان حقيقية','monoline':'خط أحادي'},presetTooltips:{'flat-6':'أيقونة مسطحة: 8 ألوان، ترشيح ضوضاء منخفض، وحماية الزوايا','vibrant-12':'فن زاهٍ: إعدادات متوازنة للملصقات والشارات والشخصيات','detailed-32':'مفصل 32: رسومات متعددة الطبقات وشخصيات مظللة','ultra-64':'Ultra HD 64: فن غني بالألوان ومناظر طبيعية','truecolor':'ألوان حقيقية: صور واقعية ولوحات معقدة','monoline':'خط أحادي: فن خطي وظلال وأختام أحادية اللون'},outputFormats:'صيغ الإخراج',formatBoth:'SVG + EPS 10',formatSvg:'SVG فقط',formatEps:'EPS 10',tuningTitle:'عناصر ضبط التحويل المتجهي',colorLimit:'حد لوحة الألوان',truecolorLabel:'ألوان حقيقية كاملة (256+)',colorsUnit:n=>`${n} لونًا`,colorHints:{flat:'مثالي للأيقونات والشعارات البسيطة',vibrant:'الأفضل للملصقات والشارات والشخصيات',detailed:'فن متعدد الدرجات ورسومات مفصلة',photo:'موصى به للمناظر والموضوعات الفوتوغرافية'},speckleFilter:'مرشح الشوائب',cornerThreshold:'عتبة الزوايا',curveSmoothness:'نعومة المنحنى',curveTolerance:'تسامح ملاءمة المنحنى',colorMerge:'دمج الألوان المتشابهة',nativeTopology:'البنية الأصلية',validated:'تم التحقق',stripWhiteBg:'إزالة الخلفية البيضاء',stripWhiteDesc:'ينظف المساحات السالبة والهالات',whiteSensitivity:'حساسية اللون الأبيض',antiHalo:'تقليص الحافة لمنع الهالة'},
  canvas:{...en.canvas,dropTitle:'اسحب صورة ذكاء اصطناعي وأفلتها هنا',dropDesc:'يدعم PNG وJPG وJPEG وWEBP من Midjourney أو Recraft أو Leonardo AI',browseBtn:'اختيار ملف من الجهاز',dualView:'عرض مزدوج',vectorView:'متجه',outlineView:'حدود',dualViewTitle:'عرض مزدوج: البكسلات الأصلية والنتيجة المتجهية',vectorViewTitle:'رسم متجهي كامل الألوان',outlineViewTitle:'فحص الحدود ومنحنيات Bézier',revectorize:'إعادة التحويل المتجهي',revectorizeTitle:'تشغيل التحويل المتجهي مجددًا بالخيارات الحالية',convertVector:'تحويل إلى متجه',convertAll:'تحويل الكل',vectorizing:'جارٍ التحويل المتجهي…',computingCurves:'جارٍ حساب منحنيات Bézier والزوايا الحادة',noVectorTitle:'لم يُنشأ رسم متجهي بعد',noVectorDesc:'حوّل هذا الأصل النقطي إلى SVG وEPS 10',vectorReadyTitle:'جاهز لإنشاء الرسم المتجهي',vectorReadyDesc:'انقر أدناه لتحويل البكسلات إلى منحنيات Bézier تجارية',tagRaster:'بكسلات نقطية',tagOriginal:'البكسلات الأصلية',tagVectorSvg:'SVG متجهي (منحنيات Bézier نقية)',tagVectorWireframe:'هيكل متجهي (منحنيات Bézier نقية)',tagOutlinePure:'حدود متجهية',zoomOut:'تصغير (-)',resetZoom:'إعادة التكبير (100%)',zoomIn:'تكبير (+) — حتى 800%',wireframeInspect:'فحص الحدود المتجهية النقية',wireframeFills:'التبديل إلى التعبئات كاملة الألوان',checkerboardSolid:'التبديل إلى خلفية داكنة مصمتة',checkerboardTrans:'التبديل إلى شبكة شفافة',verifiedTitle:'جاهز للميكروستوك',verifiedSpecs:'4000 × 4000 بكسل • متجه فقط',verifiedBadge:'EPS 10'},
  rightSidebar:{...en.rightSidebar,queueTitle:n=>`طابور (${n})`,queueProgress:(c,t,f)=>`تحويل ${c}/${t}: ${f}`,queueConverted:n=>`تم تحويل ${n}`,resetQueue:'إعادة تعيين',convertAll:'تحويل الكل',queueEmptyTitle:'الطابور فارغ',queueEmptyDesc:'اسحب الصور إلى اللوحة أو انقر على اختيار ملف',statusVectorizing:(c,t)=>`جارٍ تحويل ${c}/${t}`,metadataTitle:'بيانات وصفية للميكروستوك بمساعدة الذكاء الاصطناعي',generateBtn:'إنشاء',regenerateBtn:'إعادة الإنشاء',categoryLabel:'الفئة:',keywordsCount:n=>`${n} كلمة مفتاحية (نسخ)`,keywordsMore:n=>`+${n} أخرى`,noMetadataDesc:'لم تُنشأ بيانات وصفية بعد. انقر على «إنشاء» للحصول على عنوان تجاري و35 كلمة مفتاحية محسّنة للبحث وفئة.',copiedKeywords:'تم نسخ الكلمات المفتاحية',csvExporterTitle:'تصدير بيانات الميكروستوك الوصفية',csvBtn:'CSV',csvColumnsLabel:'الأعمدة:',batchExporterTitle:'تصدير الرسومات المتجهة جماعيًا',downloadZipBtn:'تنزيل حزمة ZIP جماعية',packagingZip:'جارٍ تجهيز حزمة ZIP…',batchZipDesc:'يجمع كل ملفات SVG وEPS 10 والمعاينات المحوّلة في ملف ZIP واحد'},
  pricingModal:{...en.pricingModal,title:'اختر خطة Vectria',subtitle:'اختر الخطة المناسبة لحجم إنتاجك. رصيد واحد = تحويل كامل واحد (SVG + EPS 10 + معاينة PNG).',rolloverNotice:'لا رسوم خفية. تُرحّل الأرصدة غير المستخدمة ما دام الاشتراك نشطًا.',monthly:'شهري',yearly:'سنوي',savePercent:'وفّر حتى 20%',perMonth:' / شهر',perYear:' / سنة',currentPlan:'الخطة الحالية',popularBadge:'شائعة',bestValueBadge:'أفضل قيمة',choosePlan:(n,p)=>`اختر ${n} (${p})`,bullets:{starter:['SVG نقي وEPS 10 بلا صور نقطية','معاينة PNG/JPG عالية الدقة','فاحص مزدوج وحدود','معالجة ملف واحد أو عدة ملفات'],pro:['جميع ميزات Starter','تصدير ZIP جماعي حتى 50 ملفًا','كلمات مفتاحية بالذكاء الاصطناعي وCSV لـ Adobe','تحقق تقني من EPS 10 بلا صور نقطية','طابور معالجة ذو أولوية'],studio:['جميع ميزات Pro','طابور جماعي غير محدود','مسار مخصص عالي السرعة','ترخيص فرق ودعم ذو أولوية']},activeSubscription:(p,c)=>`الخطة النشطة: ${p} • ${c.toLocaleString('ar')} رصيد متاح`,cancelSubscription:'إلغاء الاشتراك',cancelConfirmTitle:'إلغاء الاشتراك؟',cancelConfirmDesc:c=>`ستحتفظ بـ ${c.toLocaleString('ar')} من الأرصدة المتبقية، لكن الطابور ذي الأولوية وأدوات ZIP سيتوقفان في نهاية الفترة الحالية.`,cancelKeepBtn:'الاحتفاظ بالاشتراك',cancelProceedBtn:'نعم، إلغاء',cancelledSuccess:'تم إلغاء الاشتراك. تظل أرصدتك المتبقية نشطة.',higherPlanAlert:'خطتك الحالية أعلى بالفعل.',samePlanNotice:'أنت مشترك بالفعل في هذه الخطة. لإضافة المزيد من الأرصدة، يرجى استخدام ميزة شحن الرصيد.',includedBadge:'مشمول',includedInPlan:'مشمول في خطتك',topupPrompt:'هل تحتاج إلى رصيد إضافي قبل موعد التجديد؟',topupBtn:'شحن الرصيد'},
  passwordModal:{title:'تغيير كلمة المرور',subtitle:'حدّث كلمة مرور حسابك. يجب أن تتكون من 8 أحرف على الأقل.',currentPasswordLabel:'كلمة المرور الحالية',newPasswordLabel:'كلمة المرور الجديدة',confirmPasswordLabel:'تأكيد كلمة المرور الجديدة',placeholderCurrent:'أدخل كلمة المرور الحالية',placeholderNew:'8 أحرف كحد أدنى',placeholderConfirm:'أعد كتابة كلمة المرور الجديدة',submitBtn:'تحديث كلمة المرور',updating:'جارٍ التحديث...',successMsg:'تم تغيير كلمة المرور بنجاح.',mismatchError:'كلمتا المرور غير متطابقتين.',minLenError:'يجب أن تتكون كلمة المرور من 8 أحرف على الأقل.',sameError:'يجب أن تختلف كلمة المرور الجديدة عن الحالية.',cancelBtn:'إلغاء'},
  topupModal:{title:'شحن أرصدة إضافية',subtitle:'اشحن أرصدة متجهة أو أرصدة ذكاء اصطناعي فورًا دون التأثير على دورة تجديد اشتراكك الحالية.',currentBalanceLabel:'رصيد الحساب الحالي:',vectorTab:'أرصدة متجهة',aiTab:'أرصدة الذكاء الاصطناعي',vectorDesc:'رصيد متجهي واحد = تحويل كامل واحد (SVG + EPS 10). لا تنتهي صلاحيته طالما أن حسابك نشط.',aiDesc:'أرصدة الذكاء الاصطناعي مخصصة لإنشاء بيانات وصفية تجارية عبر وكيل Vectria. تُخصم فقط عند النجاح.',saveTag:pct=>`وفر ${pct}٪`,perCredit:cost=>`(${cost} / رصيد)`,buyBtn:(name,price)=>`شراء ${name} (${price})`,buyPack:price=>`شراء الحزمة (${price})`,popularBadge:'الأكثر رواجًا',bestValueBadge:'أفضل قيمة',successAlert:(amount,type)=>`تمت إضافة +${amount.toLocaleString('ar')} من أرصدة ${type === 'vector' ? 'المتجهات' : 'الذكاء الاصطناعي'} بنجاح!`,connectingCheckout:'جارٍ الاتصال ببوابة الدفع الآمنة...',closeBtn:'إغلاق',packs:{vec_200:'حزمة البداية المصغرة',vec_500:'حزمة التوسع والنمو',vec_1500:'حزمة القوة القصوى',ai_500:'حزمة الذكاء الاصطناعي Mini',ai_1500:'حزمة الذكاء الاصطناعي Ultra',ai_4000:'حزمة الذكاء الاصطناعي Studio'}},
  aiModal:{...en.aiModal,title:'إعدادات بيانات الذكاء الاصطناعي بنظام BYOK',subtitle:'تُشفّر بيانات اعتماد BYOK وتُخزن بأمان، ولا تُستخدم إلا لطلبات البيانات الوصفية التي تصرح بها.',selectProvider:'اختيار مزود الذكاء الاصطناعي',apiKeyLabel:p=>`مفتاح API لـ ${p}`,apiKeysHint:'مفتاح واحد في كل سطر. إذا فشل مفتاح، تجرب Vectria التالي، بحد أقصى 10 مفاتيح.',apiKeysCount:c=>`${c}/10 مفاتيح`,getKey:'الحصول على مفتاح',primaryModel:'نموذج الذكاء الاصطناعي الأساسي',customModelPlaceholder:'أدخل معرّف نموذج مخصص',fallbackModel:'النموذج الاحتياطي (تبديل تلقائي عند فشل الأساسي)',fallbackNone:'بلا احتياطي (إظهار الخطأ فورًا)',fallbackDesc:'إذا أعاد النموذج الأساسي خطأ 429 أو 503 أو خطأ آخر، تعيد Vectria المحاولة بهذا النموذج.',customFallbackPlaceholder:'أدخل نموذجًا احتياطيًا مخصصًا',customEndpointLabel:'عنوان endpoint متوافق مع OpenAI',customEndpointDesc:'استخدم OpenRouter أو Ollama/vLLM محليًا',testConnection:'اختبار الاتصال',testConnectionDesc:'التحقق من مفتاح API وتوفر النموذج',verifying:'جارٍ التحقق…',autoMetadataTitle:'تفعيل الإنشاء التلقائي للبيانات الوصفية',autoMetadataDesc:'إضافة عنوان و35 كلمة مفتاحية وفئة تلقائيًا إلى الرسومات المحوّلة',vectriaFallbackTitle:'احتياطي Vectria للذكاء الاصطناعي',vectriaFallbackDesc:'يستخدم الاحتياطي الاختياري وكيل Vectria للذكاء الاصطناعي فقط بعد خطأ مؤقت في BYOK. يستخدم كل أصل ناجح رصيد ذكاء اصطناعي واحدًا.',sourceByok:'BYOK • صفر من أرصدة الذكاء الاصطناعي',sourceManaged:'وكيل Vectria للذكاء الاصطناعي',sourceFallback:'BYOK مع احتياطي Vectria',estimatedCharge:c=>`حتى ${c.toLocaleString('ar')} من أرصدة الذكاء الاصطناعي`,byokNoCharge:'لا يستخدم الإنشاء عبر BYOK أرصدة الذكاء الاصطناعي',insufficientAiCredits:'أرصدة الذكاء الاصطناعي غير كافية لهذه المهمة الجماعية',resultSummary:(b,m,f,c)=>`${b} BYOK • ${m} مُدار • ${f} احتياطي • خُصم ${c} من الأرصدة`,cancel:'إلغاء',saveConfig:'حفظ الإعدادات'}
};

export interface FlashCopy {
  notificationTitle: string;
  confirmationTitle: string;
  continueLabel: string;
  cancelLabel: string;
  computeResource: string;
  localLabel: string;
  cloudLabel: string;
  usingLocalTitle: string;
  usingServerTitle: string;
  usingLocalFlash: string;
  usingServerFlash: string;
  topupProcessed: string;
  paymentProcessed: string;
  maintenanceRestricted: string;
}

export const flashCopy: Record<Locale, FlashCopy> = {
  en: { notificationTitle:'Notification', confirmationTitle:'Confirmation', continueLabel:'Continue', cancelLabel:'Cancel', computeResource:'Compute resource', localLabel:'Local', cloudLabel:'Cloud', usingLocalTitle:'Using local resources', usingServerTitle:'Using Vectria Server', usingLocalFlash:'Using local resources (your device CPU)', usingServerFlash:'Using Vectria Server (cloud VPS)', topupProcessed:'Top-up processed successfully. Syncing your credits…', paymentProcessed:'Payment processed successfully. Activating your plan and syncing credits…', maintenanceRestricted:'The system is undergoing maintenance. Access is temporarily restricted to administrators.' },
  id: { notificationTitle:'Pemberitahuan', confirmationTitle:'Konfirmasi', continueLabel:'Lanjutkan', cancelLabel:'Batal', computeResource:'Sumber daya komputasi', localLabel:'Lokal', cloudLabel:'Cloud', usingLocalTitle:'Menggunakan sumber daya lokal', usingServerTitle:'Menggunakan Server Vectria', usingLocalFlash:'Menggunakan sumber daya lokal (CPU perangkat Anda)', usingServerFlash:'Menggunakan Server Vectria (VPS cloud)', topupProcessed:'Pembayaran isi ulang berhasil diproses. Menyinkronkan kredit Anda…', paymentProcessed:'Pembayaran berhasil diproses. Mengaktifkan paket dan menyinkronkan kredit Anda…', maintenanceRestricted:'Sistem sedang dalam pemeliharaan. Akses sementara dibatasi hanya untuk administrator.' },
  es: { notificationTitle:'Notificación', confirmationTitle:'Confirmación', continueLabel:'Continuar', cancelLabel:'Cancelar', computeResource:'Recurso de procesamiento', localLabel:'Local', cloudLabel:'Nube', usingLocalTitle:'Usando recursos locales', usingServerTitle:'Usando el servidor de Vectria', usingLocalFlash:'Usando recursos locales (CPU de tu dispositivo)', usingServerFlash:'Usando el servidor de Vectria (VPS en la nube)', topupProcessed:'La recarga se procesó correctamente. Sincronizando tus créditos…', paymentProcessed:'El pago se procesó correctamente. Activando tu plan y sincronizando los créditos…', maintenanceRestricted:'El sistema está en mantenimiento. El acceso está temporalmente restringido a los administradores.' },
  'pt-br': { notificationTitle:'Notificação', confirmationTitle:'Confirmação', continueLabel:'Continuar', cancelLabel:'Cancelar', computeResource:'Recurso de processamento', localLabel:'Local', cloudLabel:'Nuvem', usingLocalTitle:'Usando recursos locais', usingServerTitle:'Usando o servidor da Vectria', usingLocalFlash:'Usando recursos locais (CPU do seu dispositivo)', usingServerFlash:'Usando o servidor da Vectria (VPS na nuvem)', topupProcessed:'A recarga foi processada com sucesso. Sincronizando seus créditos…', paymentProcessed:'O pagamento foi processado com sucesso. Ativando seu plano e sincronizando os créditos…', maintenanceRestricted:'O sistema está em manutenção. O acesso está temporariamente restrito aos administradores.' },
  ru: { notificationTitle:'Уведомление', confirmationTitle:'Подтверждение', continueLabel:'Продолжить', cancelLabel:'Отмена', computeResource:'Вычислительные ресурсы', localLabel:'Локально', cloudLabel:'Облако', usingLocalTitle:'Используются локальные ресурсы', usingServerTitle:'Используется сервер Vectria', usingLocalFlash:'Используются локальные ресурсы (процессор вашего устройства)', usingServerFlash:'Используется сервер Vectria (облачный VPS)', topupProcessed:'Пополнение успешно обработано. Синхронизируем кредиты…', paymentProcessed:'Платёж успешно обработан. Активируем тариф и синхронизируем кредиты…', maintenanceRestricted:'Система находится на техническом обслуживании. Доступ временно открыт только администраторам.' },
  tr: { notificationTitle:'Bildirim', confirmationTitle:'Onay', continueLabel:'Devam et', cancelLabel:'İptal', computeResource:'İşlem kaynağı', localLabel:'Yerel', cloudLabel:'Bulut', usingLocalTitle:'Yerel kaynaklar kullanılıyor', usingServerTitle:'Vectria Sunucusu kullanılıyor', usingLocalFlash:'Yerel kaynaklar kullanılıyor (cihazınızın CPU’su)', usingServerFlash:'Vectria Sunucusu kullanılıyor (bulut VPS)', topupProcessed:'Bakiye yükleme başarıyla işlendi. Kredileriniz eşitleniyor…', paymentProcessed:'Ödeme başarıyla işlendi. Planınız etkinleştiriliyor ve krediler eşitleniyor…', maintenanceRestricted:'Sistem bakımdadır. Erişim geçici olarak yalnızca yöneticilerle sınırlandırılmıştır.' },
  vi: { notificationTitle:'Thông báo', confirmationTitle:'Xác nhận', continueLabel:'Tiếp tục', cancelLabel:'Hủy', computeResource:'Tài nguyên xử lý', localLabel:'Cục bộ', cloudLabel:'Đám mây', usingLocalTitle:'Đang dùng tài nguyên cục bộ', usingServerTitle:'Đang dùng máy chủ Vectria', usingLocalFlash:'Đang dùng tài nguyên cục bộ (CPU thiết bị của bạn)', usingServerFlash:'Đang dùng máy chủ Vectria (VPS đám mây)', topupProcessed:'Nạp tiền thành công. Đang đồng bộ tín dụng của bạn…', paymentProcessed:'Thanh toán thành công. Đang kích hoạt gói và đồng bộ tín dụng…', maintenanceRestricted:'Hệ thống đang bảo trì. Quyền truy cập tạm thời chỉ dành cho quản trị viên.' },
  th: { notificationTitle:'การแจ้งเตือน', confirmationTitle:'การยืนยัน', continueLabel:'ดำเนินการต่อ', cancelLabel:'ยกเลิก', computeResource:'ทรัพยากรประมวลผล', localLabel:'ภายในเครื่อง', cloudLabel:'คลาวด์', usingLocalTitle:'กำลังใช้ทรัพยากรภายในเครื่อง', usingServerTitle:'กำลังใช้เซิร์ฟเวอร์ Vectria', usingLocalFlash:'กำลังใช้ทรัพยากรภายในเครื่อง (CPU ของอุปกรณ์คุณ)', usingServerFlash:'กำลังใช้เซิร์ฟเวอร์ Vectria (VPS บนคลาวด์)', topupProcessed:'ประมวลผลการเติมเงินสำเร็จ กำลังซิงค์เครดิตของคุณ…', paymentProcessed:'ประมวลผลการชำระเงินสำเร็จ กำลังเปิดใช้แพ็กเกจและซิงค์เครดิต…', maintenanceRestricted:'ระบบอยู่ระหว่างการบำรุงรักษา ขณะนี้จำกัดการเข้าถึงไว้สำหรับผู้ดูแลระบบเท่านั้น' },
  ar: { notificationTitle:'إشعار', confirmationTitle:'تأكيد', continueLabel:'متابعة', cancelLabel:'إلغاء', computeResource:'مورد المعالجة', localLabel:'محلي', cloudLabel:'سحابي', usingLocalTitle:'استخدام الموارد المحلية', usingServerTitle:'استخدام خادم Vectria', usingLocalFlash:'يتم استخدام الموارد المحلية (معالج جهازك)', usingServerFlash:'يتم استخدام خادم Vectria (خادم VPS سحابي)', topupProcessed:'تمت معالجة شحن الرصيد بنجاح. جارٍ مزامنة أرصدتك…', paymentProcessed:'تمت معالجة الدفع بنجاح. جارٍ تفعيل خطتك ومزامنة الأرصدة…', maintenanceRestricted:'النظام قيد الصيانة. يقتصر الوصول مؤقتًا على المسؤولين.' },
  'zh-cn': { notificationTitle:'通知', confirmationTitle:'确认', continueLabel:'继续', cancelLabel:'取消', computeResource:'计算资源', localLabel:'本地', cloudLabel:'云端', usingLocalTitle:'使用本地资源', usingServerTitle:'使用 Vectria 服务器', usingLocalFlash:'正在使用本地资源（您的设备 CPU）', usingServerFlash:'正在使用 Vectria 服务器（云端 VPS）', topupProcessed:'充值已成功处理。正在同步您的额度…', paymentProcessed:'付款已成功处理。正在启用套餐并同步额度…', maintenanceRestricted:'系统正在维护中。访问权限暂时仅限管理员。' },
  ja: { notificationTitle:'通知', confirmationTitle:'確認', continueLabel:'続行', cancelLabel:'キャンセル', computeResource:'計算リソース', localLabel:'ローカル', cloudLabel:'クラウド', usingLocalTitle:'ローカルリソースを使用', usingServerTitle:'Vectria サーバーを使用', usingLocalFlash:'ローカルリソースを使用しています（お使いの端末の CPU）', usingServerFlash:'Vectria サーバーを使用しています（クラウド VPS）', topupProcessed:'チャージが正常に処理されました。クレジットを同期しています…', paymentProcessed:'支払いが正常に処理されました。プランを有効化し、クレジットを同期しています…', maintenanceRestricted:'システムはメンテナンス中です。現在、アクセスは管理者のみに制限されています。' },
  ko: { notificationTitle:'알림', confirmationTitle:'확인', continueLabel:'계속', cancelLabel:'취소', computeResource:'컴퓨팅 리소스', localLabel:'로컬', cloudLabel:'클라우드', usingLocalTitle:'로컬 리소스 사용', usingServerTitle:'Vectria 서버 사용', usingLocalFlash:'로컬 리소스를 사용하고 있습니다(사용자 기기의 CPU)', usingServerFlash:'Vectria 서버를 사용하고 있습니다(클라우드 VPS)', topupProcessed:'충전이 정상적으로 처리되었습니다. 크레딧을 동기화하는 중입니다…', paymentProcessed:'결제가 정상적으로 처리되었습니다. 플랜을 활성화하고 크레딧을 동기화하는 중입니다…', maintenanceRestricted:'시스템을 점검 중입니다. 현재 관리자만 접근할 수 있습니다.' }
};

export interface NotificationCopy {
  notifications: string;
  markAllRead: string;
  loading: string;
  noNotificationsYet: string;
  viewAll: string;
  centerTitle: string;
  centerDescription: string;
  closeCenter: string;
  filterAll: string;
  filterUnread: string;
  clearRead: string;
  emptyTitle: string;
  allCaughtUp: string;
  broadcastsAppearHere: string;
  deleteNotification: string;
  previous: string;
  next: string;
  pageStatus: (current: number, total: number) => string;
}

export const notificationCopy: Record<Locale, NotificationCopy> = {
  en: {
    notifications: 'Notifications',
    markAllRead: 'Mark all as read',
    loading: 'Loading notifications…',
    noNotificationsYet: 'No notifications yet.',
    viewAll: 'View all notifications',
    centerTitle: 'Notification Center',
    centerDescription: 'Review announcements and manage their read status.',
    closeCenter: 'Close Notification Center',
    filterAll: 'All',
    filterUnread: 'Unread',
    clearRead: 'Delete read notifications',
    emptyTitle: 'No notifications',
    allCaughtUp: 'You have read every notification.',
    broadcastsAppearHere: 'New announcements will appear here.',
    deleteNotification: 'Delete notification',
    previous: 'Previous',
    next: 'Next',
    pageStatus: (current, total) => `Page ${current} of ${total}`
  },
  id: {
    notifications: 'Notifikasi',
    markAllRead: 'Tandai semua sudah dibaca',
    loading: 'Memuat notifikasi…',
    noNotificationsYet: 'Belum ada notifikasi.',
    viewAll: 'Lihat semua notifikasi',
    centerTitle: 'Pusat Notifikasi',
    centerDescription: 'Tinjau pengumuman dan kelola status bacanya.',
    closeCenter: 'Tutup Pusat Notifikasi',
    filterAll: 'Semua',
    filterUnread: 'Belum dibaca',
    clearRead: 'Hapus notifikasi yang sudah dibaca',
    emptyTitle: 'Tidak ada notifikasi',
    allCaughtUp: 'Semua notifikasi sudah Anda baca.',
    broadcastsAppearHere: 'Pengumuman baru akan muncul di sini.',
    deleteNotification: 'Hapus notifikasi',
    previous: 'Sebelumnya',
    next: 'Berikutnya',
    pageStatus: (current, total) => `Halaman ${current} dari ${total}`
  },
  es: {
    notifications: 'Notificaciones',
    markAllRead: 'Marcar todas como leídas',
    loading: 'Cargando notificaciones…',
    noNotificationsYet: 'Aún no hay notificaciones.',
    viewAll: 'Ver todas las notificaciones',
    centerTitle: 'Centro de notificaciones',
    centerDescription: 'Revisa los avisos y gestiona su estado de lectura.',
    closeCenter: 'Cerrar el centro de notificaciones',
    filterAll: 'Todas',
    filterUnread: 'No leídas',
    clearRead: 'Eliminar las notificaciones leídas',
    emptyTitle: 'No hay notificaciones',
    allCaughtUp: 'Has leído todas las notificaciones.',
    broadcastsAppearHere: 'Los nuevos avisos aparecerán aquí.',
    deleteNotification: 'Eliminar notificación',
    previous: 'Anterior',
    next: 'Siguiente',
    pageStatus: (current, total) => `Página ${current} de ${total}`
  },
  'pt-br': {
    notifications: 'Notificações',
    markAllRead: 'Marcar todas como lidas',
    loading: 'Carregando notificações…',
    noNotificationsYet: 'Ainda não há notificações.',
    viewAll: 'Ver todas as notificações',
    centerTitle: 'Central de notificações',
    centerDescription: 'Consulte os avisos e gerencie o status de leitura.',
    closeCenter: 'Fechar a central de notificações',
    filterAll: 'Todas',
    filterUnread: 'Não lidas',
    clearRead: 'Excluir notificações lidas',
    emptyTitle: 'Nenhuma notificação',
    allCaughtUp: 'Você leu todas as notificações.',
    broadcastsAppearHere: 'Novos avisos aparecerão aqui.',
    deleteNotification: 'Excluir notificação',
    previous: 'Anterior',
    next: 'Próxima',
    pageStatus: (current, total) => `Página ${current} de ${total}`
  },
  ru: {
    notifications: 'Уведомления',
    markAllRead: 'Отметить все как прочитанные',
    loading: 'Загрузка уведомлений…',
    noNotificationsYet: 'Уведомлений пока нет.',
    viewAll: 'Посмотреть все уведомления',
    centerTitle: 'Центр уведомлений',
    centerDescription: 'Просматривайте объявления и управляйте отметками о прочтении.',
    closeCenter: 'Закрыть центр уведомлений',
    filterAll: 'Все',
    filterUnread: 'Непрочитанные',
    clearRead: 'Удалить прочитанные уведомления',
    emptyTitle: 'Нет уведомлений',
    allCaughtUp: 'Вы прочитали все уведомления.',
    broadcastsAppearHere: 'Новые объявления появятся здесь.',
    deleteNotification: 'Удалить уведомление',
    previous: 'Назад',
    next: 'Далее',
    pageStatus: (current, total) => `Страница ${current} из ${total}`
  },
  tr: {
    notifications: 'Bildirimler',
    markAllRead: 'Tümünü okundu olarak işaretle',
    loading: 'Bildirimler yükleniyor…',
    noNotificationsYet: 'Henüz bildirim yok.',
    viewAll: 'Tüm bildirimleri görüntüle',
    centerTitle: 'Bildirim Merkezi',
    centerDescription: 'Duyuruları inceleyin ve okunma durumlarını yönetin.',
    closeCenter: 'Bildirim Merkezini kapat',
    filterAll: 'Tümü',
    filterUnread: 'Okunmamış',
    clearRead: 'Okunmuş bildirimleri sil',
    emptyTitle: 'Bildirim yok',
    allCaughtUp: 'Tüm bildirimleri okudunuz.',
    broadcastsAppearHere: 'Yeni duyurular burada görünecek.',
    deleteNotification: 'Bildirimi sil',
    previous: 'Önceki',
    next: 'Sonraki',
    pageStatus: (current, total) => `Sayfa ${current} / ${total}`
  },
  vi: {
    notifications: 'Thông báo',
    markAllRead: 'Đánh dấu tất cả là đã đọc',
    loading: 'Đang tải thông báo…',
    noNotificationsYet: 'Chưa có thông báo.',
    viewAll: 'Xem tất cả thông báo',
    centerTitle: 'Trung tâm thông báo',
    centerDescription: 'Xem các thông báo mới và quản lý trạng thái đã đọc.',
    closeCenter: 'Đóng trung tâm thông báo',
    filterAll: 'Tất cả',
    filterUnread: 'Chưa đọc',
    clearRead: 'Xóa thông báo đã đọc',
    emptyTitle: 'Không có thông báo',
    allCaughtUp: 'Bạn đã đọc tất cả thông báo.',
    broadcastsAppearHere: 'Thông báo mới sẽ xuất hiện tại đây.',
    deleteNotification: 'Xóa thông báo',
    previous: 'Trước',
    next: 'Tiếp',
    pageStatus: (current, total) => `Trang ${current} / ${total}`
  },
  th: {
    notifications: 'การแจ้งเตือน',
    markAllRead: 'ทำเครื่องหมายทั้งหมดว่าอ่านแล้ว',
    loading: 'กำลังโหลดการแจ้งเตือน…',
    noNotificationsYet: 'ยังไม่มีการแจ้งเตือน',
    viewAll: 'ดูการแจ้งเตือนทั้งหมด',
    centerTitle: 'ศูนย์การแจ้งเตือน',
    centerDescription: 'ตรวจสอบประกาศและจัดการสถานะการอ่าน',
    closeCenter: 'ปิดศูนย์การแจ้งเตือน',
    filterAll: 'ทั้งหมด',
    filterUnread: 'ยังไม่ได้อ่าน',
    clearRead: 'ลบการแจ้งเตือนที่อ่านแล้ว',
    emptyTitle: 'ไม่มีการแจ้งเตือน',
    allCaughtUp: 'คุณอ่านการแจ้งเตือนทั้งหมดแล้ว',
    broadcastsAppearHere: 'ประกาศใหม่จะปรากฏที่นี่',
    deleteNotification: 'ลบการแจ้งเตือน',
    previous: 'ก่อนหน้า',
    next: 'ถัดไป',
    pageStatus: (current, total) => `หน้า ${current} จาก ${total}`
  },
  ar: {
    notifications: 'الإشعارات',
    markAllRead: 'تحديد الكل كمقروء',
    loading: 'جارٍ تحميل الإشعارات…',
    noNotificationsYet: 'لا توجد إشعارات بعد.',
    viewAll: 'عرض جميع الإشعارات',
    centerTitle: 'مركز الإشعارات',
    centerDescription: 'راجع الإعلانات وأدِر حالة قراءتها.',
    closeCenter: 'إغلاق مركز الإشعارات',
    filterAll: 'الكل',
    filterUnread: 'غير المقروءة',
    clearRead: 'حذف الإشعارات المقروءة',
    emptyTitle: 'لا توجد إشعارات',
    allCaughtUp: 'لقد قرأت جميع الإشعارات.',
    broadcastsAppearHere: 'ستظهر الإعلانات الجديدة هنا.',
    deleteNotification: 'حذف الإشعار',
    previous: 'السابق',
    next: 'التالي',
    pageStatus: (current, total) => `الصفحة ${current} من ${total}`
  },
  'zh-cn': {
    notifications: '通知',
    markAllRead: '全部标为已读',
    loading: '正在加载通知…',
    noNotificationsYet: '暂无通知。',
    viewAll: '查看所有通知',
    centerTitle: '通知中心',
    centerDescription: '查看公告并管理已读状态。',
    closeCenter: '关闭通知中心',
    filterAll: '全部',
    filterUnread: '未读',
    clearRead: '删除已读通知',
    emptyTitle: '没有通知',
    allCaughtUp: '所有通知均已读。',
    broadcastsAppearHere: '新公告将显示在这里。',
    deleteNotification: '删除通知',
    previous: '上一页',
    next: '下一页',
    pageStatus: (current, total) => `第 ${current} 页，共 ${total} 页`
  },
  ja: {
    notifications: '通知',
    markAllRead: 'すべて既読にする',
    loading: '通知を読み込み中…',
    noNotificationsYet: '通知はまだありません。',
    viewAll: 'すべての通知を表示',
    centerTitle: '通知センター',
    centerDescription: 'お知らせを確認し、既読状態を管理します。',
    closeCenter: '通知センターを閉じる',
    filterAll: 'すべて',
    filterUnread: '未読',
    clearRead: '既読の通知を削除',
    emptyTitle: '通知はありません',
    allCaughtUp: 'すべての通知を確認済みです。',
    broadcastsAppearHere: '新しいお知らせがここに表示されます。',
    deleteNotification: '通知を削除',
    previous: '前へ',
    next: '次へ',
    pageStatus: (current, total) => `${current} / ${total} ページ`
  },
  ko: {
    notifications: '알림',
    markAllRead: '모두 읽음으로 표시',
    loading: '알림 불러오는 중…',
    noNotificationsYet: '아직 알림이 없습니다.',
    viewAll: '모든 알림 보기',
    centerTitle: '알림 센터',
    centerDescription: '공지 사항을 확인하고 읽음 상태를 관리하세요.',
    closeCenter: '알림 센터 닫기',
    filterAll: '전체',
    filterUnread: '읽지 않음',
    clearRead: '읽은 알림 삭제',
    emptyTitle: '알림 없음',
    allCaughtUp: '모든 알림을 확인했습니다.',
    broadcastsAppearHere: '새 공지 사항이 여기에 표시됩니다.',
    deleteNotification: '알림 삭제',
    previous: '이전',
    next: '다음',
    pageStatus: (current, total) => `${current} / ${total} 페이지`
  }
};

export const studioCopy: Record<Locale, StudioCopy> = {
  en,
  id,
  es,
  'pt-br': ptBr,
  ru,
  tr,
  vi,
  th,
  ar,
  'zh-cn': zh,
  ja,
  ko
};





